// Phase G4-B — map curriculum content items → EngineItem.
//
// The session screens render EngineItem (src/types/engine.ts). The
// curriculum packs store richer authored shapes (mathExemplars,
// controlledSentences). This maps a pack item to the EngineItem the
// UI expects, preserving the per-item launchStatus so G4-C can pass
// the real value into the attempt record (watch item).

import { withMathHintCue, type MathCue } from "./mathHintCue";
import type { AgeBand, EngineItem, SupportCode } from "@/types/engine";
import type { LaunchStatus, ItemType } from "@/adaptation/types";
import type { ContentPackName, ClozeSlot } from "@/curriculum/types/content";
import {
  UnknownPackError,
  NotKidFacingPackError,
  NoMapperImplementedError,
  NoAuthoredContentError,
  MapperShapeMismatchError,
  INVARIANTS,
  type MapperInvariant,
  type MapperErrorContext,
} from "./contentToEngineItemErrors";
// MA-681: kept below the first relative import so a bare-node import fails with the same signature the qa-drive-trail runner guard pins.
import { SOUND_BOX_CUE_DISPLAY_PROMPT, SOUND_BOX_CUE_KIND_BY_SKILL, soundBoxCueFor, soundBoxPictureResolves, type SoundBoxCueKind } from "@/engine/soundBoxCue";
import { passageById } from "@/curriculum/readers/passageIndex";
import { illustrationUrlForItem, illustrationUrlForWord } from "@/curriculum/readers/approvedImageIndex";
import { resolveGrammarHintCue, resolveSemanticRelationHintCue } from "@/curriculum/data/hintTemplates";
import { pickTargetEmoji } from "@/components/visuals/SceneIllustration.jsx";
import { PART_WHOLE_PICTURE_MAX_WHOLE } from "@/components/visuals/PartWholePictures";
import { AGE_BAND_ORDER } from "@/adaptation/types";
import { dotGroupLayoutAdmits, type OptionDotGroup } from "@/lib/engine/dotGroupLayouts";
import { deriveWordProblemFactors } from "@/lib/engine/wordProblemFactors";
// MA-708 — shared with DivisionSharingRenderer so the mapper's emission
// gate and the renderer's own display clamp cannot drift apart (see the
// division_sharing branch below).
import { MAX_GROUPS as DIVISION_SHARING_MAX_GROUPS, MAX_TOTAL as DIVISION_SHARING_MAX_TOTAL } from "@/components/renderers/DivisionSharingRenderer";
// MA-697 — shared with CountOnLineRenderer (same reason as MA-708 above): the
// mapper never emits a jump the renderer's 0..20 line cannot draw.
import { COUNT_ON_LINE_MAX } from "@/components/renderers/CountOnLineRenderer";
// MA-694 — the mapper names the two display families its renderers honour
// (shared constants, so a renamed value can never silently fall back).
import { PLACE_VALUE_SUBJECT_STATED } from "@/components/renderers/PlaceValueBlocksRenderer";
import { NUMERAL_BOND_KNOWN_RIGHT } from "@/components/renderers/NumberBondRenderer";
// MA-692 twin-picture-variety — the ten frame's fill arrangements (layout only).
import { tenFrameFilledCells, TEN_FRAME_VERTICAL } from "@/components/renderers/rendererRegistry";

// Phase 0.1 bands-as-data: derive the mapper guard set from the SINGLE
// ordered band table (was a hardcoded list that excluded the grade-2 fine
// bands 7.0-7.5 / 7.5+ — a tripwire that would crash a guarded-pack session
// the moment such content was authored). Now any band in the table is valid
// here automatically.
const KNOWN_AGE_BANDS = new Set<string>(AGE_BAND_ORDER);

/** Assert a mapped EngineItem is renderable; throw MapperShapeMismatchError on
 *  the FIRST violation (fail fast, don't accumulate). Run as the last line of
 *  every guarded mapper. The option checks apply only to choice items. */
function assertMapperInvariants(
  mapped: MappedEngineItem,
  pack: ContentPackName,
  ctx: MapperErrorContext,
): void {
  const fail = (inv: MapperInvariant): never => {
    throw new MapperShapeMismatchError(pack, inv, ctx);
  };
  if (!mapped.id || mapped.id.trim() === "") fail(INVARIANTS.ID_NON_EMPTY);
  if (!mapped.prompt || mapped.prompt.trim() === "") fail(INVARIANTS.PROMPT_NON_EMPTY);
  if (mapped.answer == null || String(mapped.answer).trim() === "") fail(INVARIANTS.ANSWER_NON_EMPTY);
  // A "renders but is unanswerable" guard: ANY item the renderer shows as a
  // tappable choice grid (type multiple_choice OR displayMode tap_choice)
  // must carry real options. This catches the decodablePassages-class bug
  // (fill_blank + tap_choice with options omitted) at MAP TIME instead of in
  // a child's session — see scripts/audit/renderabilityAudit.mjs.
  if (mapped.type === "multiple_choice" || mapped.displayMode === "tap_choice") {
    const opts = mapped.options;
    if (!Array.isArray(opts)) fail(INVARIANTS.OPTIONS_ARRAY);
    const options = opts as string[];
    if (options.length < 2) fail(INVARIANTS.OPTIONS_MIN_2);
    if (!options.includes(mapped.answer)) fail(INVARIANTS.OPTIONS_INCLUDE_ANSWER);
    // Exact (case-sensitive) distinctness — grammar capitalization/punctuation
    // items legitimately render case/period variants as distinct options.
    if (new Set(options).size !== options.length) fail(INVARIANTS.OPTIONS_DISTINCT);
  }
  if (!mapped.microSkillId) fail(INVARIANTS.MICROSKILL_PRESENT);
  if (!mapped.ageBand) fail(INVARIANTS.AGEBAND_PRESENT);
  if (!KNOWN_AGE_BANDS.has(mapped.ageBand)) fail(INVARIANTS.AGEBAND_VALID);
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** EngineItem + the source item's launchStatus, so the session screen
 * can stamp the real launchStatus on the attempt (not the buildAttempt
 * default). */
export interface MappedEngineItem extends EngineItem {
  launchStatus: LaunchStatus;
  /** MA-840 slice 2a (operator D-1008-25) — the family spoken cue
   *  (mathHintCue.ts), kept OUT of `supports` and `visualScaffold`. HintSlot
   *  renders it as the SEN rung only where the ladder would otherwise be
   *  empty. Present only on the math families mathHintCue.ts names. */
  mathCue?: MathCue;
  /** MA-663 round 2 (⭐M4) — the pack this record was actually read from,
   *  stamped by the caller ONLY for a PACK_MERGE skill's union pull
   *  (useEngineQuestions.ts's pullItems); absent for every single-pack
   *  skill, where `contentFileForSkill(skillId)` already names the right
   *  file. When present, attempt logging must use THIS pack, not the
   *  skill's `primaryPackForSkill` — for ms.math.03, ~47% of served items
   *  (the 60 foundationItems ones) would otherwise be stamped with the
   *  wrong source file (mathExemplars.json) on every attempt record. */
  sourcePack?: ContentPackName;
  /** True when this entry is a bounded repeat of an earlier item in the
   *  same session (see withBoundedRepetition, #27). Absent/false on the
   *  first appearance. Carried into the UI as a `data-repetition` hook so
   *  the 2x cap is end-to-end verifiable; NOT persisted on the attempt. */
  isRepetition?: boolean;
  /** MA-794 — the string the prompt speaker SPEAKS when it differs from the
   *  on-screen `prompt`. Present only on the ms.read.08 HFW_mc_* "find the
   *  word" items: `prompt` is the answer-free "Listen, tap." and
   *  `spokenPrompt` is the authored "Find the word 'are'." (names the
   *  target, already a recorded narration take). Absent everywhere else —
   *  read it only through `readingPromptSpokenText`. */
  spokenPrompt?: string;
  /** MA-681 — which sound-box surface this item uses (soundBoxCue.ts). */
  soundBoxCueKind?: "segmented" | "dictation";
  /** RC-3b — the multi-sentence passage BODY (every sentence except the
   *  final one, which becomes the fill_blank cloze). Present only on
   *  decodablePassages items; ReadingSession renders it as a PassageReader
   *  above the cloze so the kid reads the whole connected passage before
   *  completing the last line. Absent on single-sentence packs. */
  passageBodySentences?: string[];
  /** RC-3b — the passage title (decodablePassages only). */
  passageTitle?: string;
  /** MA-826 — the passage's authored `characterNames` (decodablePassages only;
   *  the key is ABSENT when the record lists none). The practice tap-a-word
   *  (PassageReader + ReadingSession's cloze row, via buildPracticeTapUtterance)
   *  replays a tapped name WHOLE instead of sounding it out. */
  characterNames?: string[];
  /** MA-671 slice 2b (operator ruling round 0, 2026-09-21) — ADDITIVE
   *  discriminator threaded straight off the authored foundationItems
   *  record's own `itemType` field (never derived, never widened). The ONLY
   *  currently-authored value is `"phonemeCount"` (the 36 ms.read.05
   *  oral-segmenting `FND_SEG_*` records — "How many sounds do you hear in
   *  <word>?" over 4 numeral tiles). The KEY is genuinely absent (never
   *  even `undefined`-valued — a conditional spread, not a ternary field —
   *  see the mapper's own comment at the assignment site) for EVERY other
   *  foundationItems record and for every non-foundationItems pack.
   *  RED round 1 (must-fix 5) — this used to cite a
   *  "foundationItemDiscriminatorParity.test.mjs" that does not exist
   *  anywhere in the repo; the real proof lives in
   *  oralSegmentingRouting.test.mjs's "discriminator key presence" pin: for
   *  every non-FND_SEG foundationItems record, the mapped item has NO
   *  `foundationItemType` own key at all, and every other mapped field
   *  equals a committed snapshot hash (so no other field silently changed
   *  for any pre-existing record). FoundationSession.tsx reads this to OMIT
   *  its instruction line entirely for this shape (the prompt IS the
   *  instruction; "Tap the letter" is wrong for a count-the-sounds task —
   *  see the operator ruling appended to MA-671s2b-spec-v1.md). */
  foundationItemType?: "phonemeCount";
  /** MA-492/Slice 8 (D27, MA-127) — the item's authored `evidenceSentenceIds`
   *  (visualScaffold.kind === "passage_context" only), resolved to INDEXES
   *  into `passageBodySentences` (same array, same order — both derive from
   *  the same resolved `passage.sentences`). Absent when there is nothing to
   *  highlight (no ids, or none resolve — comprehensionJoinIntegrity already
   *  guards the latter never happening on a launch_safe item). The render
   *  site (ReadingSession/PassageReader) uses this to in-place highlight the
   *  evidence sentence(s) at hint-open and at reveal — see MA-127/WS5 — never
   *  needs to re-match sentence_id strings against sentence text at render
   *  time (D27: the highlight must never disclose MORE than the already-
   *  shipped MA-490 SEN panel — it points at the same sentences, just
   *  in-place instead of reprinted). */
  evidenceSentenceIndexes?: number[];
  /** MA-127 — the authored `independentReadingEligible:false` signal ("a
   *  kid can't read this passage alone"), carried through the mapper as the
   *  AFFORDANCE it drives: `true` iff the comprehension record authors
   *  `independentReadingEligible === false` AND its passage resolved (so
   *  there is a body to read). The KEY is absent otherwise (conditional
   *  spread, never `false`/`undefined`-valued). PassageReader renders a
   *  tap-only "Read it to me" speaker for the passage body when this is set
   *  (see `passageReadAloudText`). Never a gate: nothing is hidden. */
  passageReadAloud?: true;
  /** RC-VISUAL — renderable math manipulative (counters/ten-frame/number-line)
   *  derived from the source visualSupport + operands. Drives <MathVisual>. */
  mathVisual?: {
    kind: string;
    a?: number;
    b?: number;
    op?: "+" | "-";
    total: number;
    /** MA-C2 (ADR-015) — semantic glyph token for a COUNTING manipulative
     *  ("star" for "Count the stars"). Absent => the renderer draws the legacy
     *  generic circle. The renderer owns the actual SVG shape. */
    glyph?: string;
    /** MA-C18 — arrangement FAMILY for the `dot_pattern` subitize renderer
     *  ("dice" | "frame" | "generic"). Layout only; the drawn count is keyed
     *  off `total`, so this can never affect answer-safety. */
    arrangement?: string;
    /** MA-824 — the timed quick look for a `dot_pattern` subitize card: the
     *  picture is shown for this many ms (from the end of the prompt audio,
     *  or item start when audio is off), then hidden until the answer; a
     *  "See it again" tap re-shows it for the same time and records support.
     *  Set only by the mathExemplars subitize intercept (authored
     *  `sourceStimulus_v2.details.displayTimeMs`, else DEFAULT_QUICK_LOOK_MS).
     *  Timing only: the drawn count is still `total`. */
    displayTimeMs?: number;
    /** MA-C20 — authored fill color for the `two_color_ten_frame` renderer
     *  (e.g. "purple"/"orange"). Cosmetic only; the drawn COUNT is always
     *  `a`, so an unrecognized/absent color can never affect answer-safety. */
    color?: string;
    /** MA-C21 — authored shape name for the `shape_array` renderer
     *  ("circle" | "square" | "triangle" | "rectangle"). The mapper only
     *  emits this kind when `shape` already equals the correct answer. */
    shape?: string;
    /** MA-C21 — authored display FAMILY for the `shape_array` renderer.
     *  Cosmetic only; never changes which shape is drawn. */
    displayStyle?: string;
    /** MA-C21 — authored `shown` sequence for the `pattern_strip` renderer.
     *  The mapper never forwards the answer — only the shown prefix. */
    sequence?: string[];
    /** MA-C15 (ADR-067) — one {a,b} numerator/denominator pair per shuffled
     *  answer option, for the `fraction_comparison` renderer (N un-highlighted
     *  bars, none flagged correct). Absent for every other kind. */
    fractions?: { a: number; b: number }[];
    /** MA-C16 — literal named coin multiset for the `coins` renderer. When
     *  present (and its cents value equals `total`), the renderer draws
     *  EXACTLY this multiset instead of greedy-splitting `total`. */
    coinCounts?: { quarter?: number; dime?: number; nickel?: number; penny?: number };
    /** MA-C17 — exactly 2 {a,b} (hours,minutes) pairs — [0]=start, [1]=end —
     *  for the `elapsed_time_clock` renderer. The renderer reads ONLY this
     *  field, never `total` (the elapsed minutes, carried for contract
     *  parity only), so the elapsed duration can never be rendered. */
    times?: { a: number; b: number }[];
    /** MA-703 — which slot the `take_away_blank` renderer's blank belongs
     *  to ("minuend" | "subtrahend"). Absent for every other kind. */
    blank?: "minuend" | "subtrahend";
    /** MA-706 — the `skip_count_line` renderer's shown-term sequence, in
     *  display (prompt) order. `null` marks the BLANK stop's position —
     *  never its value; the blank's number is never forwarded to the
     *  renderer, only its position in the array. Present only for
     *  kind === "skip_count_line". */
    terms?: (number | null)[];
    /** MA-706 — the `skip_count_line` renderer's constant step between
     *  consecutive stops (a positive magnitude; direction — ascending vs
     *  descending — is implied purely by term ORDER, never drawn as a
     *  sign). Present only for kind === "skip_count_line". */
    step?: number;
  };
  /** MA-702 — per-OPTION dot groups for a numeral-to-quantity match
   *  ("Match the card to the right group of dots"), keyed by the option's
   *  own value string (so the shuffle of `options` cannot misalign it).
   *  When present, the option tiles draw each option's dot group INSTEAD of
   *  the option's digit (ChoiceTiles.tsx). Emitted ONLY by
   *  foundationItemToEngineItem, ONLY for records whose authored
   *  `sourceStimulus_v2` carries `answerOptions.kind === "dot_groups"` or
   *  `quantityChoices.elementKind === "dots"`, and only when EVERY option's
   *  authored layout is drawable with exactly that option's count
   *  (dotGroupLayouts.ts) and the numeral card is drawn — otherwise the key
   *  is absent and the tiles stay digits. */
  optionDotGroups?: Record<string, OptionDotGroup>;
  /** MA-710 — a unit the option tiles show after every option ("¢" on the
   *  `count_coins` money items: "11¢"). Display only — `options` and
   *  `answer` stay the bare values. Absent on every other item. */
  optionUnit?: string;
  /** VISUALS-AS-HINTS — true only for items where the picture IS the question
   *  (count/subitize/coins/clock): the answer can't be read from the prompt
   *  text, so MathSession shows the visual by default. For every other item
   *  the visual is supportive and starts HIDDEN (revealed via the hint). */
  visualRequired?: boolean;
  /** MA-709 round 1 (RED MF1) — true only for an item whose picture is the
   *  answer-SAFE multi-bar `fraction_comparison` (>= 2 un-highlighted bars,
   *  none singled out). `resolveScaffoldVisibility` shows such a picture in
   *  FULL for a learner with no history on the skill (a child's whole first
   *  session), instead of the Phase-1 hidden-by-default — so a first-timer
   *  meets the bars before the symbols; it still fades at emerging and hides
   *  at secure/fluent (SHOW rung restores). MA-742: also true for MA-703's
   *  answer-safe blank-slot subtraction picture (`take_away_blank`, a `sub`
   *  model carrying `blank`). Absent on every other item. */
  pictureShownWithoutHistory?: true;
  /** MA-303 — true for an authored transfer-probe item (a representation-
   *  shifted restatement of a practiced fact/pattern, e.g. a word-problem
   *  form of a bare equation). Forwarded so the session screen can tag the
   *  resulting attempt (`isTransferTask`/`transferTaskId`) via
   *  `transferTagsForItem` — see transferGating.ts's `evaluateTransferEvidence`,
   *  which can only ever pass a transfer requirement for attempts tagged
   *  this way. */
  isTransferProbe?: boolean;
  /** MA-303 — the transferTasks.json id this probe answers for (e.g.
   *  "TT_ms.math.09_rt_01"). Present only when isTransferProbe is true. */
  transferTaskId?: string;
  /** MA-381 (mapper-threading slice) — authored per-item diagnostic tag for
   *  WHY a specific wrong tap is wrong (e.g. "counting_error"). Passthrough
   *  only in this slice — no consumer reads it yet; buildAttempt.ts still
   *  emits generic `incorrect_answer` for every wrong tap. The classifier-
   *  core slice reads this to pick a specific ErrorType. Present on roughly
   *  half of mathExemplars items; absent (undefined) elsewhere — never
   *  fabricated. */
  misconceptionCategory?: string;
  /** MA-381 (mapper-threading slice) — authored map of stringified wrong
   *  answer -> the specific misconception it reveals, keyed the same way a
   *  future classifier would need to look up the kid's actual wrong tap.
   *  Passthrough only; see misconceptionCategory doc above. */
  diagnosticMeaningByWrongAnswer?: Record<string, string>;
  /** MA-714 — NON-RENDERED. The multiplication factor pair the item's OWN
   *  authored fields encode (`deriveWordProblemFactors` in
   *  wordProblemFactors.ts: `a`/`b`, or `[expectedAnswer, known factor]`
   *  when a missing-factor record stores the unknown as `0`), so
   *  classifyMathError can still corroborate an authored additive-
   *  confusion distractor (`picked === a + b`) when no picture is served.
   *  Stamped ONLY on the multiplicative item types
   *  (`FACTOR_PAIR_ITEM_TYPES`) whose `mathVisual` carries no numeric
   *  `a`/`b` pair (the picture-free serve: MA-659's ms.math.27 removals and
   *  the four `equal_groups_*` word-problem types) and ONLY when the
   *  derivation succeeds; the KEY is absent everywhere else (conditional
   *  spread, never `undefined`-valued). No screen, renderer or narration
   *  reads it — it is classifier input only. */
  authoredFactorPair?: { a: number; b: number };
  /** ⭐MA-631 §3a — the authored EVIDENCE shape this item genuinely is, when
   *  it differs from a screen's historical hardcoded `itemType`. Set in
   *  `mathExemplarToEngineItem` for `sourceStimulus_v2.type === "narrative"`
   *  items (one-step word problems — 38 of them, all `ms.math.15`, unique to
   *  it across the whole mathExemplars pack) and, for symmetry/future
   *  authoring, in `foundationItemToEngineItem` by the same rule — that arm
   *  is INTENTIONALLY DEAD today: `foundationItems` carries
   *  `sourceStimulus_v2` on 585 items with types `{shape_array,
   *  pattern_strip, comparison_pair}`, never `narrative` (asserted over the
   *  real corpus in contentToEngineItem.test.mjs). Consumed exclusively via
   *  `evidenceItemTypeFor` (src/engine/evidenceItemType.ts) — undefined means
   *  "use the screen's fallback", never a fabricated value. */
  evidenceItemType?: ItemType;
  /** MA-652 RED round 1 — which CODE PATH built the arithmetic model, i.e.
   *  today's original `promptOperandSource` definition, demoted to this
   *  additive field and RENAMED because RED's round-1 review found the
   *  code-path question is not the one MA-149's served-surface clause asks
   *  (see `promptOperandSource`'s own doc below for why). Set in
   *  `mathExemplarToEngineItem` from the SAME `model` that already drives
   *  `visualScaffold`/`mathVisual`:
   *    "prompt"   — `parseMathItem` found the operands in the authored
   *                  PROMPT TEXT (model present, `model.source` unset — the
   *                  plain-regex branches at the top of `parseMathItem`).
   *    "stimulus" — a stimulus reader fired instead (`stimulusOperandsFrom
   *                  Details` / `twoDigitAddendsFromStimulus` (which reads
   *                  `twoDigitAddendPair`) / `missingAddendFromStimulus` /
   *                  `partWholeFromStimulus` / `countFromSubitizeStimulus`
   *                  — every one of these sets `model.source = "stimulus"`
   *                  before returning) — the prompt TEXT AS AUTHORED (before
   *                  any twoDigitAddStem/narrativePrompt rewrite) carried no
   *                  operands, even though the FINAL SERVED prompt sometimes
   *                  does (see `promptOperandSource`).
   *    "none"     — no model at all (`parseMathItem` returned null): neither
   *                  the prompt nor a structured stimulus reader could
   *                  reproduce the authored answer.
   *  Additive-only: never affects prompt/answer/options/visual rendering.
   *  Not set on foundationItemToEngineItem or any other mapper — those never
   *  run parseMathItem/the stimulus readers this field documents. This is
   *  CODE-PATH provenance only — use `promptOperandSource` for "can the child
   *  answer from what's on screen". */
  operandModelSource?: "prompt" | "stimulus" | "none";
  /** MA-652 (RED round 1 redefinition, 2026-09-13) — is the item's SERVED
   *  prompt (`MappedEngineItem.prompt`, exactly as rendered — after any
   *  `narrativePrompt`/`twoDigitAddStem` rewrite) self-sufficient, so a
   *  served-surface probe can tell whether the child needs a picture that
   *  Mixed never renders (closes MA-149's served-surface clause). Round-1
   *  RED review found the original definition (now `operandModelSource`)
   *  measured the MAPPER'S CODE PATH, not what the child sees — 96% of the
   *  original 501-item baseline turned out to already show their numbers in
   *  the served prompt (word-problem prose, or a `twoDigitAddStem` rewrite),
   *  so a FAIL on that definition was mostly noise. This field instead
   *  checks the ACTUAL rendered prompt string:
   *    "prompt"   — a scaffold model exists and EVERY one of its answer-
   *                  relevant operands (see `modelOperandsForContainment` —
   *                  excludes whichever field IS the model's answer, e.g.
   *                  missing_addend's `b`) appears in the served prompt as a
   *                  digit or a (0-20) number word; OR no model exists and
   *                  the served prompt contains at least one digit.
   *    "stimulus" — a model exists but at least one of its operands does
   *                  NOT appear in the served prompt — the child needs
   *                  something other than the prompt text (a picture Mixed
   *                  may or may not render) to answer.
   *    "none"     — no model exists AND the served prompt has no digit
   *                  either — nothing observable ties the prompt to a
   *                  number at all.
   *  Anti-spoofing: the model-present arm is NOT spoofable by adding a
   *  stray digit to a prompt — containment is checked against the actual
   *  model the mapper built, not a bare digit scan; only the model-absent
   *  arm falls back to bare digit presence, which is exactly the class a
   *  human has to read the prompt to judge anyway. Set identically (same
   *  helper, `computePromptOperandSource`) in `mathExemplarToEngineItem`
   *  (model may be present) and `foundationItemToEngineItem` (model is
   *  always absent there — no arithmetic-model concept exists in that
   *  mapper). Additive-only: never affects prompt/answer/options/visual
   *  rendering — the emitted value is always one of these three literals,
   *  never an operand, a digit run, or prompt text itself. */
  promptOperandSource?: "prompt" | "stimulus" | "none";
  /** MA-686 slice b — true when the raw content item authors an explicit
   *  read-aloud requirement (`supportMode` includes `"readAloudRequired"`
   *  OR `sourceStimulus_v2.scaffold.readAloudOnLaunch === true`). Set ONLY
   *  in `foundationItemToEngineItem` (the only mapper whose raw items ever
   *  carry either field today — zero occurrences in mathExemplars.json,
   *  asserted in contentToEngineItem.test.mjs); every other mapper leaves
   *  this undefined, never coerced to false. Additive-only: threading this
   *  field changes NO other emitted field — see that mapper's own call
   *  site for the parity proof. Consumed by `mathPromptAutoSpeakGate`
   *  (autoSpeakItemGate.ts) as one arm of "a pre-reader cannot attempt
   *  this item from print". */
  readAloudRequired?: boolean;
  /** MA-1001 — present (always `true`) ONLY on a spoken numeral-target record:
   *  the target is named as a whole number WORD, never printed as the glyph
   *  the tiles show, and the mapped data carries a ten frame of its quantity.
   *  That frame is DRAWN on MathSession only: /play/foundation renders no
   *  picture, so there the task is "hear the word, tap the digit" (MA-1001
   *  RED r2), which is why the spoken prompt is the target's only channel.
   *   - mathExemplars: the authored MA-688 arm (`visualSupport:
   *     "spoken_numeral_target"`) whose fail-closed checks passed, i.e. its
   *     ten frame was drawn.
   *   - foundationItems (RED r1 SHOULD 1, operator ruling): no authored flag,
   *     so the record's DATA decides — `isSpokenNumeralTargetShape` over what
   *     the mapper emits (answer 0..10, the prompt names exactly that number
   *     word and carries no digit, every option a digit string, the mapped
   *     picture a ten frame of the answer). Never an id prefix.
   *  The key is ABSENT on every other item (conditional spread). Read by
   *  FoundationSession to give these records MathSession's prompt auto-speak
   *  decision. NB the mapped object's content hash (contentProvenance,
   *  JSON.stringify of the item) moves for the marked records only. */
  spokenNumeralTarget?: true;
}

interface MathExemplar {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  a?: number;
  b?: number;
  expectedAnswer: number | string;
  prompt: string;
  itemType?: string;
  /** MA-659 (RENDER-C): the item's OWN authored skill, independent of
   *  whichever served-skill label the caller passed as `microSkillId` —
   *  legacySkillIdMap.json means the same 50 ms.math.27 items can be
   *  served under the alias "math.27" (or any probe label), so a gate
   *  keyed on the threaded parameter is defeatable. Gate on this field. */
  targetSkill?: string;
  nearWrongAnswers?: (number | string)[];
  visualSupport?: string;
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
  whyThisSurfacesMisconception?: string;
  /** MA-303 — see MappedEngineItem.isTransferProbe. */
  isTransferProbe?: boolean;
  transferTaskId?: string;
  /** MA-381 (mapper-threading slice) — see MappedEngineItem's field of the
   *  same name. */
  misconceptionCategory?: string;
  /** MA-381 (mapper-threading slice) — see MappedEngineItem's field of the
   *  same name. */
  diagnosticMeaningByWrongAnswer?: Record<string, string>;
  /** MA-710 — the authored coin multiset a `count_coins` item draws. The
   *  prompt no longer names the coins ("How much money is this?" — naming
   *  them read the coin-identification half of the task aloud), so this
   *  field is the picture's source; `authoredCoinCounts` accepts it only when
   *  it is well-formed and its cents equal the key. */
  coinCounts?: { quarter?: number; dime?: number; nickel?: number; penny?: number };
  /** Authored structured stimulus. Some item families (e.g. ms.math.12
   *  missing-addend) carry the operands in `sourceStimulus_v2.details`
   *  rather than in the prompt text, so the prompt-based parse can't see
   *  them; the mapper reads this as a typed fallback. */
  sourceStimulus_v2?: {
    type?: string;
    details?: {
      equation?: string;
      unknownPosition?: string;
      knownAddend?: number;
      total?: number;
      /** MA-C17 — elapsed_time items authored as start/end hours+minutes
       *  (0..23 / 0..59), read by buildMathVisual's elapsed_time branch. */
      startHour?: number;
      startMinute?: number;
      endHour?: number;
      endMinute?: number;
      [k: string]: unknown;
    };
  };
}

interface ControlledSentence {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  sentenceTemplate: string;
  correctAnswer: string;
  options?: string[];
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
  whyOnlyOneAnswer?: string;
  // MA-617a: see controlledSentenceSchema (curriculum/schemas/content.ts).
  gloss?: string;
}

/** What the drawn quantities mean for an item. Parsed from the PROMPT,
 * which is authoritative — the item's a/b fields are frequently stale
 * (counting, missing-addend, "show the number" and comparison items all
 * carry a/b that do NOT match the prompt). #61 root cause: the old
 * parser only matched "X op Y =", missed counting / missing-addend /
 * blank-first / fact-family / "show the number" / compare, and fell back
 * to stale a/b — drawing the wrong quantities for ~126 of 163 items. */
type ScaffoldMode = "count" | "add" | "sub" | "missing_addend" | "compare";

interface ScaffoldModel {
  mode: ScaffoldMode;
  /** count n / first addend / minuend / known addend / first compared. */
  a: number;
  /** second addend / subtrahend / missing addend / second compared. */
  b: number;
  /** count n / sum / minuend / given sum / larger compared. */
  whole: number;
  /** the answer this model implies, used to self-check the parse. */
  impliedAnswer: number;
  /** MA-94: where the operands came from. "stimulus" = read from the authored
   *  sourceStimulus_v2 because the PROMPT carries no operands — for those
   *  items the emitted picture IS the question (the prompt alone is
   *  unanswerable), so the mapper marks the visual required-by-default. */
  source?: "prompt" | "stimulus";
  /** MA-703 — set ONLY for a `sub` model parsed from a blank-minuend
   *  ("___ − b = c") or blank-subtrahend ("a − ___ = c") prompt. `a` is
   *  ALWAYS the minuend and `b` the subtrahend (the existing `sub` field
   *  contract, unchanged) — this field says which of the two was the
   *  authored blank (and therefore equals `impliedAnswer`), so a
   *  downstream reader (buildMathVisual, computePromptOperandSource) can
   *  tell the known operand from the blank one without re-deriving it.
   *  Undefined for every existing full `a − b = ___` item — D1's
   *  byte-identity pin depends on this being additive-only. */
  blank?: "minuend" | "subtrahend";
  /** MA-769 — set ONLY for a `missing_addend` model whose equation's blank
   *  side is known: "left" for "___ + B = C", "right" for "A + ___ = C". `a`
   *  is ALWAYS the known addend and `b` the missing one (field contract
   *  unchanged); this says which side of the equation the "?" belongs on so
   *  the number bond can put it in the matching part circle. Absent where the
   *  side cannot be read (callers keep the historical right-hand "?"). */
  blankSide?: "left" | "right";
}

const NUM_RE = /\d+/g;
const nums = (s: string): number[] => (s.match(NUM_RE) ?? []).map(Number);
// Blank-operand markers seen in authored prompts (underscores / box / ?).
const BLANK = "(?:_{2,}|＿+|□|\\?)";
// MA-656 glyph slice — a served subtraction prompt's operator glyph is one of
// THREE code points a real author (or `speakableText`'s own fold, which is
// the reason all three are already spoken as "minus" — see
// SpeakPromptButton.tsx) can legitimately produce: ASCII HYPHEN-MINUS
// (U+002D, what every prompt used before this slice), MINUS SIGN (U+2212,
// what this slice writes to match the caption every renderer already draws),
// and EN DASH (U+2013, folded to "minus" by speakableText even though no
// content in this corpus currently authors it that way). Before this fix,
// `PLAIN_EQ` and the "what is A op B" regex below matched ONLY the ASCII
// hyphen, so an author typing the CORRECT minus glyph (U+2212) silently lost
// their `visualScaffold`/`mathVisual` — a prompt that displays "3 − 1 = ?"
// mapped to `model = null`, dropping the ten-frame/take-away picture the
// child needs, with no error anywhere. Every operator-matching regex in this
// file (and its `contentDispositionCheck.mjs` ledger mirror — kept in sync
// by hand, see that file's own copy of this comment) must use this class,
// not a hand-rolled `[+-]`, so a future glyph never silently degrades to "no
// picture" again.
// RED round 1 MF2 — exported (not module-private) so every OTHER reader of a
// served prompt's operator glyph, anywhere in the module graph that can
// import a .ts file (subtractionPoolHygieneGuard.mjs runs under tsx and does;
// mathPromptAnswerTokenGuard.mjs runs under plain `node` and can't — it keeps
// its own hand-copy, same as contentDispositionCheck.mjs's `mirrorPromptModel`
// mirror, which is deliberately decoupled by design and self-tests its own
// class against this one instead), uses this ONE definition instead of
// re-typing the character class. A re-typed copy is exactly how the
// `subtractionPoolHygieneGuard.mjs` bug (bare ASCII-only `/-/`) and the MF2
// findings (this file's own OWN model-less op sniffer, 60 lines below, having
// forgotten U+2013) both happened.
export const MINUS_GLYPHS = "\\u2212\\u2013-"; // U+2212, U+2013, then ASCII "-" (last, unescaped-safe in a char class)
export const OP_CLASS = `[+${MINUS_GLYPHS}]`;
/** True for any of the three minus glyphs (U+2212, U+2013, ASCII "-"); false
 *  for "+". Callers normalize to ONE internal op value ("-") via this
 *  predicate instead of comparing a captured operator token to a literal
 *  string, so downstream code never has to know there are three glyphs. */
export const isMinusGlyph = (tok: string): boolean => tok !== "+";
const PLAIN_EQ = new RegExp(`(\\d+)\\s*(${OP_CLASS})\\s*(\\d+)\\s*=`);

// MA-676 (closure-instrument slice 1, MA-665): closed-set classification of a
// served math prompt's SHAPE, used only for a live-QA served-surface probe
// (scripts/live-qa/scenarios/ma665NumberSentence.mjs) — never the answer, never
// operands, just whether the prompt states the number sentence the MA-665
// content fix authors (e.g. "5 + ___ = 12" / "___ + 5 = 12"). Pure, no item
// context beyond the raw prompt string.
const MISSING_ADDEND_SENTENCE_RE = /^(\d+ \+ ___|___ \+ \d+) = \d+$/;
export function promptShapeOf(prompt: string): "missing-addend-sentence" | "other" {
  if (typeof prompt !== "string") return "other";
  return MISSING_ADDEND_SENTENCE_RE.test(prompt.trim()) ? "missing-addend-sentence" : "other";
}

// MA-701 (closure-instrument slice): closed-set classification of a served
// math prompt's OPERATOR GLYPH, for a live-QA served-surface probe
// (scripts/live-qa/scenarios/ma656MinusGlyphServed.mjs) — never the answer,
// never operands, just whether the operator between two operand tokens is
// the forbidden ASCII "-"/U+2013 class (MA-656) or the correct MINUS SIGN
// (U+2212) / "+".
//
// MA-701 round 2 (RED must-fix 4 / nit): round 1's comment CLAIMED this
// reuses the file's own MINUS_GLYPHS definition, but the two regexes below
// actually hand-rolled their own `[–-]` / `[−+]` character classes
// — a false claim RED caught by reading the code, not just the comment.
// Fixed here for real: FORBIDDEN_MINUS_GLYPHS is DERIVED from MINUS_GLYPHS
// itself (every glyph in MINUS_GLYPHS except the one correct MINUS SIGN),
// so a future edit to MINUS_GLYPHS (e.g. discovering a fourth forbidden
// glyph, exactly the class of miss MF2 found — this file's own OWN
// model-less op sniffer once forgot U+2013) is picked up here automatically
// instead of needing a second hand-edit that can silently drift, same
// reasoning as MINUS_GLYPHS's own header comment above.
const OPERAND_TOKEN = `(?:\\d+|${BLANK})`;
const CORRECT_MINUS_SIGN = "\\u2212";
const FORBIDDEN_MINUS_GLYPHS = MINUS_GLYPHS.replace(CORRECT_MINUS_SIGN, ""); // MINUS_GLYPHS minus the one correct glyph
const FORBIDDEN_MINUS_OPERATOR_RE = new RegExp(`${OPERAND_TOKEN}\\s*[${FORBIDDEN_MINUS_GLYPHS}]\\s*${OPERAND_TOKEN}`);
const CLEAN_OPERATOR_RE = new RegExp(`${OPERAND_TOKEN}\\s*[+${CORRECT_MINUS_SIGN}]\\s*${OPERAND_TOKEN}`);
export function promptMinusGlyphStatus(prompt: string): "ascii" | "clean" | "no-operator" {
  if (typeof prompt !== "string") return "no-operator";
  if (FORBIDDEN_MINUS_OPERATOR_RE.test(prompt)) return "ascii";
  if (CLEAN_OPERATOR_RE.test(prompt)) return "clean";
  return "no-operator";
}

/** Parse a math exemplar into the model the scaffolds draw from. Returns
 * null when the prompt can't be parsed OR the parse fails to reproduce
 * the authored answer — callers then emit no scaffold rather than draw a
 * guess (drop-not-break). */
export function parseMathItem(item: MathExemplar): ScaffoldModel | null {
  const p = item.prompt;
  const t = item.itemType ?? "";
  const exp = Number(item.expectedAnswer);
  let m: RegExpMatchArray | null;
  let model: ScaffoldModel | null = null;

  const add = (a: number, b: number): ScaffoldModel =>
    ({ mode: "add", a, b, whole: a + b, impliedAnswer: a + b });
  const sub = (a: number, b: number): ScaffoldModel =>
    ({ mode: "sub", a, b, whole: a, impliedAnswer: a - b });
  const count = (n: number): ScaffoldModel =>
    ({ mode: "count", a: n, b: 0, whole: n, impliedAnswer: n });

  if (t === "count_objects" || t === "count_touch_objects") {
    // MA-C1 (ADR-012): the count is the ANSWER, not an a/b operation. Derive
    // it from the authored expectedAnswer (authoritative) so the no-digit
    // "Count the stars" items (ME0001-ME0012, prompt has no number but carry
    // stale a:9/b:2) no longer return null -> buildMathVisual fall through ->
    // a wrong a+b two-group picture. Fall back to a prompt digit only when
    // expectedAnswer is non-numeric. Matches subitize_to5 below.
    if (!Number.isNaN(exp)) model = count(exp);
    else {
      const n = nums(p)[0];
      if (n != null) model = count(n);
    }
  } else if (t === "subitize_to5") {
    // Subitize prompts carry no number ("How many dots…?"); the count is
    // the answer.
    if (!Number.isNaN(exp)) model = count(exp);
  } else if ((m = p.match(/show the number\s*(\d+)/i))) {
    model = count(Number(m[1]));
  } else if ((m = p.match(/more:\s*(\d+)\D+?(\d+)/i))) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    model = { mode: "compare", a, b, whole: Math.max(a, b), impliedAnswer: Math.max(a, b) };
  } else if ((m = p.match(new RegExp(`what is\\s*(\\d+)\\s*(${OP_CLASS})\\s*(\\d+)`, "i")))) {
    // Fact-family tail "…What is A op B?" — the operands are the LAST
    // pair, not the family triple (a/b fields point at the triple).
    model = isMinusGlyph(m[2]) ? sub(Number(m[1]), Number(m[3])) : add(Number(m[1]), Number(m[3]));
  } else if ((m = p.match(new RegExp(BLANK + "\\s*\\+\\s*(\\d+)\\s*=\\s*(\\d+)")))) {
    // Blank-first missing addend: "___ + B = C" → missing = C − B.
    const known = Number(m[1]);
    const whole = Number(m[2]);
    model = { mode: "missing_addend", a: known, b: whole - known, whole, impliedAnswer: whole - known, blankSide: "left" };
  } else if ((m = p.match(new RegExp("(\\d+)\\s*\\+\\s*" + BLANK + "\\s*=\\s*(\\d+)")))) {
    // Blank-second missing addend: "A + ___ = C" → missing = C − A.
    const known = Number(m[1]);
    const whole = Number(m[2]);
    model = { mode: "missing_addend", a: known, b: whole - known, whole, impliedAnswer: whole - known, blankSide: "right" };
  } else if ((m = p.match(new RegExp(BLANK + "\\s*[" + MINUS_GLYPHS + "]\\s*(\\d+)\\s*=\\s*(\\d+)")))) {
    // MA-703 — blank-minuend subtraction: "___ − B = C" → missing minuend
    // = C + B. `whole` (the magnitude supportsForModel's F5/T10/NB ladder
    // switches on) is the minuend itself, exactly like a normal `sub`
    // item's `whole`, even though here it is also the answer — see the
    // ScaffoldModel `blank` field's own doc comment for why that's safe.
    const b = Number(m[1]);
    const c = Number(m[2]);
    const a = c + b;
    model = { mode: "sub", a, b, whole: a, impliedAnswer: a, blank: "minuend" };
  } else if ((m = p.match(new RegExp("(\\d+)\\s*[" + MINUS_GLYPHS + "]\\s*" + BLANK + "\\s*=\\s*(\\d+)")))) {
    // MA-703 — blank-subtrahend subtraction: "A − ___ = C" → missing
    // subtrahend = A − C.
    const a = Number(m[1]);
    const c = Number(m[2]);
    const b = a - c;
    model = { mode: "sub", a, b, whole: a, impliedAnswer: b, blank: "subtrahend" };
  } else if ((m = p.match(PLAIN_EQ))) {
    model = isMinusGlyph(m[2]) ? sub(Number(m[1]), Number(m[3])) : add(Number(m[1]), Number(m[3]));
  } else if ((m = p.match(/has\s*(\d+)\D+?gets\s*(\d+)\s*more/i))) {
    model = add(Number(m[1]), Number(m[2]));
  } else if ((m = p.match(/^(\d+) is one part of (\d+)\. What is the other part\?$/))) {
    // MA-656 round 2 (bond-routing): the 6 subtraction fact-family items
    // (ME0098/0100/0102/0104/0106/0108). Anchored to the EXACT round-1
    // rewrite template — cross-checked below against the record's own a/c
    // fields (its authoritative operand provenance) before trusting the
    // regex capture, and again against expectedAnswer via the degenerate/
    // fail-closed rules mirroring partWholeFromStimulus. Any mismatch on
    // either check falls through to `model = null` (no visual) rather than
    // drawing a guess.
    const known = Number(m[1]);
    const whole = Number(m[2]);
    const recordA = Number((item as unknown as { a?: number }).a);
    const recordC = Number((item as unknown as { c?: number }).c);
    if (known === recordA && whole === recordC && known > 0 && known < whole && whole - known === exp) {
      model = { mode: "missing_addend", a: known, b: whole - known, whole, impliedAnswer: whole - known };
    }
  } else if ((m = p.match(/^The parts are (\d+) and (\d+)\. What is the whole\?$/))) {
    // MA-656 round 2 (bond-routing): the 6 addition fact-family items
    // (ME0097/0099/0101/0103/0105/0107). Same anchored-template +
    // cross-check discipline as the subtraction branch above, against a/b.
    const x = Number(m[1]);
    const y = Number(m[2]);
    const recordA = Number((item as unknown as { a?: number }).a);
    const recordB = Number((item as unknown as { b?: number }).b);
    if (x === recordA && y === recordB && x + y === exp) {
      model = add(x, y);
    }
  }

  // Missing-addend items (ms.math.12) are authored with the equation in
  // `sourceStimulus_v2.details` ("5 + ___ = 12"), not the prompt ("What
  // number goes in the blank?"), so none of the prompt regexes above can
  // see them and they fell through to a generic synth-split visual that
  // drew fabricated operands and REVEALED the answer (e.g. a "two_ten_frames"
  // item drew 4+3=7). Read the authored structured stimulus instead and
  // build the SAME missing_addend model the blank-equation prompt path
  // produces, which routes to the answer-safe number-bond hint (whole +
  // known part + "?"; supportsForModel → ["NB"]) and suppresses the
  // misleading counter (buildMathVisual draws a+b=whole !== the answer →
  // dropped). Fail-closed: only fires when the prompt parse found nothing
  // AND the stimulus carries an explicit addend-unknown signature whose
  // numbers reproduce the authored answer. ADR-024 / MA-C8.
  if (!model) {
    // MA-94 (+ the ADR-024 missing-addend precursor): the prompt carries no
    // operands — read the authored structured stimulus instead. Every reader
    // is fail-closed (emits only when it reproduces the authored answer) and
    // the model is marked stimulus-sourced so the emitted visual is shown by
    // default (the prompt alone is unanswerable; hiding the picture would
    // serve a blank question — the exact class the 2026-07-03 prod QA hit).
    const fromStimulus =
      missingAddendFromStimulus(item, exp) ??
      stimulusOperandsFromDetails(item, exp) ??
      twoDigitAddendsFromStimulus(item, exp) ??
      partWholeFromStimulus(item, exp) ??
      countFromSubitizeStimulus(item, exp);
    if (fromStimulus) {
      fromStimulus.source = "stimulus";
      model = fromStimulus;
    }
  }
  if (!model) return null;
  // Trust the model only when it reproduces the authored answer.
  if (!Number.isNaN(exp) && model.impliedAnswer !== exp) return null;
  return model;
}

/** Build a missing-addend model from an item's authored `sourceStimulus_v2`
 *  when the prompt itself has no parseable equation. Fail-closed: returns
 *  null unless the stimulus carries the explicit addend-unknown signature
 *  (numeric knownAddend + total, an addition-with-blank equation, an
 *  addend `unknownPosition`) AND `total - knownAddend` reproduces the
 *  authored answer. This can NOT fire on subtraction / comparison /
 *  sum-unknown items, and a degenerate bond (known 0 → whole-0 reveals the
 *  answer; known ≥ whole → answer ≤ 0) is dropped rather than drawn. */
function missingAddendFromStimulus(
  item: MathExemplar,
  exp: number,
): ScaffoldModel | null {
  const det = item.sourceStimulus_v2?.details;
  if (!det) return null;
  const known = Number(det.knownAddend);
  const whole = Number(det.total);
  const pos = det.unknownPosition;
  // Addend-unknown signature only (not minuend/subtrahend/sum unknown).
  if (pos !== "first" && pos !== "second") return null;
  if (!Number.isFinite(known) || !Number.isFinite(whole)) return null;
  // The equation, when present, must be an addition with a blank — this
  // rules out any stimulus shaped like a subtraction/comparison that
  // happened to carry knownAddend/total keys.
  const eq = typeof det.equation === "string" ? det.equation : "";
  if (eq && !(/\+/.test(eq) && /(?:_{2,}|＿+|□|\?)/.test(eq))) return null;
  // Degenerate / leak-by-whole: drop rather than draw.
  if (known <= 0 || known >= whole) return null;
  const missing = whole - known;
  // Fail-closed: the derived missing part MUST be the authored answer.
  if (Number.isNaN(exp) || missing !== exp) return null;
  return { mode: "missing_addend", a: known, b: missing, whole, impliedAnswer: missing };
}

/** MA-94 — the general stimulus-operand reader. ~207 launch_safe
 *  `directPractice` add/sub items (ms.math.07/08/09/10/11 + narrative 15)
 *  carry their operands ONLY in `sourceStimulus_v2.details.{addends,
 *  operation,result}` (or `{firstCount,secondCount,operation}` for
 *  two-ten-frames, `{numbers,operation}` for narratives) — absent from the
 *  prompt ("How many are left?", "What is the sum?") and from top-level
 *  a/b — so the prompt parse returns null and the item rendered BLANK.
 *  Fail-closed on THREE independent checks: (1) the operation must be an
 *  explicit add/subtract token; (2) when the stimulus carries `result`, the
 *  reconstructed value must equal it; (3) parseMathItem's final gate — the
 *  model's impliedAnswer must equal the authored expectedAnswer. A stimulus
 *  that fails any check (e.g. the 25 missing-operand equation items whose
 *  addends do NOT reconstruct the answer, or ms.math.14's operation-less
 *  two-digit pairs) yields NO model — no picture is drawn from a guess. */
function stimulusOperandsFromDetails(
  item: MathExemplar,
  exp: number,
): ScaffoldModel | null {
  const det = item.sourceStimulus_v2?.details;
  if (!det || Number.isNaN(exp)) return null;
  const op = typeof det.operation === "string" ? det.operation : "";
  const isAdd = /^add(?:ition)?$/i.test(op);
  const isSub = /^subtract(?:ion)?$/i.test(op);
  if (!isAdd && !isSub) return null;
  // Operand pair: addends [a,b] | two-ten-frames counts (addition
  // firstCount/secondCount, subtraction startCount/crossedOut) | number-line
  // start + jumps (b = the summed jump distance) | narrative numbers.
  let pairSrc = null;
  if (Array.isArray(det.addends)) pairSrc = det.addends;
  else if (Array.isArray(det.numbers)) pairSrc = det.numbers;
  else if (det.firstCount !== undefined && det.secondCount !== undefined)
    pairSrc = [det.firstCount, det.secondCount];
  else if (det.startCount !== undefined && det.crossedOut !== undefined)
    pairSrc = [det.startCount, det.crossedOut];
  else if (det.start !== undefined && Array.isArray(det.jumps) && det.jumps.length > 0) {
    // Number-line jumps: addition authors non-negative jumps; subtraction
    // authors NEGATIVE jumps (jump back, e.g. start 15, jumps [-7]). The
    // operand is the total jump DISTANCE — all jumps must agree in direction
    // with the operation, else no model (fail-closed on mixed/contradicting
    // signs; the implied-answer gate below re-verifies the arithmetic).
    const jumps = det.jumps.map(Number);
    const allFinite = jumps.every((j) => Number.isFinite(j));
    const directionOk = isAdd ? jumps.every((j) => j >= 0) : jumps.every((j) => j <= 0);
    if (allFinite && directionOk) {
      pairSrc = [det.start, Math.abs(jumps.reduce((x, y) => x + y, 0))];
    }
  }
  if (!pairSrc || pairSrc.length !== 2) return null;
  const a = Number(pairSrc[0]);
  const b = Number(pairSrc[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a < 0 || b < 0) return null;
  const implied = isAdd ? a + b : a - b;
  // Cross-check the authored result when present; reject a contradicting one.
  const authored = Number(det.result);
  if (det.result !== undefined && (!Number.isFinite(authored) || authored !== implied)) return null;
  // The reconstruction must reproduce the authored answer (else this is a
  // missing-operand/comparison shape and any drawn pair would mislead/reveal).
  if (implied !== exp) return null;
  return isAdd
    ? { mode: "add", a, b, whole: a + b, impliedAnswer: a + b }
    : { mode: "sub", a, b, whole: a, impliedAnswer: a - b };
}

/** MA-C22 — the two-digit ADDITION (ms.math.14) addend pair. These 30
 *  `directPractice` "Add the two numbers." items carry their operands in
 *  `sourceStimulus_v2.details` as EITHER `{addends:[a,b]}` (base_ten_blocks
 *  flavor) or `{start,addend}` (number_line flavor) — but, unlike the ~207
 *  items `stimulusOperandsFromDetails` reads, with NO `operation` field
 *  (verified: this shape-without-operation combination is unique to
 *  ms.math.14 across the corpus), so that reader's op guard drops them and
 *  they rendered BLANK. Split out as its own reader (rather than loosening
 *  `stimulusOperandsFromDetails`'s operation requirement, which every other
 *  caller relies on to avoid guessing add-vs-subtract). */
function twoDigitAddendPair(item: MathExemplar): [number, number] | null {
  const det = item.sourceStimulus_v2?.details;
  if (!det || det.operation !== undefined) return null;
  if (Array.isArray(det.addends) && det.addends.length === 2) {
    const a = Number(det.addends[0]);
    const b = Number(det.addends[1]);
    return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
  }
  if (det.start !== undefined && det.addend !== undefined) {
    const a = Number(det.start);
    const b = Number(det.addend);
    return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
  }
  return null;
}

/** MA-C22 — builds the `add` model from twoDigitAddendPair. Fail-closed: the
 *  pair must sum to the authored answer (parseMathItem's final gate
 *  re-checks this too, but a mismatched pair should never even reach the
 *  prompt-stem rewrite below). */
function twoDigitAddendsFromStimulus(item: MathExemplar, exp: number): ScaffoldModel | null {
  if (Number.isNaN(exp)) return null;
  const pair = twoDigitAddendPair(item);
  if (!pair) return null;
  const [a, b] = pair;
  if (a + b !== exp) return null;
  return { mode: "add", a, b, whole: a + b, impliedAnswer: a + b };
}

/** MA-114 — the part-whole sibling of missingAddendFromStimulus. The
 *  ms.math.05/06 "Which part is missing?" families author
 *  `{knownPart,totalTarget,missingPart}` (NOT `{knownAddend,total}`), so the
 *  ADR-024 reader never fired and the items fell through to the synth-split,
 *  which drew a picture of exactly `expectedAnswer` objects — the
 *  hint-revealable visual DEPICTED THE ANSWER COUNT. Building the
 *  missing_addend model instead routes them to the answer-safe number-bond
 *  scaffold (whole + known part + "?") and the ADR-020 gate suppresses the
 *  counter picture (known+missing = whole ≠ expectedAnswer). Fail-closed and
 *  degenerate-dropping exactly like missingAddendFromStimulus: known ≤ 0
 *  (whole alone reveals) and known ≥ whole (answer ≤ 0) yield no model —
 *  those stay honestly pictureless/grandfathered. */
function partWholeFromStimulus(
  item: MathExemplar,
  exp: number,
): ScaffoldModel | null {
  const det = item.sourceStimulus_v2?.details;
  if (!det || Number.isNaN(exp)) return null;
  const known = Number(det.knownPart);
  const whole = Number(det.totalTarget);
  const missing = Number(det.missingPart);
  if (!Number.isFinite(known) || !Number.isFinite(whole) || !Number.isFinite(missing)) return null;
  if (known <= 0 || known >= whole) return null; // degenerate / leak-by-whole
  if (known + missing !== whole) return null;
  if (missing !== exp) return null;
  return { mode: "missing_addend", a: known, b: missing, whole, impliedAnswer: missing };
}

/** MA-C18 — the subitize count reader. ms.math.01 dot_card/five_frame/
 *  picture_set exemplars ("Quick look. How many?") carry the count ONLY in
 *  `sourceStimulus_v2.details.count` — the prompt has no digits and itemType is
 *  `directPractice`, so neither the prompt parse nor the count/subitize itemType
 *  branch fires and they rendered BLANK. Build the count model (the answer IS
 *  the count) fail-closed: the visualSupport must be a subitize card, the count
 *  a positive integer, and — where the stimulus carries a SECOND signal
 *  (five_frame `filledPositions`, picture_set `fingersUp`) — its length must
 *  ALSO equal the count (two-source cross-check). parseMathItem's final gate
 *  then re-verifies count === expectedAnswer. */
const SUBITIZE_SUPPORTS = new Set(["dot_card", "five_frame", "picture_set"]);
/** MA-824 (operator ruling D1) — the quick-look time for a subitize card that
 *  authors no `displayTimeMs` (the 28 `ME0025-0036` / `MEG_M01_*` records). */
export const DEFAULT_QUICK_LOOK_MS = 1500;
/** MA-824 — the authored look time when it is a positive integer, else the
 *  recorded default. Never 0 / NaN (a zero look would hide the picture). */
function quickLookMsFor(item: { sourceStimulus_v2?: { details?: object } }): number {
  const det = item.sourceStimulus_v2?.details as { displayTimeMs?: unknown } | undefined;
  const ms = Number(det?.displayTimeMs);
  return Number.isInteger(ms) && ms > 0 ? ms : DEFAULT_QUICK_LOOK_MS;
}
function countFromSubitizeStimulus(
  item: MathExemplar,
  exp: number,
): ScaffoldModel | null {
  const raw = typeof item.visualSupport === "string" ? item.visualSupport : "";
  if (!SUBITIZE_SUPPORTS.has(raw)) return null;
  const det = item.sourceStimulus_v2?.details;
  if (!det || Number.isNaN(exp)) return null;
  const count = Number(det.count);
  if (!Number.isInteger(count) || count <= 0) return null;
  // Two-source cross-check: a second authored signal must agree with the count.
  if (raw === "five_frame" && Array.isArray(det.filledPositions) && det.filledPositions.length !== count) return null;
  if (raw === "picture_set" && Array.isArray(det.fingersUp) && det.fingersUp.length !== count) return null;
  if (count !== exp) return null;
  return { mode: "count", a: count, b: 0, whole: count, impliedAnswer: count };
}

/** Choose visual supports whose drawn quantity is correct AND renderable
 * for this item's magnitude. Out-of-range scaffolds are dropped, not
 * rendered broken (counting never gets a number-line jump; values that
 * overflow a frame fall back to the bond/base-ten). */
export function supportsForModel(
  model: ScaffoldModel,
  opts?: { visualSupport?: string; hasSourceStimulus?: boolean },
): SupportCode[] {
  const { mode, whole, blank } = model;
  switch (mode) {
    case "count":
      if (whole <= 5) return ["F5"];
      if (whole <= 10) return ["T10"];
      if (whole <= 20) return ["T10", "BT"];
      return ["BT"];
    case "add":
    case "sub": {
      // `whole` is the sum (add) or the minuend (sub) — the magnitude the
      // frame/number-line must span. MA-258: OBJ (loose countable dots) was
      // removed — it duplicated F5/T10's structured frames at every
      // magnitude with no distinct information, per ADR-078 (renumbered from 075 — dup with ADR-075-ma59).
      // MA-655 — ladder rule: a frame-prompted item must never offer F5
      // when the prompt says "ten frame" (MA-651 §C2 — 13 of the 21
      // frame-authored subtraction items carry a<=5, so without this they'd
      // open on a FIVE frame while the prompt/inline picture is a TEN
      // frame). Frame-authored subtraction (mode "sub", visualSupport is a
      // frame kind per FRAME_VISUALS/"ten_frame"/"frame") always prefers T10
      // over F5, at every magnitude the ten-frame renderer covers — mirrors
      // the routing gate in buildMathVisual's `isFrame` check below. This is
      // belt-and-braces alongside HintSlot's filterHintLadderForInlineVisual
      // (which drops T10 AND F5 once the frame picture is actually on
      // screen) — it also covers the moment before a STIMULUS-SOURCED frame
      // picture is shown for a fresh learner (visualRequired:true items are
      // still hidden until "Show hint" is tapped the first time; see
      // MA-94's visualRequired note above `buildMathVisual`).
      //
      // MA-655 RED must-fix 1: this must carry the SAME `sourceStimulus_v2
      // != null` guard as the mapper's routing arm (contentToEngineItem.ts,
      // buildMathVisual's `isFrame && ... && item.sourceStimulus_v2 != null`
      // check), or the bare `.includes("frame")` substring test matches the
      // generic multi-option visualSupport sentence ("...ten-frame, number
      // path, or object groups...") too. Without the guard, 8 ms.math.08
      // items authored generic (ME0049/0050/0051/0057/0058/0059/0062/0070,
      // all `visualRequired:false`, prompt "3 - 1 = ?" etc.) silently flip
      // their FIRST-hint scaffold from a five frame to a ten frame — the
      // opposite of what the F5/T10 magnitude split exists to do, and with
      // no on-screen picture to keep the ladder filter (HintSlot) from
      // masking it, since `inlineVisualKind` is undefined for those items.
      const isFrameAuthored =
        mode === "sub" &&
        opts?.hasSourceStimulus === true &&
        typeof opts?.visualSupport === "string" &&
        (FRAME_VISUALS.has(opts.visualSupport) ||
          opts.visualSupport.includes("ten_frame") ||
          opts.visualSupport.includes("frame")) &&
        opts.visualSupport !== "two_ten_frames";
      // R1 (operator ruling round 1, 2026-09-21): a `sub` model with `blank`
      // set must NEVER offer NL — the number line needs BOTH operands drawn
      // honestly (the auto-revealed start numeral, the countable hop count),
      // and for a blank model one of those two IS the answer (the minuend for
      // blank-minuend, the hop count for blank-subtrahend). RED measured this
      // as a 14/14 answer leak. The ladder for these 14 is therefore the
      // normal ladder MINUS NL — still non-empty (Acceptance), same F5/T10
      // magnitude split, same NB (now itself fixed to draw an honest bond —
      // see the `blank`-aware NB derivation in components/scaffolds/index.tsx).
      const nl = blank ? [] : ["NL" as const];
      if (whole <= 5) return isFrameAuthored ? ["T10", ...nl, "NB"] : ["F5", ...nl, "NB"];
      if (whole <= 20) return ["T10", ...nl, "NB"];
      return ["NB"]; // bond is text-only → correct at any magnitude
    }
    case "missing_addend":
      // MA-C11 (ADR-024 follow-up): authored number_line items get the
      // answer-safe NLMA scaffold (start + target + "?" arc, no intermediate
      // ticks) as their primary hint, with the bond as a fallback.
      // All other missing-addend items keep the bond-only path.
      if (opts?.visualSupport === "number_line") return ["NLMA", "NB"];
      return ["NB"]; // whole = sum, known part, "?" — never reveals the answer
    case "compare":
      return ["CMP"];
  }
}

/** The visualScaffold.data the scaffold components consume (see getParts
 * in components/scaffolds). */
export function scaffoldDataForModel(model: ScaffoldModel): {
  mode: ScaffoldMode;
  a: number;
  b: number;
  op: "+" | "-";
  whole: number;
  missing?: number;
  blank?: "minuend" | "subtrahend";
  blankSide?: "left" | "right";
} {
  return {
    mode: model.mode,
    a: model.a,
    b: model.b,
    op: model.mode === "sub" ? "-" : "+",
    whole: model.whole,
    missing: model.mode === "missing_addend" ? model.b : undefined,
    // MA-703 — forwarded so components/scaffolds/index.tsx's ScaffoldNB/T10/
    // F5 know which slot of a blank-subtraction `sub` model is the answer
    // (the same reasoning as `missing` above for `missing_addend`): without
    // this, ScaffoldNB's mode-keyed NB_UNKNOWN_SLOT["sub"]==="first" rule
    // (correct for a NORMAL full subtraction item, where the remainder is
    // the unknown) would print the WRONG cell as "?" and the ANSWER as a
    // plain numeral for these 14 items — a real leak MA-600's own test
    // (scaffoldContractTest.mjs) caught. Only the `blank` KEY ITSELF is
    // conditional (a real conditional spread, not `blank: model.blank ??
    // undefined`) — S-1 (operator ruling round 2, 2026-09-21): an earlier
    // draft ALSO carried an unconditional `blank: model.mode === "sub" ?
    // model.blank : undefined` property after this spread, which always
    // wins (a later key always overrides an earlier one in the same object
    // literal) and always creates the key (even set to `undefined`) — so
    // every ordinary sub item actually carried `"blank" in data === true`
    // despite the spread's own comment claiming otherwise. That dead
    // unconditional property is removed; only the spread remains, so
    // ordinary sub items truly lack the key.
    ...(model.mode === "sub" && model.blank ? { blank: model.blank } : {}),
    // MA-769 — which side of the equation the blank is on, for ScaffoldNB's
    // "?" placement. Conditional key (absent unless the side is known), so
    // every other item's data is byte-identical to before.
    ...(model.mode === "missing_addend" && model.blankSide ? { blankSide: model.blankSide } : {}),
  };
}

/** Math exemplar → EngineItem (multiple-choice). Options = the
 * correct answer + nearWrongAnswers, shuffled. */
// VISUALS-AS-HINTS — item types whose picture IS the question (must show by
// default; the prompt alone can't be answered).
const VISUAL_REQUIRED_ITEM_TYPES = new Set([
  "count_objects", "count_touch_objects", "subitize_to5", "count_coins", "tell_time",
  // MA-C17 — the two clock faces ARE the question (read-the-clock, twice).
  "elapsed_time",
]);
// Sequence/skip-count items: a counters/+ picture is MEANINGLESS for "2,4,6,__"
// (it drew an arbitrary 2+3 group). Emit no manipulative for these.
// MA-C3 (ADR-013): the equal-groups WORD-PROBLEM variants get NO manipulative
// (multi_step / missing_factor / compare / array_transfer). The answer for those
// is NOT a*b, so any counter picture either contradicts or reveals it.
// MA-C12: division_sharing now routes to its own partition renderer — empty rings
// + a pile of total items — for the "how many per group?" sub-type
// (expectedAnswer === b). The "how many groups?" sub-type (expectedAnswer === a)
// still gets no visual (handled by an explicit return in buildMathVisual below).
// Plain `equal_groups` (expectedAnswer === a*b) routes to the `array` renderer.
// MA-706: `skip_count_next`/`skip_count_offset_next`/`skip_count_missing_term`/
// `skip_count_reverse` are REMOVED from this set — they now route to the
// `skip_count_line` branch below (a reliably-parseable sequence-with-blank
// shape). `skip_count_two_step` STAYS here: its prompt asks a DERIVED
// quantity ("how many more...", "how many fives did you count") with no
// blank position in the shown sequence to draw — D2 of the MA-706 spec
// (word-problem/"how many more" shapes keep refusing a picture).
const NO_VISUAL_ITEM_TYPES = new Set([
  "skip_count_two_step",
  "equal_groups_multi_step", "equal_groups_missing_factor",
  "equal_groups_compare", "equal_groups_array_transfer",
]);

// MA-714 — the multiplicative item types whose authored `a`/`b` (or, for a
// missing-factor record, `[expectedAnswer, known factor]`) IS a
// multiplication factor pair — the only types on which the non-rendered
// `authoredFactorPair` is derived (see MappedEngineItem.authoredFactorPair).
const FACTOR_PAIR_ITEM_TYPES = new Set([
  "array_multiplication", "equal_groups",
  "equal_groups_multi_step", "equal_groups_missing_factor",
  "equal_groups_compare", "equal_groups_array_transfer",
]);

// MA-706 — the 4 skip-count item types whose prompt is a reliably-parseable
// "shown terms + one blank stop" sequence (see the MA-706 spec's Phase 0
// shape table). Each of these prompts is of the exact authored form
// "<lead-in>: t1, t2, ..., tN. What number comes next?" or "...is missing?",
// with the blank position marked literally as "___" IN the comma-separated
// list (never inferred).
const SKIP_COUNT_LINE_ITEM_TYPES = new Set([
  "skip_count_next", "skip_count_offset_next",
  "skip_count_missing_term", "skip_count_reverse",
]);

/** MA-706 — parse a skip-count prompt's shown-term sequence + blank
 *  position, fail-closed. Returns `null` (no picture) unless ALL of:
 *   - the prompt matches "<lead-in>: <comma list>. What ...?" with a
 *     3-8 token list (D4's stop cap);
 *   - the list contains EXACTLY one "___" token (the blank) and every
 *     other token is a bare non-negative integer;
 *   - at least one ADJACENT pair of SHOWN (non-blank) tokens exists, and
 *     EVERY such adjacent pair's difference has the same sign and the
 *     same magnitude as the item's own authored `a` (step) — a single
 *     inconsistent pair fails the whole parse, never partially trusted;
 *   - filling the blank by extrapolating `step` from its nearest shown
 *     neighbor reproduces `expectedAnswer` EXACTLY (this reconstruction is
 *     used only to VALIDATE the parse — the value itself is never
 *     returned/rendered; the blank's own array slot stays `null`).
 *  This mirrors every other buildMathVisual branch's "answer reproduces
 *  the parse, or no picture" discipline (e.g. `twoDigitAddStem`,
 *  `array_multiplication`'s rows*cols===total gate). */
function parseSkipCountLine(item: MathExemplar): { step: number; terms: (number | null)[] } | null {
  const prompt = String(item.prompt ?? "");
  const m = /:\s*([0-9,\s_]+)\.\s*What\b/i.exec(prompt);
  if (!m) return null;
  const rawTokens = m[1].split(",").map((t) => t.trim());
  if (rawTokens.length < 3 || rawTokens.length > 8) return null;
  const terms: (number | null)[] = [];
  let blankIndex = -1;
  for (let i = 0; i < rawTokens.length; i++) {
    const tok = rawTokens[i];
    if (/^_+$/.test(tok)) {
      if (blankIndex !== -1) return null; // more than one blank — refuse
      blankIndex = i;
      terms.push(null);
    } else if (/^\d+$/.test(tok)) {
      terms.push(Number(tok));
    } else {
      return null; // unparseable token — refuse
    }
  }
  if (blankIndex === -1) return null; // no blank at all — not this shape
  const authoredStep = Number(item.a);
  if (!Number.isFinite(authoredStep) || authoredStep <= 0) return null;
  let direction = 0; // +1 ascending, -1 descending, derived from real adjacent shown pairs
  for (let i = 0; i < terms.length - 1; i++) {
    const x = terms[i];
    const y = terms[i + 1];
    if (x === null || y === null) continue;
    const diff = y - x;
    if (Math.abs(diff) !== authoredStep) return null; // inconsistent with the authored step
    const dir = diff > 0 ? 1 : -1;
    if (direction === 0) direction = dir;
    else if (dir !== direction) return null; // inconsistent direction across the sequence
  }
  if (direction === 0) return null; // no adjacent shown pair found to anchor direction/step
  // Fail-closed reconstruction check (never rendered): extrapolate the
  // blank's value from its nearest shown neighbor and confirm it equals
  // the authored answer exactly.
  const neighborIndex = blankIndex > 0 ? blankIndex - 1 : blankIndex + 1;
  const neighborVal = terms[neighborIndex];
  if (neighborVal === null || neighborIndex < 0 || neighborIndex >= terms.length) return null;
  const stepsAway = blankIndex - neighborIndex; // always +1 or -1 by construction
  const reconstructed = neighborVal + direction * authoredStep * stepsAway;
  const exp = Number(item.expectedAnswer);
  if (!Number.isFinite(exp) || reconstructed !== exp) return null;
  return { step: authoredStep, terms };
}

/** MA-706 (hint-rung slice, operator rulings rounds 1-2) — the hint rung for
 *  `skip_count_two_step` items, which get NO picture (NO_VISUAL_ITEM_TYPES,
 *  D2) and, before this, no hint ladder either. Fail-closed, keyed on the
 *  PROMPT SHAPE (never an id list): returns `null` (no rung) unless every
 *  check below passes. Two shapes, each reusing an EXISTING scaffold:
 *
 *   - "how many more … reach T?" (e.g. "10, 20, 30. How many more to reach
 *     34?") -> `NLMA`: start = the last shown term, target = the stated T.
 *     ScaffoldNLMA labels only 0, start and target with one dashed "?" arc —
 *     the answer (T − last) is never drawn. `data` deliberately carries no
 *     `missing`/`b` field (getParts derives it; nothing renders it).
 *   - "how many <fives> did you count …?" -> `SKL`: the EXISTING
 *     SkipCountLineRenderer, drawn ONLY inside the hint panel (never as an
 *     inline `mathVisual` — these items stay in NO_VISUAL_ITEM_TYPES), with
 *     terms 0, step, 2·step … N, every stop labelled, no blank stop. The
 *     number of JUMPS equals the answer: counting the jumps IS the skill
 *     these items teach, so this is a hint-reveal by design (answerVisible
 *     InPicture R2 — the `skip_count_line` contract counts jumps as readable
 *     and answerVisiblePictureGuard's hint-panel rule pins the verdict).
 *
 *  Shared checks: the prompt's comma list is ≥2 bare integers that are
 *  exactly step, 2·step, … (i.e. counted from 0 by the authored `a`); the
 *  derived answer reproduces `expectedAnswer` exactly. Exported for
 *  scaffoldContractTest.mjs, which validates this same contract. */
export function skipCountTwoStepHint(
  item: Pick<MathExemplar, "itemType" | "prompt" | "a" | "expectedAnswer">,
): { supports: SupportCode[]; visualScaffold: { kind: string; data: Record<string, unknown> } } | null {
  if ((item.itemType ?? "") !== "skip_count_two_step") return null;
  const prompt = String(item.prompt ?? "");
  const step = Number(item.a);
  const exp = Number(item.expectedAnswer);
  if (!Number.isInteger(step) || step <= 0 || !Number.isInteger(exp) || exp <= 0) return null;
  const listMatch = /(\d+(?:\s*,\s*\d+)+)\s*[.?]/.exec(prompt);
  if (!listMatch) return null;
  const shown = listMatch[1].split(",").map((t) => Number(t.trim()));
  if (shown.length < 2 || !shown.every((n, i) => n === step * (i + 1))) return null;
  const last = shown[shown.length - 1];
  const moreMatch = /How many more\b[^?]*\breach (\d+)\?$/i.exec(prompt);
  if (moreMatch) {
    const target = Number(moreMatch[1]);
    if (!(target > last) || target - last !== exp) return null;
    return {
      supports: ["NLMA"],
      visualScaffold: { kind: "skip_count_how_many_more", data: { mode: "missing_addend", a: last, whole: target } },
    };
  }
  const WORD_STEP: Record<string, number> = { twos: 2, threes: 3, fours: 4, fives: 5, tens: 10 };
  const countMatch = /How many (twos|threes|fours|fives|tens) did you count\b[^?]*\?$/i.exec(prompt);
  if (countMatch) {
    if (WORD_STEP[countMatch[1].toLowerCase()] !== step) return null;
    if (shown.length !== exp) return null;
    return {
      supports: ["SKL"],
      visualScaffold: { kind: "skip_count_jumps", data: { terms: [0, ...shown], step } },
    };
  }
  return null;
}

// MA-C6 (ADR-021): a `fraction` item gets the `fraction_bar` manipulative ONLY
// when the kid must READ the bar to answer ("What fraction of the bar is
// shaded?", ms.math.22) — there the bar IS the question's referent, exactly
// like "Count the stars" shows the stars (the answer is read off the picture,
// not given away by it). The fraction COMPARISON/SELECTION items (ms.math.23
// "Tap the biggest share"; ms.math.28 "Tap the biggest fraction" / "Tap the
// fraction equal to N/N") encode the ANSWER in the bar's a/b, and the single-
// bar renderer can only draw THAT one fraction — so any bar it produces depicts
// the correct option and reveals the answer (the authored "fraction bars to
// compare" intent needs a multi-bar renderer that does not exist yet). Per the
// principle ADR-020 settled — a revealable answer-revealing hint is a give-away,
// not made safe by being hidden-by-default — those items get NO picture; the
// prompt + the answer options are the question. This single predicate drives
// BOTH the mathVisual emission AND `visualRequired`, so for fractions the two
// stay in lockstep (visualRequired === (mathVisual != null)) by construction.
function isReadTheBarFraction(item: { itemType?: string; prompt?: unknown }): boolean {
  return (item.itemType ?? "") === "fraction" && /\bshaded\b/i.test(String(item.prompt ?? ""));
}

/** MA-709 (half b) — whether every fraction a `fraction_comparison` picture
 *  draws is ALSO stated in symbols (`n/d`, as a whole token) on the served
 *  prompt or answer tiles. When true the child can answer from the symbols
 *  alone, so the bars are an optional support, not the question. */
export function comparedFractionsStatedSymbolically(
  mathVisual: { kind?: string; fractions?: Array<{ a: number; b: number }> } | null | undefined,
  prompt: string,
  options: readonly string[],
): boolean {
  if (mathVisual?.kind !== "fraction_comparison" || !Array.isArray(mathVisual.fractions)) return false;
  if (mathVisual.fractions.length === 0) return false;
  const stated = new Set<string>();
  for (const text of [prompt, ...options]) {
    for (const m of String(text ?? "").matchAll(/(?<![\d/])(\d+)\s*\/\s*(\d+)(?![\d/])/g)) {
      stated.add(`${Number(m[1])}/${Number(m[2])}`);
    }
  }
  return mathVisual.fractions.every((f) => stated.has(`${f.a}/${f.b}`));
}

/** MA-709 round 1 (R3) — a prompt that asks the child to READ the picture
 *  ("What fraction of the bar is shaded?", ms.math.22's read-the-bar stem;
 *  "…is colored?", "…part of the…", "…shown…", "…picture…"). Derived from
 *  the real ms.math.22 read-the-bar prompts (which ARE required); none of the
 *  served ms.math.23/28 comparison prompts match (pinned in
 *  ma709ScaffoldVisibilityContract.test.mjs). */
export const READS_THE_PICTURE_PROMPT =
  /\b(colou?red|shaded|shown|filled|picture|part of the|what fraction)\b/i;

/** MA-709 (half b; round 1 R3) — the `fraction_comparison` arm of
 *  `visualRequired`: the answer-safe multi-bar picture (>= 2 un-highlighted
 *  bars) is NOT required only when BOTH (i) every compared fraction is
 *  printed in symbols on the served prompt/tiles AND (ii) the prompt does not
 *  ask the child to read the picture (`READS_THE_PICTURE_PROMPT`). Otherwise
 *  the bars are (part of) the question and stay required. NOTE (RED S1):
 *  today's builder only emits `fraction_comparison` when every option parses
 *  as `n/d`, so (i) always holds for real output — (ii) is the condition
 *  that can actually bite on a future reworded item ("What fraction of the
 *  pizza is colored?" would otherwise map to optional option-bars). */
export function fractionComparisonPictureRequired(
  mathVisual: { kind?: string; fractions?: Array<{ a: number; b: number }> } | null | undefined,
  prompt: string,
  options: readonly string[],
): boolean {
  const isFractionComparisonVisual =
    mathVisual?.kind === "fraction_comparison" &&
    Array.isArray(mathVisual.fractions) &&
    mathVisual.fractions.length >= 2;
  if (!isFractionComparisonVisual) return false;
  const answerableFromSymbols =
    comparedFractionsStatedSymbolically(mathVisual, prompt, options) &&
    !READS_THE_PICTURE_PROMPT.test(String(prompt ?? ""));
  return !answerableFromSymbols;
}

const COIN_CENT_VALUES: Record<"quarter" | "dime" | "nickel" | "penny", number> = {
  quarter: 25,
  dime: 10,
  nickel: 5,
  penny: 1,
};
const COIN_WORD_KEY: Record<string, "quarter" | "dime" | "nickel" | "penny"> = {
  quarter: "quarter", quarters: "quarter",
  dime: "dime", dimes: "dime",
  nickel: "nickel", nickels: "nickel",
  penny: "penny", pennies: "penny", pennys: "penny",
};
const UNMODELED_COIN_DENOM = /\b(half[\s-]?dollars?|dollars?)\b/i;

/** MA-710 — the authored `coinCounts` of a `count_coins` record, normalised
 *  to all four keys. Fail-closed: `undefined` unless the value is an object
 *  whose keys are all known coin names, every count is a non-negative
 *  integer, at least one coin is present, and the cents add up to `cents`
 *  (the authored key) — so an authoring drift can never draw coins that
 *  contradict the answer. The caller then falls back to the prompt parse. */
function authoredCoinCounts(
  raw: unknown,
  cents: number,
): Record<"quarter" | "dime" | "nickel" | "penny", number> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const counts: Record<"quarter" | "dime" | "nickel" | "penny", number> = {
    quarter: 0, dime: 0, nickel: 0, penny: 0,
  };
  let coins = 0;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!Object.prototype.hasOwnProperty.call(COIN_CENT_VALUES, k)) return undefined;
    if (typeof v !== "number" || !Number.isInteger(v) || v < 0) return undefined;
    counts[k as keyof typeof counts] = v;
    coins += v;
  }
  if (coins === 0) return undefined;
  const sum = (Object.keys(counts) as (keyof typeof counts)[]).reduce(
    (acc, k) => acc + counts[k] * COIN_CENT_VALUES[k],
    0,
  );
  return sum === cents ? counts : undefined;
}

/** MA-710 — the unit a choice tile shows after each option. Every
 *  `count_coins` key and option is a number of cents, so their tiles read
 *  "11¢" (display only: the option VALUES stay bare numerals, so grading,
 *  the attempt log and the wrong-answer meanings keyed "10"/"12" are
 *  untouched). Absent for every other item type. */
export const COIN_OPTION_UNIT = "¢";

/** MA-C16 (ADR-022 fast-follow) — parse the coin multiset NAMED in a
 *  `count_coins` prompt ("(1 dime, 1 nickel)" or prose "3 nickels"). Returns
 *  the literal per-denomination counts ONLY when the named set's own cents
 *  total equals `cents` (the authored answer) and no unmodeled denomination
 *  (half-dollar/dollar) is present — otherwise `undefined`, so the caller
 *  falls back to the legacy greedy-split-of-total behavior (fail-closed: an
 *  authoring drift between the prompt text and the answer never produces a
 *  literal drawing that contradicts the answer). */
function parseLiteralCoinCounts(
  prompt: string,
  cents: number,
): Record<"quarter" | "dime" | "nickel" | "penny", number> | undefined {
  if (UNMODELED_COIN_DENOM.test(prompt)) return undefined;
  const counts: Record<"quarter" | "dime" | "nickel" | "penny", number> = {
    quarter: 0, dime: 0, nickel: 0, penny: 0,
  };
  let any = false;
  const re = /(\d+)\s+(quarters?|dimes?|nickels?|pennies|pennys?|penny)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(prompt)) !== null) {
    const n = Number(m[1]);
    const key = COIN_WORD_KEY[m[2].toLowerCase()];
    if (key && Number.isFinite(n)) {
      counts[key] += n;
      any = true;
    }
  }
  if (!any) return undefined;
  const sum = (Object.keys(counts) as (keyof typeof counts)[]).reduce(
    (s, k) => s + counts[k] * COIN_CENT_VALUES[k],
    0,
  );
  return sum === cents ? counts : undefined;
}

/** MA-94 — narrative (ms.math.15) story surfacing. The word-problem STORY
 *  ("Sam has 4 apples. Sam gets 3 more apples.") lives only in
 *  sourceStimulus_v2.details.story; the prompt is just the question ("How
 *  many apples does Sam have now?") — served blank+unanswerable. Prepend the
 *  story so the served text is self-answerable (the authored intent; NOT the
 *  deferred AI-illustration path). Fail-closed: only when the story's numbers
 *  reconstruct the authored answer under details.operation — a story that
 *  contradicts the answer is never surfaced. */
function narrativePrompt(item: MathExemplar): string | null {
  if ((typeof item.visualSupport === "string" ? item.visualSupport : "") !== "narrative") return null;
  const det = item.sourceStimulus_v2?.details;
  const story = det && typeof det.story === "string" ? det.story.trim() : "";
  if (!story) return null;
  const prompt = String(item.prompt ?? "");
  if (prompt.includes(story)) return null; // already surfaced upstream
  const exp = Number(item.expectedAnswer);
  if (Number.isNaN(exp)) return null;
  const op = typeof det?.operation === "string" ? det.operation : "";
  const nums = Array.isArray(det?.numbers) && det.numbers.length === 2
    ? det.numbers.map(Number)
    : (story.match(/\d+/g) ?? []).map(Number);
  if (nums.length !== 2 || !nums.every((n) => Number.isFinite(n))) return null;
  const implied = /^add(?:ition)?$/i.test(op)
    ? nums[0] + nums[1]
    : /^subtract(?:ion)?$/i.test(op)
      ? nums[0] - nums[1]
      : NaN;
  if (implied !== exp) return null;
  return `${story} ${prompt}`;
}

/** MA-C22 — rewrite the operand-free "Add the two numbers." stem into the
 *  actual equation ("23 + 14 = ?") once twoDigitAddendPair recovers the
 *  operands, so the item is answerable from the prompt TEXT alone — the same
 *  answer-safe surface the working single-digit "10 + 3 = ?" siblings on this
 *  skill already use. The sum itself is never printed. §3b RED F1: gate the
 *  rewrite on the SAME fail-closed `a + b === exp` check that
 *  twoDigitAddendsFromStimulus uses to build the model — so a mis-authored
 *  operand-free pair whose sum ≠ the authored answer can never be rewritten
 *  into a stem whose equation contradicts the correct option (stem/model
 *  coupling; today's corpus has 0 such items but the invariant is now enforced
 *  in code, not just a comment). */
function twoDigitAddStem(item: MathExemplar, exp: number): string | null {
  const pair = twoDigitAddendPair(item);
  if (!pair) return null;
  const [a, b] = pair;
  if (!Number.isFinite(exp) || a + b !== exp) return null;
  return `${a} + ${b} = ?`;
}

/** MA-1001 (RED r1 SHOULD 1) — the spoken numeral-target SHAPE, read from a
 *  mapped record's own data: the answer is a whole number 0..10, the prompt
 *  names exactly that number as a whole word and carries no digit, there are
 *  at least two options and every one is a digit string, and the mapped
 *  picture is a ten frame of the answer (drawn on MathSession; /play/foundation
 *  draws no picture). Fail-closed (any missing piece -> false). */
export function isSpokenNumeralTargetShape(rec: {
  prompt?: unknown;
  answer?: unknown;
  options?: readonly unknown[];
  mathVisual?: { kind?: string; total?: number } | null;
}): boolean {
  const answer = String(rec.answer ?? "").trim();
  if (!/^\d+$/.test(answer)) return false;
  const n = Number(answer);
  const word = NUMBER_WORDS[n];
  const prompt = typeof rec.prompt === "string" ? rec.prompt : "";
  const options = rec.options ?? [];
  return (
    word !== undefined && n <= 10 &&
    new RegExp(`\\b${word}\\b`, "i").test(prompt) && !/\d/.test(prompt) &&
    options.length >= 2 && options.every((o) => /^\d+$/.test(String(o).trim())) &&
    rec.mathVisual?.kind === "ten_frame" && rec.mathVisual.total === n
  );
}

// MA-652 RED round 1 — number words used by `promptOperandSource`'s
// served-prompt containment check below (0-20 covers every model operand
// seen in the real corpus). A model operand outside this range that isn't
// spelled as a digit reads as absent — fail-closed toward "stimulus"/"none",
// never a false "prompt". MA-1001 also reads them (isSpokenNumeralTargetShape,
// above).
const NUMBER_WORDS: Record<number, string> = {
  0: "zero", 1: "one", 2: "two", 3: "three", 4: "four", 5: "five",
  6: "six", 7: "seven", 8: "eight", 9: "nine", 10: "ten",
  11: "eleven", 12: "twelve", 13: "thirteen", 14: "fourteen", 15: "fifteen",
  16: "sixteen", 17: "seventeen", 18: "eighteen", 19: "nineteen", 20: "twenty",
};

/** Does the served prompt TEXT display this exact number, as a digit run or
 *  as its (0-20) number word, on a token boundary? Used by
 *  `computePromptOperandSource`'s containment check below and by MA-712's
 *  division_sharing gate in buildMathVisual (the pile may only draw a total
 *  the prompt states) — never by prompt/answer rendering. */
function numberAppearsInPrompt(prompt: string, n: number): boolean {
  if (!Number.isFinite(n)) return false;
  const digitRe = new RegExp(`(?<!\\d)${Math.trunc(Math.abs(n))}(?!\\d)`);
  if (digitRe.test(prompt)) return true;
  const word = NUMBER_WORDS[n];
  return word ? new RegExp(`\\b${word}\\b`, "i").test(prompt) : false;
}

/** MA-652 RED round 1 — the model operand values that must be visible in the
 *  served prompt for it to count as self-sufficient, per mode. Deliberately
 *  excludes whichever field IS the mode's answer:
 *    - `missing_addend`: keeps `a` (the known addend) and `whole` (the
 *      authored total) — `b` is the missing addend, i.e. `impliedAnswer`.
 *    - `count`: keeps only `a` — `count`'s `impliedAnswer` IS `a` (there is
 *      no separate given), so a subitize/count prompt with no number in it
 *      correctly reads as non-prompt-sourced (it needs the picture).
 *    - every other mode (`add`, `sub`, `compare`): both `a` and `b` are
 *      genuine givens, neither equals `impliedAnswer` in general.
 *  Never includes `impliedAnswer` itself under any mode.
 *
 *  MA-703: a `sub` model with `blank` set carries the SAME exclusion —
 *  whichever of `a`/`b` is the authored blank equals `impliedAnswer` and
 *  is never in the served prompt (that's the whole point of the blank);
 *  only the known operand is required for the prompt to count as
 *  self-sufficient. Mirrors the `missing_addend` carve-out immediately
 *  above. */
function modelOperandsForContainment(model: ScaffoldModel): number[] {
  if (model.mode === "missing_addend") return [model.a, model.whole];
  if (model.mode === "count") return [model.a];
  if (model.mode === "sub" && model.blank === "minuend") return [model.b];
  if (model.mode === "sub" && model.blank === "subtrahend") return [model.a];
  return [model.a, model.b];
}

/** MA-652 RED round 1 — `promptOperandSource`, defined on the FINAL SERVED
 *  PROMPT (see `MappedEngineItem.promptOperandSource`'s doc for the full
 *  rationale). Takes the exact string the child reads and the scaffold
 *  model (or null) the mapper already built — never re-derives either. */
function computePromptOperandSource(
  prompt: string,
  model: ScaffoldModel | null,
): "prompt" | "stimulus" | "none" {
  if (model) {
    const operands = modelOperandsForContainment(model);
    return operands.every((n) => numberAppearsInPrompt(prompt, n)) ? "prompt" : "stimulus";
  }
  return /\d/.test(prompt) ? "prompt" : "none";
}

/** MA-754 — choice identity for de-duplication (displayed form; see caller). */
function displayedChoiceKey(v: string): string {
  const t = v.normalize("NFC").trim().replace(/\s+/g, " ");
  return /^-?\d+(\.\d+)?$/.test(t) ? `#${Number(t)}` : `s:${t}`;
}

/** MA-754 — drop distractors equal to the keyed answer or to an earlier distractor. */
export function dedupeDistractors(answer: string, distractors: string[]): string[] {
  const seen = new Set<string>([displayedChoiceKey(answer)]);
  const out: string[] = [];
  for (const d of distractors) {
    const k = displayedChoiceKey(d);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(d);
  }
  return out;
}

/** MA-840 slice 2a — every caller (the dispatcher, the audits, the
 *  no-hint ledger guard) gets the family spoken cue (mathHintCue.ts). */
export function mathExemplarToEngineItem(item: MathExemplar, microSkillId: string): MappedEngineItem {
  return withMathHintCue(mathExemplarToEngineItemUncued(item, microSkillId), microSkillId);
}

/** MA-840 slice 2a — exported for the cue tests only (the pre-cue mapping). */
export function mathExemplarToEngineItemUncued(
  item: MathExemplar,
  microSkillId: string,
): MappedEngineItem {
  const answer = String(item.expectedAnswer);
  // MA-754 — defence in depth: a distractor that shows the same as the keyed
  // answer (or as an earlier distractor) is dropped, so a mis-authored
  // `nearWrongAnswers` can never serve a duplicate choice (MA-737 RED N4:
  // ["8","10","8","9"]). Identity = what the child sees: trimmed, whitespace-
  // collapsed, numeric strings by value (same key as servedChoicesDistinctGuard).
  // A no-op on today's corpus (proved by the MA-754 test: 1662 items identical).
  const distractors = dedupeDistractors(answer, (item.nearWrongAnswers ?? []).map(String));
  const options = shuffle([answer, ...distractors]);
  // Prompt-driven scaffold model. When it can't be parsed (or the parse
  // doesn't reproduce the answer), emit no scaffold rather than draw the
  // wrong quantities — the item still works as a multiple-choice.
  const model = parseMathItem(item);
  // MA-656 round 3 (MF3), CORRECTED round 4 (MF2/MF3, RED round 2): the 6
  // subtraction fact-family ids draw the number_bond mathVisual as their
  // DEFAULT picture (see buildMathVisual above). The ladder here is
  // ["T10","NB"] — BOTH rungs, at the MAPPER layer. NB is dropped from the
  // ON-SCREEN ladder only at the surface-aware layer
  // (`filterHintLadderForInlineVisual` in HintSlot.tsx), which drops it
  // ONLY where the bond picture is actually visible (concrete/new band on
  // MathSession) — never here, where the mapper cannot see visibility or
  // surface. Round 3 dropped NB at THIS layer instead, which RED found
  // makes the bond unreachable on `/play/mixed` (these items render no
  // picture there — `visualRequired:false`) and past the concrete band
  // (the picture fades, per the standard mastery-visibility rule) — the
  // part-whole bond, `ms.math.06`'s own named skill, had NO representation
  // left on two of the three surfaces these items are served on. Moving
  // the drop downstream to the surface-aware mechanism fixes this without
  // giving up the restatement-avoidance goal that motivated dropping it in
  // the first place.
  //
  // CORRECTION (round 4, RED round 2 MF2): round 3's comment here claimed
  // ScaffoldNL was excluded because "wiring this ladder" surfaced a
  // reachable leak — false. `supportsForModel`'s own missing_addend arm
  // can only ever return `["NB"]` or `["NLMA","NB"]`; NL is not one of its
  // possible outputs for ANY input, so NL was never reachable here before
  // or after this routing, and this carve-out never changed that. What IS
  // true, verified by SSR: `ScaffoldNL` fed missing_addend data WOULD draw
  // the missing part as countable hop arcs — a real, worth-filing latent
  // component hazard (noted for whoever picks up MA-660/the scaffold
  // accessibility pass), just not one this routing change ever avoided.
  // "T10" is a deliberate, DELIBERATELY NEW ladder rung on its own merits
  // — not a filtered survivor of the generic add/sub ladder (this mode's
  // only other possible rung, NLMA, is likewise excluded — same
  // reasoning as NL, not previously reachable, not filtered out here). It
  // is answer-safe by the same MA-595(a) "complete the frame" contract as
  // the bond's own "?": ScaffoldT10's missing_addend branch pre-fills only
  // the known part and leaves the rest open. Disclosed cost (RED nit N2):
  // ScaffoldT10 renders NO aria-label at all, so this hint is silent for a
  // screen-reader child — pre-existing, out of this PR's scope, flagged
  // for the scaffold-accessibility pass. Disclosed cost (RED MF3): T10's
  // open-cell COUNT is the answer at every one of the six wholes (e.g.
  // 6/4→2 open) — this is a hint-REVEAL cost, the same one every H1 tap on
  // a take-away item already accepts (the child asked to see more), not a
  // DEFAULT-picture leak (the bond, not T10, is what's on screen by
  // default; T10 requires an explicit "Show hint" tap).
  const isBondRoutedSubtraction =
    model?.mode === "missing_addend" &&
    /^\d+ is one part of \d+\. What is the other part\?$/.test(String(item.prompt));
  // MA-706 (hint-rung slice) — the only rung a `skip_count_two_step` item
  // can carry (no scaffold model ever parses from a skip-count prompt); see
  // skipCountTwoStepHint's own header. `null` for every other item.
  const skipTwoStepHint = model ? null : skipCountTwoStepHint(item);
  const supports: SupportCode[] = isBondRoutedSubtraction
    ? ["T10", "NB"]
    : model
      ? supportsForModel(model, {
          visualSupport: typeof item.visualSupport === "string" ? item.visualSupport : undefined,
          hasSourceStimulus: item.sourceStimulus_v2 != null,
        })
      : (skipTwoStepHint?.supports ?? []);
  // MA-94: hoisted so visualRequired can key off what is actually emitted.
  const mathVisual = NO_VISUAL_ITEM_TYPES.has(item.itemType ?? "")
    ? undefined
    : buildMathVisual(item, model, options);
  // MA-94: a visual built from STIMULUS-sourced operands means the prompt
  // alone carries no operands — the picture IS the question, so it must show
  // by default (visualRequired), exactly like count/coins/clock. Covers the
  // reader models (source === "stimulus") and the base-ten read branch (the
  // place_value_blocks kind is emitted for visualSupport "base_ten_blocks"
  // only from the stimulus). Without this the fixed items would STILL serve
  // blank for a fresh learner (hidden-by-default) — the visualRequired:false
  // blind spot the 2026-07-01 sweep named.
  //
  // MA-116: a stimulus-sourced missing_addend model (the ~196 MA-114
  // part-whole items) has NO mathVisual — buildMathVisual can't draw a
  // faithful a+b picture because known+missing (the whole) !== the answer
  // (the missing part) — but IS always paired with a visualScaffold (the
  // answer-safe NB "missing_addend" kind, set unconditionally below whenever
  // `model` is non-null). The prompt alone is unanswerable there too, so the
  // scaffold must be visualRequired the same way a mathVisual would be. Gated
  // narrowly to `mode === "missing_addend"` (not just "stimulus-sourced") so
  // this does NOT also fire for the ~207 stimulusOperandsFromDetails
  // add/sub/narrative items (ms.math.07-11/15), which are intentionally
  // mathVisual-less word problems with no scaffold to show.
  const stimulusSourcedVisual =
    (mathVisual != null &&
      // MA-704 — `column_add` is emitted only beside a served prompt that
      // states both addends (rule `columnAddWhereCountersRefused` checks the
      // served equation), so even when its operands were read from the
      // stimulus (ME_14_*, "Add the two numbers." rewritten by
      // twoDigitAddStem) the picture is a support, not the question: it
      // fades on the normal curve and the item stays Mixed-servable.
      ((model?.source === "stimulus" && mathVisual.kind !== "column_add") ||
        (mathVisual.kind === "place_value_blocks" &&
          (typeof item.visualSupport === "string" ? item.visualSupport : "") === "base_ten_blocks") ||
        (mathVisual.kind === "numeral_card" &&
          (typeof item.visualSupport === "string" ? item.visualSupport : "") === "numeral_card") ||
        // MA-688 — the spoken-target ten frame is shown by default: it is the
        // non-glyph channel for a child whose auto-read is off.
        (mathVisual.kind === "ten_frame" &&
          (typeof item.visualSupport === "string" ? item.visualSupport : "") === "spoken_numeral_target") ||
        // MA-654 — mirrors the base_ten_blocks clause immediately above: the
        // two_ten_frames_teen read branch has no `model` (no operation, no
        // missing-addend signature), so it must be threaded into
        // visualRequired here the same way, or these items would still
        // serve blank for a fresh learner (the visualRequired:false blind
        // spot MA-94's header comment names).
        (mathVisual.kind === "two_ten_frames_teen" &&
          (typeof item.visualSupport === "string" ? item.visualSupport : "") === "two_ten_frames") ||
        // MA-665 (round 2, R2-1) — mirrors the two clauses immediately above:
        // once the ms.math.12 prompt states the number sentence itself
        // ("5 + ___ = 12"), `parseMathItem`'s prompt-regex branch matches
        // before the stimulus fallback ever runs, so `model.source` is no
        // longer "stimulus" for these 12 items even though the picture IS
        // still the answer-safe two-ten-frames known-addend+total render
        // (MA-C10) that must stay on screen by default. Keyed on the emitted
        // mathVisual kind + the authored visualSupport, exactly like the two
        // clauses above, not on model.source, so the picture's default
        // visibility does not depend on which code path happened to parse
        // the operands.
        (mathVisual.kind === "two_ten_frames_missing_addend" &&
          (typeof item.visualSupport === "string" ? item.visualSupport : "") === "two_ten_frames"))) ||
    (mathVisual == null && model?.source === "stimulus" && model.mode === "missing_addend");
  // MA-C15 (ADR-067): the multi-bar fraction_comparison visual is answer-safe
  // (N un-highlighted bars, none singled out) — unlike the old single-bar
  // comparison bug, showing it does NOT reveal the answer, so it may be
  // shown by default. Gated on >=2 bars so a degenerate/unparsable payload
  // (never emitted — see buildMathVisual) can't force a broken visual to
  // show. MA-709 (half b): answer-safe no longer implies REQUIRED — that
  // decision is `fractionComparisonPictureRequired` (below, in the
  // `visualRequired` disjunction), which keeps the picture required only
  // when the compared fractions are not printed on the prompt/tiles.
  // MA-709 round 1 (RED MF1): the answer-safe role is now carried to the
  // served item as `pictureShownWithoutHistory` (see its field doc).
  const isFractionComparisonVisual =
    mathVisual?.kind === "fraction_comparison" &&
    Array.isArray(mathVisual.fractions) &&
    mathVisual.fractions.length >= 2;
  // MA-652 RED round 1 — hoisted so promptOperandSource (below) reads the
  // EXACT SAME rendered string the `prompt:` field emits — no re-derivation,
  // no second source of truth for what the child actually sees.
  const finalPrompt = narrativePrompt(item) ?? twoDigitAddStem(item, Number(item.expectedAnswer)) ?? item.prompt;
  // MA-714 — see MappedEngineItem.authoredFactorPair. Only when the served
  // item has no picture pair to corroborate against; the visual pair, when
  // present, stays the classifier's only source (no behaviour change for
  // pictured items).
  const authoredFactorPair =
    FACTOR_PAIR_ITEM_TYPES.has(item.itemType ?? "") &&
    !(typeof mathVisual?.a === "number" && typeof mathVisual?.b === "number")
      ? deriveWordProblemFactors(item)
      : null;
  // MA-1001 — the spoken-target arm marker (see MappedEngineItem.spokenNumeralTarget).
  const spokenNumeralTargetArm =
    (typeof item.visualSupport === "string" ? item.visualSupport : "") === "spoken_numeral_target" &&
    mathVisual?.kind === "ten_frame";
  return {
    id: item.id,
    type: "multiple_choice",
    prompt: finalPrompt,
    answer,
    options,
    microSkillId,
    ageBand: item.ageBand ?? "5.5-6.0",
    difficulty: item.difficulty ?? 1,
    supports,
    explanation: item.whyThisSurfacesMisconception ?? "",
    // Scaffolds read data.{mode,a,b,op,whole,missing} to draw the right
    // quantities (#61). `kind` carries the mode for debugging.
    visualScaffold: model
      ? { kind: model.mode, data: scaffoldDataForModel(model) }
      : (skipTwoStepHint?.visualScaffold ?? undefined),
    // RC-VISUAL: a renderable manipulative for EVERY visual item, from the
    // authored `visualSupport` type + operands (parsed model OR top-level a/b).
    mathVisual,
    // MA-1001 — additive, key absent off the arm (never `false`).
    ...(spokenNumeralTargetArm ? { spokenNumeralTarget: true as const } : {}),
    // VISUALS-AS-HINTS: force the picture for items where it IS the question.
    // Beyond the count/coin/clock types, a fraction item is visual-required
    // when the kid must READ the bar ("What fraction of the bar is shaded?",
    // ms.math.22) — there the bar is the prompt and is unanswerable without
    // it — OR (MA-C15, narrowed by MA-709) when it is a comparison/selection
    // item whose safe multi-bar visual rendered AND the compared fractions
    // are not printed on the prompt/tiles.
    visualRequired:
      VISUAL_REQUIRED_ITEM_TYPES.has(item.itemType ?? "") ||
      isReadTheBarFraction(item) ||
      // MA-709 (half b) — `isFractionComparisonVisual` keeps its first job
      // (the multi-bar picture is answer-SAFE: no bar singled out, so it
      // may be shown at all) but no longer does the second ("the picture
      // is REQUIRED") unconditionally: a comparison whose compared
      // fractions are all printed on the served prompt/tiles is answerable
      // from the symbols, so its bars are a support that fades on the
      // normal curve (full/faded/hidden, hint restores them) and the item
      // is Mixed-servable. Rule on the served item, never a skill list —
      // see fractionComparisonPictureRequired.
      fractionComparisonPictureRequired(mathVisual, finalPrompt, options) ||
      stimulusSourcedVisual,
    // MA-709 round 1 (RED MF1) — full at no-history for the answer-safe
    // comparison picture; absent (not false) everywhere else.
    // MA-742 — the same no-history rule for MA-703's answer-safe blank-slot
    // subtraction picture: a `sub` model carrying `blank`, emitted as
    // `take_away_blank` (the blank operand is never drawn or printed). Keyed
    // on the model + emitted kind, never on an id list. Deliberately NOT
    // `visualRequired` (that would hold the picture at full opacity at every
    // band): with this flag a first-timer sees it in full, it fades at
    // emerging and hides at secure/fluent (SHOW rung restores it).
    // MA-699 — the same rule for the answer-safe number bond of a
    // picture-less unknown-addend item ("1 + ___ = 5", "___ + 3 = 12"): a
    // `missing_addend` model with a non-degenerate bond (0 < known < whole),
    // no mathVisual, not already required (`stimulusSourcedVisual` covers
    // the stimulus-sourced bonds). Keyed on the model, not on which code
    // path parsed the operands and not on an id or skill list. Deliberately
    // NOT `visualRequired` (operator ruling D-1001-8): a required picture
    // never fades, and the prompt states both givens so the item stays
    // Mixed-servable. `mathSessionInlineVisual` mounts the bond on this flag.
    // `model.a > 0` is defensive and cannot fire today: a known-0 bond
    // ("0 + ___ = 5") always gets a number_line mathVisual, so
    // `mathVisual == null` already excludes it (MA-699 RED round 1, NIT-1).
    pictureShownWithoutHistory:
      isFractionComparisonVisual ||
      (model?.mode === "sub" && model.blank != null && mathVisual?.kind === "take_away_blank") ||
      (model?.mode === "missing_addend" &&
        mathVisual == null &&
        !stimulusSourcedVisual &&
        model.a > 0 &&
        model.a < model.whole)
        ? true
        : undefined,
    displayMode: "tap_choice",
    // MA-710 — money tiles carry their unit ("11¢"); display only, keyed on
    // the item type (every count_coins value is cents), absent otherwise.
    ...((item.itemType ?? "") === "count_coins" ? { optionUnit: COIN_OPTION_UNIT } : {}),
    launchStatus: item.launchStatus ?? "launch_safe",
    // MA-303: forward the authored transfer-probe tag so the session
    // screen can tag the resulting attempt via transferTagsForItem.
    isTransferProbe: item.isTransferProbe === true || undefined,
    transferTaskId: item.transferTaskId,
    // MA-381 (mapper-threading slice): forward authored diagnostic metadata
    // unmodified. No reader yet — the classifier-core slice consumes these.
    misconceptionCategory: item.misconceptionCategory,
    diagnosticMeaningByWrongAnswer: item.diagnosticMeaningByWrongAnswer,
    ...(authoredFactorPair ? { authoredFactorPair } : {}),
    // ⭐MA-631 §3a — narrowed to EXACTLY this rule, and nothing else: a
    // narrative-stimulus item is genuinely a word problem ("Sam has 4
    // apples..."), and evidenceItemTypeFor's caller decides whether to use it.
    // Measured: 38 of 2,462 items across both math packs, all ms.math.15's,
    // all in mathExemplars — narrative is unique to this skill.
    evidenceItemType: item.sourceStimulus_v2?.type === "narrative" ? "wordProblem" : undefined,
    // MA-652: see MappedEngineItem.operandModelSource's doc for the full
    // derivation. Read off the SAME `model` this function already built
    // above — no re-parse, no second source of truth. RED round 1: this is
    // CODE-PATH provenance, kept verbatim under its new name.
    operandModelSource: model == null ? "none" : model.source === "stimulus" ? "stimulus" : "prompt",
    // MA-652 RED round 1 — see MappedEngineItem.promptOperandSource's doc.
    // Computed from the SAME `finalPrompt` the `prompt:` field above emits
    // and the SAME `model` — no re-parse, no second source of truth.
    promptOperandSource: computePromptOperandSource(finalPrompt, model),
  };
}

/** MA-303 — derive the `isTransferTask`/`transferTaskId` fields a session
 *  screen spreads into its `buildAttempt` event for a served item. A
 *  non-probe item (the overwhelming majority) yields `{isTransferTask:
 *  false, transferTaskId: null}` — buildAttempt's own defaults, made
 *  explicit here so every screen that adopts this helper serves the SAME
 *  contract instead of re-deriving it. See transferGating.ts's
 *  `evaluateTransferEvidence`, which can only ever observe a passed
 *  transfer requirement for attempts tagged this way. */
export function transferTagsForItem(
  item: MappedEngineItem,
): { isTransferTask: boolean; transferTaskId: string | null } {
  return {
    isTransferTask: item.isTransferProbe === true,
    transferTaskId: item.transferTaskId ?? null,
  };
}

/** MA-690 (slice ms-math-05-family-pictures) — authored part-whole stimulus
 *  family (`visualSupport` === `sourceStimulus_v2.type`) → the answer-safe
 *  picture kind buildMathVisual emits for it. `number_bond` is deliberately
 *  absent: the NB bond already is that family's picture. */
export const PART_WHOLE_FAMILY_KINDS: Readonly<Record<string, "split_ten_frame" | "set_picture" | "part_strip">> = {
  split_ten_frame: "split_ten_frame",
  two_set_visual: "set_picture",
  missing_addend_strip: "part_strip",
};

/** MA-690 — the largest whole a part-whole family picture draws (a ten
 *  frame's capacity; ms.math.05/06 wholes are 3-10), defined once beside the
 *  renderers. A larger whole gets no picture (fail closed), never a clipped
 *  one. */
export { PART_WHOLE_PICTURE_MAX_WHOLE };

/** MA-690 — is this record's stimulus a part-whole decomposition authored as
 *  `family`? The stimulus type must equal the visualSupport token and its
 *  details must carry the part-whole fields (knownPart, totalTarget). */
function isPartWholeStimulusOf(item: MathExemplar, family: string): boolean {
  const s = item.sourceStimulus_v2;
  if (!s || s.type !== family) return false;
  const det = s.details as Record<string, unknown> | undefined;
  return det != null && typeof det === "object" && "knownPart" in det && "totalTarget" in det;
}

const FRAME_VISUALS = new Set([
  "ten_frame","five_frame","split_ten_frame","two_ten_frames","double_ten_frame","two_set_visual",
]);
const LINE_VISUALS = new Set(["number_line"]);

// MA-704/MA-707 — the generic "groups" fallback (below, buildMathVisual's
// final return) has no magnitude ceiling of its own: unlike ten_frame/
// number_line (capped at 10 cells via frameFits/lineFits above), it draws
// a+b loose dots for ANY total, up to 146 on the real corpus (ms.math.26,
// ME_M26_018, 67+79). A counted-all pile above "two ten-frames' worth" is
// not a usable model — refuse the picture rather than draw it. STOPGAP: the
// honest models this really wants (a two-digit place-value addition kind;
// a number-bond with the withheld whole) are NOT built here and stay owed
// as separate, larger slices (see MA-704/MA-707's own BACKLOG text) — this
// constant only stops the unusable dot-pile UX, it does not add a model.
export const MAX_LOOSE_COUNTED_OBJECTS = 20;

/** MA-704 — rule `columnAddWhereCountersRefused`: which plain addition
 *  records (itemType `equation` / `directPractice`) draw the MA-707
 *  `column_add` base-ten picture instead of loose counters. Operator ruling
 *  (closure40, option A): `column_add` is the model exactly where loose
 *  counters are refused —
 *    (i)  both authored addends are 10–99 (a two-digit sum, never countable
 *         as a loose pile), or
 *    (ii) one addend is 1–9 and the other 10–99 AND their sum exceeds
 *         MAX_LOOSE_COUNTED_OBJECTS (below or at it — e.g. the teen fact
 *         10 + 3 — the counters picture and its count_on ladder stay).
 *  The routing may depend on a + b; the picture does not (ColumnAddRenderer
 *  reads only a and b, and its whole-picture identity is pinned).
 *
 *  The addends are the RECORD'S OWN authored fields — top-level `a`/`b`,
 *  else the `twoDigitAddendPair` stimulus pair; when both are authored they
 *  must agree. Fail-closed (returns null, the record falls through to
 *  today's branches): non-integer or out-of-range addends, a sum that is not
 *  the authored answer, or a prompt-parsed model that is not the same
 *  addition. Returns [a, b] in authored order. */
function columnAddWhereCountersRefused(
  item: MathExemplar,
  model: ReturnType<typeof parseMathItem>,
): [number, number] | null {
  const itemType = item.itemType ?? "";
  if (itemType !== "equation" && itemType !== "directPractice") return null;
  const top = item as { a?: unknown; b?: unknown };
  const stim = twoDigitAddendPair(item);
  let pair: [number, number] | null = null;
  if (top.a !== undefined || top.b !== undefined) {
    pair = [Number(top.a), Number(top.b)];
    if (stim && (stim[0] !== pair[0] || stim[1] !== pair[1])) return null;
  } else {
    pair = stim;
  }
  if (!pair) return null;
  const [A, B] = pair;
  if (!Number.isInteger(A) || !Number.isInteger(B)) return null;
  if (A + B !== Number(item.expectedAnswer)) return null;
  if (!model || model.mode !== "add" || model.a !== A || model.b !== B) return null;
  // The SERVED prompt (the same expression the item's `prompt` field emits)
  // must be the bare equation of exactly these addends, so the picture is a
  // support beside a self-sufficient prompt — never the question itself
  // (which is why `column_add` stays out of `stimulusSourcedVisual` below).
  const served = narrativePrompt(item) ?? twoDigitAddStem(item, Number(item.expectedAnswer)) ?? item.prompt;
  if (String(served).trim() !== `${A} + ${B} = ?`) return null;
  const twoDigit = (n: number) => n >= 10 && n <= 99;
  const oneDigit = (n: number) => n >= 1 && n <= 9;
  if (twoDigit(A) && twoDigit(B)) return pair;
  const mixed = (twoDigit(A) && oneDigit(B)) || (oneDigit(A) && twoDigit(B));
  if (mixed && A + B > MAX_LOOSE_COUNTED_OBJECTS) return pair;
  return null;
}

// MA-C2 (ADR-015): a COUNTING manipulative draws the NAMED object, not a
// generic circle -- a pre-reader asked to "Count the stars" should SEE stars
// (Principle 5: non-reader-legible). The mapper detects the prompt noun and
// threads a SEMANTIC glyph token ("star") into the mathVisual payload; the
// renderer owns the SVG shape (one node per glyph, same anchor + fill token as
// the circle it replaces, so the count-integrity guard stays valid). Detection
// is bound to the count_objects path ONLY and falls back to circles for any
// unrecognized noun -- so "dots"/subitize/touch items are untouched and a glyph
// can never fire on an add/sub/array/word item (none reach the count branch).
//
// MA-690 — the SAME noun table also reads a foundation counting set's authored
// `countableSet.objectKind` (foundationMathVisual, cardinality_count). A noun
// gets a glyph ONLY when it is a simple shape the renderer already draws in its
// own style: "block" is the filled rounded square shapeGlyphs.tsx already
// draws for `square`. Every other noun (bears, apples, fish, ...) stays the
// fail-closed generic circle -- never the wrong object, never invented art.
// "dots"/"counters" ARE circles, so they need no entry.
// MA-690 (slice count-glyph-art) — the renderer now draws seven more nouns
// (rendererRegistry.tsx `COUNT_GLYPH_RINGS`): bear, apple, leaf, shell, fish,
// button, duck. "stones" stays deliberately uncovered (no recognisable stone
// silhouette in one flat fill), so it keeps the generic circle.
const COUNT_NOUN_GLYPHS: Record<string, string> = {
  star: "star", stars: "star",
  block: "block", blocks: "block",
  bear: "bear", bears: "bear",
  apple: "apple", apples: "apple",
  leaf: "leaf", leaves: "leaf",
  shell: "shell", shells: "shell",
  fish: "fish", fishes: "fish",
  button: "button", buttons: "button",
  duck: "duck", ducks: "duck",
};
/** MA-690 — own-key lookup (a noun like "constructor" must never resolve to
 *  an Object.prototype member and leak a non-glyph value into the payload). */
function glyphForNoun(noun: unknown): string | undefined {
  const k = typeof noun === "string" ? noun.trim().toLowerCase() : "";
  return k && Object.prototype.hasOwnProperty.call(COUNT_NOUN_GLYPHS, k) ? COUNT_NOUN_GLYPHS[k] : undefined;
}
function detectCountGlyph(prompt: string): string | undefined {
  const m = String(prompt ?? "").toLowerCase().match(/count the ([a-z]+)/);
  return m ? glyphForNoun(m[1]) : undefined;
}

/** MA-690 (RED SHOULD-3) — the prompt must NAME the glyph's noun (singular or
 *  plural, whole word) before a glyph is drawn, so an authored stimulus noun
 *  that has drifted from the prompt ("How many blocks…" over objectKind
 *  "stars") can never draw an object the prompt does not ask about. */
const GLYPH_PROMPT_NOUN: Record<string, RegExp> = {
  star: /\bstars?\b/i,
  block: /\bblocks?\b/i,
  bear: /\bbears?\b/i,
  apple: /\bapples?\b/i,
  leaf: /\b(?:leaf|leaves)\b/i,
  shell: /\bshells?\b/i,
  fish: /\bfish(?:es)?\b/i,
  button: /\bbuttons?\b/i,
  duck: /\bducks?\b/i,
};
function glyphNamedByPrompt(glyph: string | undefined, prompt: unknown): string | undefined {
  if (!glyph || !Object.prototype.hasOwnProperty.call(GLYPH_PROMPT_NOUN, glyph)) return undefined;
  return GLYPH_PROMPT_NOUN[glyph].test(String(prompt ?? "")) ? glyph : undefined;
}

/** MA-690 (batch-8 extension) — the noun of a "How many <noun> in all?"
 *  prompt, for a two-group join picture whose stimulus carries no noun field
 *  (ms.math.07 `counter_set`, "How many stars in all?"). */
function detectInAllGlyph(prompt: string): string | undefined {
  const m = /\bhow many ([a-z]+) in all\b/i.exec(String(prompt ?? ""));
  return m ? glyphNamedByPrompt(glyphForNoun(m[1]), prompt) : undefined;
}

/** MA-690 — the dot_pattern arrangement FAMILY for an `itemType:
 *  "subitize_to5"` record whose `visualSupport` is not a subitize card (the
 *  28 ms.math.01 records carry prose boilerplate there, no authored layout).
 *  Round-robin over the record's trailing id number (ME0025 -> 25,
 *  MEG_M01_001 -> 1), so each count is served as three visibly different
 *  cards instead of one. An id with no trailing number gets "dice". Layout
 *  only: DotPatternRenderer draws exactly `total` dots in every family. */
const SUBITIZE_ROUND_ROBIN = ["dice", "frame", "generic"] as const;
function subitizeArrangementForId(id: string): (typeof SUBITIZE_ROUND_ROBIN)[number] {
  const m = /(\d+)$/.exec(String(id ?? ""));
  return m ? SUBITIZE_ROUND_ROBIN[Number(m[1]) % 3] : "dice";
}

/** MA-692 twin-picture-variety (2026-10-07) — the ten-frame fill arrangement
 *  for a ms.math.00d spoken-numeral picture ("Tap the number six." over a
 *  frame of six). Before this, every record of a numeral drew the identical
 *  standard frame, so 10-11 records per numeral were one served form. Rule:
 *  round robin over the record's trailing id number (as MA-690's
 *  subitizeArrangementForId) — id mod 3: 0 the standard fill, 1 "pairs"
 *  (TEN_FRAME_FILL_ORDER), 2 the vertical frame (TEN_FRAME_VERTICAL, part d,
 *  2026-10-17; part c alternated standard / pairs by parity). All three keep the frame's 5-and-10 structure: a
 *  scattered fill was dropped by RED round 1 MUST-1 (2026-10-07) because it
 *  breaks the at-a-glance quantity read the frame is there to give. The pairs
 *  family is kept ONLY when it fills a different set of cells than the standard fill
 *  for this count (the vertical frame always differs) (a full frame of 10, a pairs frame of 1 or 9, and the empty
 *  frame of 0 are the standard picture), so a different payload always draws a
 *  different picture. Layout only: the frame draws exactly `n` counters. */
const NUMERAL_FRAME_ROUND_ROBIN = [undefined, "pairs", TEN_FRAME_VERTICAL] as const;
export function numeralFrameArrangement(id: unknown, n: number): "pairs" | "vertical" | undefined {
  const m = /(\d+)$/.exec(String(id ?? ""));
  if (!m || !Number.isInteger(n) || n < 1 || n > 10) return undefined;
  const family = NUMERAL_FRAME_ROUND_ROBIN[Number(m[1]) % NUMERAL_FRAME_ROUND_ROBIN.length];
  if (!family) return undefined;
  // MA-692 d (2026-10-17) — the vertical frame (2 columns x 5 rows, filled
  // column-first) is a different drawn picture for every count 1..10, so it
  // is always kept. id number mod 3: standard / pairs / vertical.
  if (family === TEN_FRAME_VERTICAL) return family;
  const cells = tenFrameFilledCells(n, family);
  return cells.every((c, i) => c === i) ? undefined : family;
}

/** MA-692 twin-picture-variety — a ms.math.00d numeral-recognition foundation
 *  record ("Tap the number six.", FI_NUM_00b: a cardinality_count stimulus)
 *  draws its single-colour quantity frame in the record's fill arrangement, as
 *  the mathExemplars spoken-numeral records do (numeralFrameArrangement).
 *  Applied to foundationMathVisual's result, so the counting-sets branch
 *  itself (coachShapePin's pinned text) is unchanged; counting skills keep the
 *  standard fill. It gates on the foundation stimulus `representation`
 *  (cardinality_count) because foundation records author no quantityPicture;
 *  the mathExemplars spoken-numeral branch gates on the authored
 *  quantityPicture instead (RED round 1 N-2). Both fail closed. */
function withNumeralFrameArrangement<T extends { mathVisual?: MappedEngineItem["mathVisual"] }>(item: FoundationItem, math: T): T {
  const mv = math.mathVisual;
  const rep = (item.sourceStimulus_v2 as { representation?: unknown } | undefined)?.representation;
  if (
    item.targetSkill !== "ms.math.00d" || rep !== "cardinality_count" || !mv || mv.kind !== "ten_frame" ||
    (mv.b ?? 0) !== 0 || mv.glyph !== undefined || mv.arrangement !== undefined
  ) return math;
  const arrangement = numeralFrameArrangement(item.id, Number(mv.total));
  return arrangement ? { ...math, mathVisual: { ...mv, arrangement } } : math;
}

/** Derive the renderable manipulative. `model` (from parseMathItem) wins for
 *  a/b/op; otherwise fall back to the item's top-level a/b; counts default to
 *  the answer. Addition items missing both operands get a sensible split so the
 *  visual stays answerable (a follow-up can author real a/b). */
function buildMathVisual(
  item: MathExemplar,
  model: ReturnType<typeof parseMathItem>,
  options: string[],
): MappedEngineItem["mathVisual"] | undefined {
  const raw = typeof item.visualSupport === "string" ? item.visualSupport : "";
  if (!raw || raw === "narrative") return undefined; // word problems: text is the prompt
  // MA-656 round 2 (bond-routing): the 12 fact-family items route through
  // parseMathItem's new anchored-template branches above. Handle their
  // mathVisual explicitly here rather than falling into the generic
  // op-inference logic below, which reads the promptStr for a literal
  // operator token — these prompts intentionally carry none (that's the
  // whole point of MA-656's rewrite), so the generic path would either
  // draw nothing or, worse, coincidentally match on unrelated digits.
  // Addition twins (mode "add", matched by this exact prompt template): the
  // answer-safe two-group "groups" picture, unconditionally (not the
  // generic total<=10 ten_frame/groups split), per MA-656 round 2's
  // routing spec.
  //
  // MA-656 round 3 (MF3, RED must-fix): subtraction twins (mode
  // "missing_addend") now draw the answer-safe `number_bond` mathVisual —
  // whole on top, the known part in one lower circle, "?" in the other —
  // instead of suppressing entirely. Round 2 shipped these scaffold-only
  // (NB hint-ladder-only, at the cost of an H1), which is exactly what the
  // BACKLOG entry's own class-1 text asked NOT to ship ("the bond today
  // sits only in the hint ladder at the cost of an H1"). `number_bond` is
  // reused-props-only (NumberBondRenderer.tsx reads ONLY `a`/`total`, never
  // `b` — `b` is carried for contract parity with every other renderer,
  // exactly like ScaffoldNB already carries `whole`/`a`/`b` without ever
  // printing the missing one), NOT added to mathVisualStage.ts's
  // STAGED_KINDS (same reasoning as `ten_frame`/`ten_frame_sub`: the
  // authored bond IS the model, not an early counted-all stand-in to fade
  // away), so it renders unchanged at every band.
  if (model?.mode === "missing_addend" && /^\d+ is one part of \d+\. What is the other part\?$/.test(String(item.prompt))) {
    return { kind: "number_bond", a: model.a, b: model.b, op: "+", total: model.whole };
  }
  if (model?.mode === "add" && /^The parts are \d+ and \d+\. What is the whole\?$/.test(String(item.prompt))) {
    return { kind: "groups", a: model.a, b: model.b, op: "+", total: model.whole };
  }
  // Phase 3 (fractions) — additive mapper branch mirroring the array /
  // place-value paths below. A `fraction` item resolves the `fraction_bar`
  // renderer (registry key): a = numerator (shaded parts), b = denominator
  // (total equal parts), authored as top-level a/b on the item. The renderer
  // reads visualData {a,b} (the same a/b channel ArrayRenderer uses) and
  // draws a bar split into `b` parts with `a` shaded. This branch runs BEFORE
  // the `total = Number(expectedAnswer)` guard below because a `fraction`
  // item's expectedAnswer is now a fraction STRING ("1/2") — Number("1/2") is
  // NaN, which would otherwise drop the bar. The renderer ignores `total`, so
  // it is derived from the numerator purely for mathVisual contract parity.
  if ((item.itemType ?? "") === "fraction") {
    // MA-C15 (ADR-067): the comparison/selection fractions ("Tap the biggest
    // share"; "Tap the biggest/equal fraction") used to get NO picture
    // (ADR-021) because the single-bar renderer could only draw the ANSWER
    // fraction. Now they get a MULTI-bar comparison — one un-highlighted bar
    // per already-shuffled answer option (the same `options` array the
    // multiple_choice buttons render from, so the answer position is random),
    // all bars identical width/scale/styling, none singled out. Every option
    // must parse as a clean n/d fraction string or this returns undefined
    // (never a partial/garbled bar set).
    if (!isReadTheBarFraction(item)) {
      const parsed = options.map((opt) => {
        const m = /^(\d+)\/(\d+)$/.exec(String(opt).trim());
        if (!m) return null;
        const numerator = Number(m[1]);
        const denominator = Number(m[2]);
        if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator < 1) return null;
        return { a: numerator, b: denominator };
      });
      if (parsed.length >= 2 && parsed.every((p): p is { a: number; b: number } => p !== null)) {
        return {
          kind: "fraction_comparison",
          fractions: parsed,
          total: parsed.length,
        };
      }
      return undefined;
    }
    const fracRaw = typeof item.visualSupport === "string" ? item.visualSupport : "";
    if (fracRaw && fracRaw !== "narrative") {
      const numerator = Number((item as { a?: number }).a);
      const denominator = Number((item as { b?: number }).b);
      if (Number.isFinite(numerator) && Number.isFinite(denominator)) {
        return { kind: "fraction_bar", a: numerator, b: denominator, op: "+", total: numerator };
      }
    }
    return undefined;
  }
  // Wave 2-A (money/time) concept visuals. coins: total=cents (numeric). clock:
  // a=hours,b=minutes (top-level), total=mins-since-midnight; emitted before the
  // numeric guard since a tell_time answer is a STRING.
  if ((item.itemType ?? "") === "count_coins") {
    const cents = Number(item.expectedAnswer);
    if (Number.isFinite(cents)) {
      // MA-C16: draw the LITERAL coins named in the prompt when they're
      // present and consistent with the answer; else fall back to the
      // legacy greedy split of `cents` (unnamed-set items, unchanged).
      // MA-710: the authored multiset first (the prompt no longer names
      // the coins), then the legacy prompt parse.
      const coinCounts =
        authoredCoinCounts(item.coinCounts, cents) ??
        parseLiteralCoinCounts(String(item.prompt ?? ""), cents);
      return coinCounts ? { kind: "coins", total: cents, coinCounts } : { kind: "coins", total: cents };
    }
  }
  if ((item.itemType ?? "") === "tell_time") {
    const hh = Number((item as { a?: number }).a);
    const mm = Number((item as { b?: number }).b);
    if (Number.isFinite(hh) && Number.isFinite(mm)) return { kind: "clock", a: hh, b: mm, total: hh * 60 + mm };
  }
  // MA-C17 (ADR-022 deferred item) — elapsed-time ("start → end / how long?")
  // items: draw BOTH clock faces from the authored structured stimulus (the
  // prompt alone carries no times). Mirrors the tell_time/count_coins
  // pattern above: the mapper emits whenever the authored hours/minutes are
  // well-formed; it does NOT itself require the elapsed minutes to
  // reconstruct expectedAnswer (or even be positive) — that faithfulness
  // check is the guard's job (moneyTimePlaceValueVisualIntegrityTest.mjs),
  // exactly like the existing clock branch defers its a/b-vs-answer check
  // to the guard rather than swallowing a mismatched item silently.
  if ((item.itemType ?? "") === "elapsed_time") {
    const det = item.sourceStimulus_v2?.details;
    const sh = Number(det?.startHour);
    const sm = Number(det?.startMinute);
    const eh = Number(det?.endHour);
    const em = Number(det?.endMinute);
    const validHM = (h: number, m: number) =>
      Number.isInteger(h) && h >= 0 && h <= 23 && Number.isInteger(m) && m >= 0 && m <= 59;
    if (det && validHM(sh, sm) && validHM(eh, em)) {
      return {
        kind: "elapsed_time_clock",
        times: [{ a: sh, b: sm }, { a: eh, b: em }],
        total: (eh * 60 + em) - (sh * 60 + sm),
      };
    }
    return undefined;
  }
  // MA-704 — two-digit ADDITION as base-ten columns (rule
  // `columnAddWhereCountersRefused`, see its doc comment). Placed ahead of
  // the base_ten_blocks branch and the MA-C22 number_line refusal below,
  // both of which return before the MA-707 `addition_regroup` arm is
  // reached. A record the rule does not accept falls through unchanged.
  {
    const pair = columnAddWhereCountersRefused(item, model);
    if (pair) {
      return { kind: "column_add", a: pair[0], b: pair[1], op: "+", total: pair[0] + pair[1] };
    }
  }
  // MA-94 — base-ten READ items (ms.math.13 "What number is shown?" / "How
  // many tens/ones …?"). The value lives ONLY in sourceStimulus_v2.details
  // {tensRods,onesCubes,number,focus}; these rendered blank. Route to the
  // existing place_value_blocks renderer (draws rods+cubes for `total`, no
  // numeral text — read-the-blocks, same "the visual IS the question" pattern
  // as read-the-clock). Fail-closed: the authored `number` (when present)
  // must equal tens*10+ones, and the answer must be READABLE off the drawing
  // (the whole value, or the focused tens/ones digit) — else no picture.
  if (raw === "base_ten_blocks") {
    const det = item.sourceStimulus_v2?.details;
    const tens = Number(det?.tensRods);
    const ones = Number(det?.onesCubes);
    if (det && Number.isFinite(tens) && Number.isFinite(ones) && tens >= 0 && ones >= 0) {
      const value = tens * 10 + ones;
      const authored = Number(det.number);
      const consistent = det.number === undefined || authored === value;
      const expN = Number(item.expectedAnswer);
      const answerReadable =
        expN === value ||
        (det.focus === "ones" && expN === ones) ||
        (det.focus === "tens" && expN === tens);
      if (consistent && answerReadable && value > 0) {
        return { kind: "place_value_blocks", total: value };
      }
    }
    return undefined;
  }
  // MA-C22 — the number_line-flavored half of the two-digit ADDITION
  // (ms.math.14) items. The generic groups/number_line tail further below
  // would either clamp the line to 10 and mark the landing dot ON the sum
  // (a straight answer reveal — the existing MA-94 §3b RED-1 guard exists for
  // exactly this) or fall back to drawing up to ~90 individual counters for a
  // 2-digit sum (unusable UX). Neither is an acceptable picture, so these
  // render NO manipulative — twoDigitAddStem already put the equation in the
  // prompt, matching the working single-digit siblings' answer-safe surface.
  if (raw === "number_line" && twoDigitAddendPair(item)) {
    return undefined;
  }
  // MA-C19 — numeral card read-the-numeral items (ms.math.00c/00d "Which
  // number is on the card?" / "What number do you see?"). The value lives
  // ONLY in sourceStimulus_v2.details.targetNumeral — the prompt has no
  // digits, so this rendered blank. The numeral IS the question (same
  // "the visual IS the question" class as read-the-blocks/read-the-clock) —
  // showing it is faithful, not an answer leak. Fail-closed: targetNumeral
  // must be a non-negative integer that equals expectedAnswer exactly.
  if (raw === "numeral_card") {
    const det = item.sourceStimulus_v2?.details;
    const n = Number(det?.targetNumeral);
    const exp = Number(item.expectedAnswer);
    if (det && Number.isInteger(n) && n >= 0 && Number.isInteger(exp) && n === exp) {
      return { kind: "numeral_card", a: n, b: 0, total: n };
    }
    return undefined;
  }
  // MA-688 — spoken-target numeral recognition (ms.math.00d "Tap the number
  // seven."). The numeral card above drew the ANSWER glyph over four digit
  // tiles, so a child who knew no numeral passed by matching shapes. These
  // records name the target as a number WORD (spoken on mount by MA-686's
  // math auto-speak gate) and draw its QUANTITY as a ten frame — never the
  // glyph, so the correct tile is the only "7" on screen. Fail-closed: the
  // authored targetNumeral is an integer 0..10 equal to expectedAnswer, the
  // prompt names exactly that number as a whole word, and the prompt carries
  // no digit at all (a digit would be the glyph again).
  // MA-818 — 0 is admitted: "Tap the number zero." draws an EMPTY ten frame
  // (a = b = total = 0), K.CC.A.3's "0 representing a count of no objects".
  // audit:served-answerability admits exactly that picture for a K.CC.A.3
  // item keyed 0 (isZeroCountFrame); an empty frame anywhere else is still
  // degenerate there.
  if (raw === "spoken_numeral_target") {
    const det = item.sourceStimulus_v2?.details;
    const n = Number(det?.targetNumeral);
    const exp = Number(item.expectedAnswer);
    const prompt = String(item.prompt ?? "");
    const word = NUMBER_WORDS[n];
    if (
      det && Number.isInteger(n) && n >= 0 && n <= 10 && Number.isInteger(exp) && n === exp &&
      word !== undefined && new RegExp(`\\b${word}\\b`, "i").test(prompt) && !/\d/.test(prompt)
    ) {
      // MA-692 — on ms.math.00d, when the authored quantityPicture names the
      // ten frame, the frame is drawn in the record's fill arrangement (any
      // other or missing quantityPicture, and every other skill's spoken
      // numeral, e.g. ms.math.00c, keep the standard frame, fail-closed).
      const arrangement = item.targetSkill === "ms.math.00d" && det.quantityPicture === "ten_frame"
        ? numeralFrameArrangement(item.id, n)
        : undefined;
      return { kind: "ten_frame", a: n, b: 0, op: "+", total: n, ...(arrangement ? { arrangement } : {}) };
    }
    return undefined;
  }
  // MA-C18 — subitize cards (ms.math.01 dot_card/five_frame/picture_set). The
  // count reader (parseMathItem → countFromSubitizeStimulus) produced a
  // stimulus-sourced `count` model; route it to the dot_pattern renderer in the
  // authored arrangement family. Intercept BEFORE the generic tail, which would
  // otherwise emit a ten_frame (raw "five_frame" matches the frame token) or
  // groups — a count-faithful but arrangement-less picture shipped by accident.
  // Fail-closed: no count model (degenerate / inconsistent stimulus) => no picture.
  //
  // MA-690 — the intercept ALSO fires on `itemType: "subitize_to5"` (the 28
  // ms.math.01 records whose visualSupport is the prose "use counters,
  // ten-frame, ..." boilerplate). Keyed on the token alone, they missed this
  // arm and fell to the counting branch below, which drew a countable
  // ten_frame row under "How many dots do you see without counting?". The
  // authored token still picks the family when it is a subitize card;
  // otherwise subitizeArrangementForId round-robins dice/frame/generic.
  if (SUBITIZE_SUPPORTS.has(raw) || (item.itemType ?? "") === "subitize_to5") {
    if (model?.mode === "count" && Number.isFinite(model.whole) && model.whole > 0) {
      const arrangement = raw === "dot_card" ? "dice"
        : raw === "five_frame" ? "frame"
        : raw === "picture_set" ? "generic"
        : subitizeArrangementForId(item.id);
      return { kind: "dot_pattern", a: model.whole, b: 0, total: model.whole, arrangement, displayTimeMs: quickLookMsFor(item) };
    }
    return undefined;
  }
  // MA-C10 (ADR-024 fast-follow) — answer-safe two-ten-frames renderer for
  // missing-addend items (ms.math.12) whose authored visualSupport is
  // "two_ten_frames". Fills exactly `a` (= known addend) cells and shows the
  // total as a numeral label; the child counts forward, never filling to the
  // total. Gated on whole < 20: when total === 20 the 20-cell frame's empty
  // cells (= 20 - known) trivially equal the answer, so those 3 items fall
  // back to the number-bond hint instead. This branch exits BEFORE the
  // drawnAnswer === total gate because this renderer draws only the known
  // addend, not a+b — that gate does not apply here.
  if (model?.mode === "missing_addend" && raw === "two_ten_frames") {
    const known = model.a;
    const whole = model.whole;
    if (Number.isFinite(known) && Number.isFinite(whole) && known > 0 && known < whole && whole < 20) {
      // MA-694 — at secure/fluent mathVisualStage.ts projects these frames to
      // the numeral number bond (the frames' empty cells count the answer).
      // A blank-LEFT sentence ("___ + 7 = 15") carries `arrangement:
      // known_right` so that bond puts "?" on the left, the side of the
      // blank (MA-769 C). The frames renderer ignores `arrangement`.
      return {
        kind: "two_ten_frames_missing_addend", a: known, b: 0, op: "+", total: whole,
        ...(model.blankSide === "left" ? { arrangement: NUMERAL_BOND_KNOWN_RIGHT } : {}),
      };
    }
    return undefined;
  }
  // MA-690 (slice ms-math-05-family-pictures) — the part-whole stimulus
  // families ms.math.05/06 author besides the number bond. Their prompts name
  // the picture ("Look at the split frame", "Check the set picture", "Use the
  // strip picture"), but until this branch the generic tail's ADR-020 gate
  // (correctly) refused them — it draws a+b, the whole — so all three fell
  // back to the bare NB numeral bond and the named picture never appeared
  // (MA-651 batch7 C2, batch11 P1). Each family now gets its own answer-safe
  // kind (PartWholePictures.tsx): the whole as a numeral, the known part as
  // counters, the missing part as one "?" element; `b` is 0, bookkeeping only
  // (the take_away_blank / two_ten_frames_missing_addend precedent), so the
  // missing part never reaches a renderer (no renderer receives it; the
  // whole-independence invariant in ma690FamilyPictures.test.mjs pins that no
  // renderer derives it either).
  //
  // Scope: a PART-WHOLE stimulus only (details carry knownPart/totalTarget and
  // the stimulus type is the visualSupport) — the ms.math.09 `two_set_visual`
  // ADDITION records (details {addends, result}) never enter. Inside, FAIL
  // CLOSED: anything but a stimulus-sourced missing_addend model that matches
  // the authored triple, with 0 < known < whole <= 10 and missing === the
  // answer, returns undefined, so a refused record keeps today's pictureless
  // form. (For a model-less part-whole record the MA-114 backstop below
  // already returns undefined; the branch's own return keeps that outcome
  // local instead of relying on the tail.)
  if (Object.prototype.hasOwnProperty.call(PART_WHOLE_FAMILY_KINDS, raw) && isPartWholeStimulusOf(item, raw)) {
    if (model?.mode !== "missing_addend" || model.source !== "stimulus") return undefined;
    const det = item.sourceStimulus_v2!.details as Record<string, unknown>;
    const known = model.a;
    const whole = model.whole;
    if (
      !Number.isInteger(known) || !Number.isInteger(whole) ||
      !(known > 0 && known < whole && whole <= PART_WHOLE_PICTURE_MAX_WHOLE) ||
      // Defence in depth, unreachable today: partWholeFromStimulus builds the
      // model from these same details and already refuses known + missing !==
      // whole and missing !== the answer, so these three never fire on a
      // served record. Kept so the branch stays fail-closed if the reader or
      // the model's source ever changes.
      known !== Number(det.knownPart) || whole !== Number(det.totalTarget) ||
      known + model.b !== whole || model.b !== Number(item.expectedAnswer)
    ) {
      return undefined;
    }
    return { kind: PART_WHOLE_FAMILY_KINDS[raw], a: known, b: 0, op: "+", total: whole };
  }
  // MA-703 — blank-minuend ("___ − b = c") / blank-subtrahend
  // ("a − ___ = c") subtraction. Intercepted BEFORE the generic op==="-"
  // tail below, whose `subEligible` gate explicitly EXCLUDES any prompt
  // containing "___" (the missing-subtrahend/minuend suppression the MA-C9
  // comment documents) — these 13 items would otherwise fall through to
  // `return undefined` there and serve with no picture at all, the exact
  // defect this item fixes.
  //
  // Answer-safe by construction, per shape:
  //   blank === "subtrahend" (a − ___ = c): draw the `a` known objects,
  //     NONE crossed (the take-away amount is never drawn as a count),
  //     plus one outlined "?" placeholder the renderer adds itself. `b` is
  //     passed as 0 — a bookkeeping value only, never the real (unknown)
  //     subtrahend, which this payload never carries at all.
  //   blank === "minuend" (___ − b = c): draw `a` (= impliedAnswer, the
  //     full minuend) objects with the known `b` crossed — structurally
  //     identical to the normal take_away dot layout (the first a−b=c are
  //     intact, the last b are crossed), so counting every object DOES
  //     give the answer — the concrete-band strategy the block's own
  //     sketch explicitly sanctions. What must never happen is a NUMERAL
  //     of that answer being printed: TakeAwayBlankRenderer's caption
  //     shows "?" for the blank side (StagedMathRenderers' `numeralCard`
  //     already renders "?" for any non-finite value, so the renderer
  //     passes `undefined` for the blank operand rather than its real
  //     number).
  //
  // Deliberately NOT the `take_away` kind: mathVisualStage.ts's
  // STAGED_KINDS would project a `take_away` payload to `count_back` past
  // "concrete", and CountBackRenderer PRINTS its `a` prop as a numeral
  // card — for blank-minuend, `a` IS the answer, so that projection would
  // be a straight answer leak. `take_away_blank` is a new, UNSTAGED kind
  // (same reasoning as `ten_frame_sub`'s own exclusion: the authored model
  // IS the picture at every band, not an early counted-all stand-in).
  if (model?.mode === "sub" && model.blank) {
    // `total` (the module's `Number(item.expectedAnswer)` local) is
    // declared further down this function — model.impliedAnswer IS that
    // same value here (parseMathItem's final gate already required
    // impliedAnswer === expectedAnswer before returning a model at all).
    return {
      kind: "take_away_blank",
      a: model.a,
      b: model.blank === "subtrahend" ? 0 : model.b,
      op: "-",
      total: model.impliedAnswer,
      blank: model.blank,
    };
  }
  // MA-654 — teen-number ten-frame READ items (ms.math.13 "What number is
  // shown?" / "How many ones are with the ten?" / "How many tens are in
  // this teen number?"), the two_ten_frames twin of the base_ten_blocks
  // branch above. The value lives ONLY in sourceStimulus_v2.details
  // {firstFrameFilled, secondFrameFilled, number?, focus?} — no `operation`
  // and no missing-addend signature, so this is NOT the MA-C10 missing-
  // addend branch just above, and it is NOT the visualSupport==="two_ten_frames"
  // ADDITION siblings (ms.math.11 "What is the sum?", stimulus shape
  // {firstCount,secondCount,operation}, read by stimulusOperandsFromDetails
  // into a plain "add" model further below) — narrowly gated on the
  // PRESENCE of `firstFrameFilled` in the stimulus details, the one field
  // that signature never carries, so the addition siblings fall through to
  // their existing (unaffected) generic add/sub path untouched. These
  // teen-read items fell through to the synth-split fallback below (a
  // fabricated a/b split — e.g. answer 11 became a=6/b=5, not "ten and
  // one") and rendered blank on Mixed (visualRequired:false). Route to the
  // two_ten_frames_teen renderer: first frame fully filled (10), second
  // frame filled with the ones — read-the-frames, the same "the visual IS
  // the question" pattern as read-the-blocks. Fail-closed: firstFrameFilled
  // must be exactly 10 (a partial first frame isn't "ten and some ones"),
  // secondFrameFilled must be a valid ones count (0-9), the authored
  // `number` (when present) must equal 10+secondFrameFilled, and the
  // answer must be READABLE off the drawing for the item's own focus (the
  // whole teen number when unfocused, the ones count for focus:"ones", or
  // the tens count [always 1, since the first frame is always exactly one
  // ten] for focus:"tens") — else no picture, and this branch returns
  // undefined unconditionally so the synth-split tail below is never
  // reached for this stimulus type (mirrors the MA-114 backstop).
  if (raw === "two_ten_frames" && item.sourceStimulus_v2?.details?.firstFrameFilled !== undefined) {
    const det = item.sourceStimulus_v2?.details;
    const first = Number(det?.firstFrameFilled);
    // MA-654 RED round 1 (nit 3): `det.secondFrameFilled === null` must be
    // rejected explicitly — `Number(null)` coerces to `0`, a valid integer
    // in [0,9], so without this check a null payload would silently draw
    // "0 ones" instead of failing closed (no picture) as a malformed/
    // incomplete stimulus should.
    const second = det?.secondFrameFilled === null ? NaN : Number(det?.secondFrameFilled);
    if (det && first === 10 && Number.isInteger(second) && second >= 0 && second <= 9) {
      const value = first + second;
      const authored = Number(det.number);
      const consistent = det.number === undefined || authored === value;
      const expN = Number(item.expectedAnswer);
      const answerReadable =
        (det.focus === undefined && expN === value) ||
        (det.focus === "ones" && expN === second) ||
        (det.focus === "tens" && expN === 1);
      if (consistent && answerReadable) {
        return { kind: "two_ten_frames_teen", a: first, b: second, total: value };
      }
    }
    return undefined;
  }
  // MA-114 (fail-closed backstop): a part-whole stimulus that the reader
  // REFUSED to model (degenerate known ≤ 0 / known ≥ whole / inconsistent
  // triple) must never fall through to the synth-split below — for a "Which
  // part is missing?" item that path draws exactly expectedAnswer objects,
  // i.e. it REVEALS the answer. No model from a part-whole stimulus = no
  // picture, period.
  if (!model && item.sourceStimulus_v2?.details &&
      (item.sourceStimulus_v2.details.knownPart !== undefined ||
       item.sourceStimulus_v2.details.totalTarget !== undefined)) {
    return undefined;
  }
  const total = Number(item.expectedAnswer);
  if (!Number.isFinite(total)) return undefined;
  // Phase 2 M1-full (grade-2 ceiling) — itemType-keyed concept visuals.
  // ADDITIVE: array-multiplication items route to the `array` renderer
  // (kind "array"; a = rows, b = cols, total = a*b), mirroring how
  // equal_groups falls through to the `groups` kind below. Division-as-
  // sharing reuses the existing `groups` visual (total split into `a`
  // equal shares; no new renderer). These branch off itemType BEFORE the
  // prompt-parse path so the authored a/b dimensions are used verbatim.
  const itemType = item.itemType ?? "";
  // MA-707 — two-digit ADDITION WITH REGROUPING (ms.math.26, all 50 records
  // `itemType: "addition_regroup"`). Routed here, before every generic
  // add/sub arm below, because the generic tail can only draw `a + b`
  // loose dots — no tens structure, so the regroup is undrawable — and the
  // MA-704 stopgap (MAX_LOOSE_COUNTED_OBJECTS) refuses that pile above 20,
  // which left these items with no picture at all. Emits `column_add`: the
  // two addends as base-ten blocks (tens rods / ones cubes) in two
  // labelled rows under a Tens and a Ones column, the sum row EMPTY
  // (ColumnAddRenderer.tsx never reads `total`; `total` is carried for
  // contract parity only).
  //
  // Keyed on the itemType plus the record's OWN authored top-level `a`/`b`
  // — never on the prompt text. Fail-closed (no picture) unless both
  // addends are integers 10–99 (two-digit: at most 9 rods and 9 cubes per
  // row, never a loose counted field), they reconstruct the answer, and,
  // when parseMathItem also read a model from the prompt, that model names
  // the same two addends (a record whose prompt and authored fields
  // disagree draws nothing rather than a picture of the wrong sum).
  if (itemType === "addition_regroup") {
    const A = Number((item as { a?: number }).a);
    const B = Number((item as { b?: number }).b);
    const twoDigit = (n: number) => Number.isInteger(n) && n >= 10 && n <= 99;
    const modelAgrees = !model || (model.mode === "add" && model.a === A && model.b === B);
    if (twoDigit(A) && twoDigit(B) && A + B === total && modelAgrees) {
      return { kind: "column_add", a: A, b: B, op: "+", total };
    }
    return undefined;
  }
  // MA-C1 (ADR-012): counting items are ONE group of `total` (= expectedAnswer)
  // objects -- NEVER an a+b addition. These items carry stale top-level a/b
  // (e.g. count "9" with a:9,b:2); without this branch they fell through to the
  // generic add path and drew a+b=11 counters WITH a "+", contradicting the
  // answer set. `b: 0` makes Groups draw a single group with no "+" (the
  // renderer only adds the second group + operator when b > 0); ten_frame is
  // used up to its 10-cell capacity, else uncapped groups. (a/b are ignored
  // for count items.)
  // MA-690: `subitize_to5` no longer reaches this branch — the subitize
  // intercept above returns for it on every path (dot_pattern or undefined).
  if (
    itemType === "count_objects" ||
    itemType === "count_touch_objects"
  ) {
    const kind = total <= 10 ? "ten_frame" : "groups";
    // Only count_objects names a concrete noun ("Count the stars"); count_touch
    // prompts literally say "dots" -> no glyph -> circles (the
    // byte-identical legacy return below, with no glyph key).
    const glyph =
      itemType === "count_objects" ? detectCountGlyph(String(item.prompt)) : undefined;
    return glyph
      ? { kind, a: total, b: 0, op: "+", total, glyph }
      : { kind, a: total, b: 0, op: "+", total };
  }
  // MA-659 round 2 (RED must-fix 3): ms.math.27 is 3.OA fact fluency,
  // picture-free BY SKILL — the same editorial call `isReadTheBarFraction`
  // makes for fraction comparison (judge report §C2,
  // MA-651-batch3-arrays-2026-09-14.md). Round 1 gated only the
  // `array_multiplication` arm on this, so C3's equal_groups retags (which
  // keep `itemType: "equal_groups"`) re-opened the exact defect through the
  // `discrete_clusters` door — 3 ms.math.27 items drew a 30/30/36-object
  // concrete picture, worse than the C2 defect this slice exists to close
  // (a year behind the 8.0–8.5 band). Fixed at the root: one shared,
  // skill-keyed predicate every picture-bearing arm below consults BEFORE
  // its own dimension/clamp gate, gated on the item's OWN `targetSkill`
  // (never the served label — legacySkillIdMap.json aliases like
  // "math.27" -> "ms.math.27" would defeat a gate keyed on a threaded
  // parameter; `item.targetSkill` is authored on the item and can't be
  // relabeled by the serving path). Any future picture-bearing arm added
  // to this function for a fact-fluency-eligible item type must consult it
  // too, not just array_multiplication/equal_groups.
  const isPictureFreeFactFluencySkill = item.targetSkill === "ms.math.27";
  if (itemType === "array_multiplication") {
    const rows = Number((item as { a?: number }).a);
    const cols = Number((item as { b?: number }).b);
    // MA-659 (MA-651 batch 3, RENDER-C): mirror the equal_groups product gate
    // exactly — only emit a picture that reconstructs expectedAnswer.
    // MA-659 C2: >25 dots is uncountable/illegible (26–81 dots at 34px pitch
    // — still corpus-dead OUTSIDE ms.math.27 today: no ms.math.17/18
    // array_multiplication item's rows*cols exceeds 25 since both skills'
    // own ceiling is max(a,b)<=5, so this clause is a forward guard for
    // those two skills; ms.math.27 items reach up to 9x10=90 post-MA-659b,
    // but every one of them is already suppressed by the skill gate below
    // regardless of this threshold).
    if (
      Number.isFinite(rows) && Number.isFinite(cols) &&
      rows * cols === total &&
      rows * cols <= 25 &&
      !isPictureFreeFactFluencySkill
    ) {
      return { kind: "array", a: rows, b: cols, op: "+", total };
    }
    return undefined;
  }
  // MA-C13 (ADR-013 follow-up): plain "N equal groups of M, how many in all?"
  // renders as `a` DISCRETE CLUSTERS of `b` dots — the groups-of structure is
  // visible (each group is enclosed in an ellipse outline). Total dot count =
  // a*b = expectedAnswer. No "+" operator. Only emit when the dimensions
  // reproduce the answer; otherwise draw no picture rather than a misleading one.
  // (division_sharing + the equal-groups word-problem variants are in
  // NO_VISUAL_ITEM_TYPES, so they never reach here.)
  if (itemType === "equal_groups") {
    const groups = Number((item as { a?: number }).a);
    const per = Number((item as { b?: number }).b);
    // MA-659 (MA-651 batch 3, RENDER-D): gate emission mapper-side on the
    // renderer's own clamp (`DiscreteClustersRenderer` MAX_CLUSTERS=6,
    // MAX_PER=8) — otherwise an over-clamp item silently draws the WRONG
    // number of groups/dots with no error, because the product gate above
    // knows nothing about the renderer's clamp. MA-659 round 2: also
    // skill-gated on ms.math.27, same predicate as array_multiplication —
    // see the shared comment above.
    if (
      Number.isFinite(groups) && Number.isFinite(per) &&
      groups > 0 && per > 0 && groups * per === total &&
      groups <= 6 && per <= 8 &&
      !isPictureFreeFactFluencySkill
    ) {
      return { kind: "discrete_clusters", a: groups, b: per, op: "+", total };
    }
    return undefined;
  }
  // MA-C12 (ADR-013 follow-up): division_sharing → partition/"dealing" visual.
  // Emits `a` empty rings (groups) + a pile of `a*b` items (the dividend).
  // ANSWER-SAFE: rings are empty (child can't count items in a ring); pile has
  // a*b items (the dividend, not the per-share answer b); ring count = a (groups,
  // not b). The child must mentally divide total by groups.
  // GATE: only for "how many per group?" items (expectedAnswer === b, i.e.
  // total===perShare here). The 3 "how many groups?" items (expectedAnswer===a)
  // are suppressed — their answer equals the ring count, which would reveal it.
  if (itemType === "division_sharing") {
    const groups = Number((item as { a?: number }).a);
    const perShare = Number((item as { b?: number }).b);
    const dividend = groups * perShare;
    if (
      Number.isFinite(groups) && Number.isFinite(perShare) &&
      groups > 0 && perShare > 0 &&
      total === perShare &&  // expectedAnswer === b  →  "how many per group?" item
      // MA-708: mirror MA-659's equal_groups → discrete_clusters gate —
      // emit nothing rather than a TRUNCATED (false) pile when the
      // authored dividend/group-count would exceed DivisionSharingRenderer's
      // own clamp. Without this, the renderer silently draws fewer items
      // than authored (MEG_M19_008: total=36 drawn as 30, no error, no
      // suppression) — importing the SAME constants the renderer clamps to
      // means the mapper's gate and the renderer's clamp cannot drift apart.
      groups <= DIVISION_SHARING_MAX_GROUPS && dividend <= DIVISION_SHARING_MAX_TOTAL &&
      // MA-712: the pile may only draw a total the PROMPT ITSELF STATES (as a
      // digit or number word). A combine-then-share item ("10 red blocks and
      // 2 blue blocks … shared into 4 groups") never states its dividend, so
      // a pile of 12 would do step one (the combining) for the child — emit
      // nothing instead, the same fail-closed move the "how many groups?"
      // sub-type takes above. A rule on the prompt, not an id list.
      numberAppearsInPrompt(String(item.prompt ?? ""), dividend)
    ) {
      return { kind: "division_sharing", a: groups, b: perShare, op: undefined, total: dividend };
    }
    return undefined;
  }
  // MA-706 — skip_count_line: a horizontal number track (one stop per
  // sequence position, NOT one tick per integer). Only the 4 reliably-
  // parseable shapes in SKIP_COUNT_LINE_ITEM_TYPES reach here (the
  // NO_VISUAL_ITEM_TYPES check above already routed skip_count_two_step's
  // "how many more/how many counted" word-problem shape away from
  // buildMathVisual entirely — D2, no blank position to draw). Fail-closed:
  // a prompt that doesn't parse into a validated {step,terms} model gets no
  // picture, same as any other shape parseSkipCountLine rejects.
  if (SKIP_COUNT_LINE_ITEM_TYPES.has(itemType)) {
    const model = parseSkipCountLine(item);
    if (!model) return undefined;
    return { kind: "skip_count_line", total, step: model.step, terms: model.terms };
  }
  const promptStr = String(item.prompt ?? "").toLowerCase();
  // M2 (place value) — additive mapper branch mirroring the equal-groups /
  // groups path: a `place_value` item resolves the `place_value_blocks`
  // renderer (registry key), which decomposes `total` into hundreds/tens/ones.
  // The number to DECOMPOSE is the prompt's READ-SHAPE SUBJECT, extracted only
  // as the object of an anchored corpus template (see round 2 rewrite below —
  // "How many tens are in 34?" → 34), NOT the expectedAnswer, and NOT any bare
  // digit token found elsewhere in the prompt: a COMPOSE prompt with no such
  // template (e.g. "Build 27 with blocks. How many did you use?") now draws
  // nothing rather than guessing, and an unanchored near-miss of the template
  // (e.g. "How many tens in 34?", missing "are") also draws nothing — both
  // examples this comment used to cite as drawing a picture (27 and 34,
  // respectively) are false under the current anchored logic (RED round 3).
  //
  // MA-658: this branch used to fall back to `total` (=== expectedAnswer)
  // when no two-digit subject was present, "so the visual stays answerable
  // (never NaN)". That was wrong for a COMPOSE item ("You have 1 ten and 2
  // ones. What number did you build?") — 1 and 2 are filtered out by the
  // >=10 gate, so subjectNums is always empty for these, and the fallback
  // handed the renderer the answer itself: all 50 ms.math.21 items drew
  // their own answer in rods and cubes. Every neighbouring branch in this
  // file (number-bond, comparison, three-group, take-away) fails closed —
  // emits no mathVisual — when it cannot verify the model from a
  // prompt-sourced subject; this branch now matches them. ms.math.20's read
  // items ("How many tens are in 34?") keep their picture (34 is the
  // prompt-sourced subject); ms.math.13's base_ten_blocks read items take a
  // different branch entirely (:1165) and are unaffected. Whole-corpus
  // mapper diff vs master: exactly the 50 ms.math.21 ids lose their
  // mathVisual — see scripts/audit/placeValueComposeVisualIntegrityTest.mjs.
  // The 50 remain Mixed-servable: `visualSupport` on these items is a prose
  // string, not the literal "base_ten_blocks", so `visualRequired` was
  // already false and stays false with no mathVisual at all.
  //
  // MA-658 RED must-fix 4 (round 2 rewrite): a bare "any 10-999 token in the
  // prompt" test is not the same test as "the prompt's READ SUBJECT" — it
  // also matches a regrouping COMPOSE count that happens to be >=10, e.g.
  // "You have 1 ten and 12 ones. What number did you build?" (answer 22),
  // AND (round 1's own fix was found unsound in the RED round-2 review) an
  // unrelated stray token elsewhere in the prompt that happens to equal the
  // keyed answer, e.g. "Team 22 has 1 ten and 12 ones. What number did you
  // build?" (answer 22) — round 1's `subjectReconcilesWithAnswer` clause
  // PASSED that stray "22" via its own first disjunct (`n ===
  // expectedAnswerNum`), i.e. a guard whose job was "don't draw the answer"
  // contained "the token equals the answer" as a pass condition, and drew
  // the answer. Answer-reconciliation is not a substitute for reading the
  // actual template; round 2 replaces it with an ANCHORED READ-SHAPE match
  // against the two real ms.math.20 templates this arm serves ("How many
  // tens are in 34?" / "How many ones are in 34?" — the ONLY prompt shapes
  // in the corpus that carry a digit subject through this arm; ms.math.13's
  // digit-bearing reads take the separate itemType-`directPractice` branch
  // at :1165 and never reach here, and ms.math.13's other reads carry no
  // digit at all), plus a HARD REJECT on `total === expectedAnswer`
  // regardless of shape — the answer is never drawn, full stop, closing the
  // structural hole in round 1's fix rather than adding another exception
  // to it.
  if ((item.itemType ?? "") === "place_value" || raw.includes("place_value")) {
    const expectedAnswerNum = Number(item.expectedAnswer);
    // Single-subject read shape, anchored to the real corpus templates
    // ("How many tens/ones/hundreds are/is in N?") — the digit is extracted
    // directly as the OBJECT of the template, never scanned for as a bare
    // token, so an unrelated stray number elsewhere in the prompt (a page
    // number, a team number, a second child's already-stated answer) can
    // never be mistaken for the read subject in the first place.
    const readShapeMatch = promptStr.match(
      /\bhow many\s+(?:tens?|ones?|hundreds?)\s+(?:are|is)\s+in\s+(\d{1,3})\b/i,
    );
    if (readShapeMatch) {
      const subject = Number(readShapeMatch[1]);
      // HARD REJECT (RED round 2): never draw the keyed answer as the
      // picture, regardless of what shape matched — a belt-and-braces
      // backstop independent of the read-shape anchor above.
      if (Number.isFinite(expectedAnswerNum) && subject === expectedAnswerNum) {
        return undefined;
      }
      // MA-694 — the prompt states this number, so the blocks' aria must
      // not count its places for a screen-reader child (the item asks for
      // one of them). Only this branch sets it: here `total` IS the prompt's
      // own subject and (just above) never the answer.
      return { kind: "place_value_blocks", total: subject, displayStyle: PLACE_VALUE_SUBJECT_STATED };
    }
    // MA-C14 comparison shape ("Which number is greater, 34 or 43?"): two
    // DISTINCT subject tokens, neither a compose count ("N ten(s)"/"N
    // one(s)" — dropped before comparison, same rationale as round 1: a
    // regrouping compose count must never be mistaken for a comparison
    // subject). The answer (total = Number(expectedAnswer)) is NOT drawn —
    // the child reads relative magnitude off the blocks — so no read-shape
    // anchor or reconciliation is required here; the two-subject shape
    // itself is the comparison contract (unchanged from pre-MA-658,
    // verified unaffected by the whole-corpus diff). NOTE (RED round 2,
    // named not fixed — pre-existing, unpinned, out of this must-fix's
    // scope): this branch has no read-shape anchor of its own, so a compose
    // prompt with two stray distinct 10-999 tokens (e.g. "... Hint: it is
    // between 10 and 20 ...") still draws a comparison from them.
    const composeCountRe = /\d{1,3}\s*(tens?|ones?)\b/g;
    const composeCountSpans: Array<[number, number]> = [];
    for (const m of promptStr.matchAll(composeCountRe)) {
      composeCountSpans.push([m.index ?? 0, (m.index ?? 0) + m[0].length]);
    }
    const numTokenRe = /\d{1,3}/g;
    const subjectNums: number[] = [];
    for (const m of promptStr.matchAll(numTokenRe)) {
      const n = Number(m[0]);
      if (n < 10 || n > 999) continue;
      const start = m.index ?? 0;
      const isComposeCount = composeCountSpans.some(([s, e]) => start >= s && start < e);
      if (isComposeCount) continue;
      subjectNums.push(n);
    }
    if (subjectNums.length === 2 && subjectNums[0] !== subjectNums[1]) {
      return { kind: "place_value_comparison", a: subjectNums[0], b: subjectNums[1], total };
    }
    // No verified read-shape or comparison model → no mathVisual (mirrors
    // every other branch's fail-closed convention). This is the fail-closed
    // path for every compose prompt ("You have 1 ten and 2 ones. What
    // number did you build?") — no read-shape match, and after compose-
    // count filtering its stray tokens never form a valid two-subject
    // comparison either.
    return undefined;
  }
  // MA-94: when a verified model exists, the operation IS the model's — a
  // stimulus-sourced subtraction ("Where do you land?" with operation:
  // "subtraction") must route to the take-away picture even though the prompt
  // has no subtraction wording for the heuristic to sniff. The prompt
  // heuristic remains the model-less fallback (unchanged behavior there).
  // RED round 1 MF2(b) — this bare-glyph branch (fires only when parseMathItem
  // found NO model at all) originally recognized U+2212 but not U+2013, even
  // though OP_CLASS above (used the moment a model DOES exist) accepts all
  // three. An en-dash item with no other subtraction signal would have fallen
  // through to "+" here — an addition picture for a subtraction item. Both
  // non-ASCII glyphs are unambiguous minus signs wherever they appear (unlike
  // ASCII "-", which also appears in hyphenated words), so neither needs the
  // ASCII branch's `(?<!\d)` lookbehind guard.
  const op: "+" | "-" = model
    ? (model.mode === "sub" ? "-" : "+")
    : raw.includes("take_away") || /\btake away\b|\bleft\b|\bfewer\b|[−–](?=\s*\d)|(?<!\d)-(?=\s*\d)/.test(promptStr)
      ? "-"
      : "+";
  let a = (model?.a ?? (item as { a?: number }).a);
  let b = (model?.b ?? (item as { b?: number }).b);
  // Recover the REAL operands from the prompt when present (e.g.
  // "There are 1 counters. We add 3 more.") so the two groups are the
  // authored ones, not a synthesized split. Only accept a pair that
  // actually reconstructs the answer under the operation.
  if (a === undefined || b === undefined) {
    const ns = (promptStr.match(/\d+/g) ?? []).map(Number);
    for (let i = 0; i < ns.length - 1; i++) {
      const x = ns[i], y = ns[i + 1];
      if ((op === "+" && x + y === total) || (op === "-" && x - y === total)) {
        a = x; b = y; break;
      }
    }
  }
  const isFrame = FRAME_VISUALS.has(raw) || raw.includes("ten_frame") || raw.includes("frame");
  const isLine = LINE_VISUALS.has(raw);
  const isAdditionType =
    raw.includes("join") || raw.includes("two_") || raw.includes("split") ||
    raw.includes("double") || raw.includes("take_away") || raw.includes("bond") ||
    raw.includes("addend") || op === "-";
  if (isAdditionType && (a === undefined || b === undefined)) {
    // synthesize an answerable split of the total (a + b = total)
    a = Math.ceil(total / 2);
    b = total - a;
  }
  // A ten-frame has exactly 10 cells, so it can only represent counts up to 10.
  // For sums/operands that overflow (e.g. "7 + 5 = 12"), a ten-frame silently
  // drops the extra dots (showing 7 + 3), contradicting the problem. When the
  // dots wouldn't fit, fall back to the uncapped "groups" visual (which draws
  // a + b counters correctly). See rendererRegistry TenFrame (10-cell) vs Groups.
  const framedDots = (a ?? total ?? 0) + (b ?? 0);
  const frameFits = Number.isFinite(framedDots) && framedDots <= 10;
  // MA-94, cap kept post-MA-491: NumberLine's own range now SCALES to the
  // real total instead of clamping (MA-491 fixed the renderer to draw a
  // faithful line past 10), so this 10-cap is no longer required for
  // correctness. Left in place deliberately: it keeps number_line reserved
  // for small, single-glance sums (matching TenFrame's own 10-cell reach
  // just above) and falls back to the uncapped groups visual for larger
  // sums, which is a readability/consistency choice, not a correctness fix
  // — relax it only alongside a real routed item that needs a >10 line.
  const lineFits = Number.isFinite(framedDots) && framedDots <= 10;
  // MA-94 §3b RED-1: NEVER route a stimulus-sourced model to the number_line
  // renderer — it prints a numeral under every tick and marks the landing
  // position, so the dot sits ON the printed answer (a read-off reveal, no
  // counting effort). Stimulus-sourced jump items get the groups counters
  // instead (count-what's-shown, the ADR-020-sanctioned pattern). Prompt-
  // parsed models keep the legacy routing (which emits zero number_lines on
  // the current corpus).
  const lineSafe = lineFits && model?.source !== "stimulus";
  const kind = isLine ? (lineSafe ? "number_line" : "groups") : isFrame && frameFits ? "ten_frame" : "groups";
  // MA-C5 (ADR-020): emit the add/sub manipulative ONLY when the picture the
  // renderer ACTUALLY draws reconstructs the answer. The Groups / TenFrame /
  // NumberLine renderers (rendererRegistry.tsx) draw a+b and IGNORE `op`
  // entirely — TenFrame fills a+b cells, Groups shows "a + b", NumberLine marks
  // a and a+b. So the only faithful picture is a+b === expectedAnswer (= total).
  // This suppresses, as misleading or self-revealing: a missing-addend
  // "___ + 1 = 4" (drew 1+3, REVEALING the answer 3); a comparison "Which group
  // has more: 4 or 3?" (drew an addition 4+3); items with unresolved operands
  // (pre-chapter numeral-ID ms.math.00c/00d → a degenerate empty group). The
  // faithful majority — additions where a+b===total, incl. the synth-split and
  // a−0 single-group cases — is unchanged. This is the add/sub generalization of
  // the count (ADR-012) and concept (ADR-013) rule.
  // F1 fix (diligence 2026-06-26, post-MA-C5/ADR-020): Groups / TenFrame /
  // NumberLine draw a+b and IGNORE `op` — a subtraction item would draw an
  // ADDITION picture. The synth-split (isAdditionType includes op==="-") gives
  // a+b===total, so misleading subtractions slipped through the gate.
  // MA-C9: the real take-away renderer (`take_away` kind) draws `a` objects with
  // the last `b` crossed out, faithfully depicting a−b=remaining. Emit it when:
  //   • prompt has no blank operand (missing-subtrahend "7−___=3" is suppressed;
  //     the digit pair found would be (7,3) not the actual subtrahend 4, giving
  //     a wrong crossed-out count even though a−b===total passes numerically)
  //   • both operands are real and finite
  //   • a−b===total (distinguishes real operands from synth-split, which gives
  //     a+b===total, so a−b = total−2b ≠ total for b>0)
  //   • b>0 (at least one object removed; b===0 has no take-away picture)
  // All remaining subtractions — synth-split, missing-minuend/subtrahend, no
  // operand pair — are still suppressed. Guarded by addSubVisualIntegrityTest
  // (take_away branch: op==="-", a−b===total, no <text> numeral).
  if (op === "-") {
    const subEligible =
      !promptStr.includes("___") &&
      Number.isFinite(a) &&
      Number.isFinite(b) &&
      (a as number) - (b as number) === total &&
      (b as number) > 0 &&
      // MA-94 §3b RED-2: TakeAwayRenderer clamps at 20 objects — a minuend
      // above 20 would draw 20−b ≠ the problem and contradict the answer.
      (a as number) <= 20;
    if (subEligible) {
      const A = a as number;
      const B = b as number;
      // MA-655 — route AUTHORED subtraction stimuli to a faithful renderer
      // BEFORE the generic take_away arm, which used to fire on every
      // faithful subtraction regardless of what visualSupport/
      // sourceStimulus_v2.type the item was actually authored against (see
      // docs/build/reports/MA-651-batch1-take-away-2026-09-14.md — 121
      // items across 6 authored stimulus shapes all rendered the identical
      // dot grid). ADDITIVE: each new kind self-registers a renderer in
      // rendererRegistry.tsx; take_away remains the fallback for
      // generic/counter_set/picture_take_away-authored items, which MA-651
      // §C5/C6/PASS found already faithfully match their prompt (bare
      // "how many are left" or explicit story text over crossed dots) — no
      // authored structure is being discarded for those.
      //
      // (1) Frame-authored subtraction (MA-651 §C2, 21 ids) — visualSupport/
      // sourceStimulus_v2.type is a frame kind (isFrame, computed above) and
      // a<=10 (a ten-frame has exactly 10 cells): ten_frame_sub — a filled,
      // the LAST b of them crossed, exactly the picture ScaffoldT10 already
      // draws in sub mode (scaffolds/index.tsx). `two_ten_frames` is also a
      // FRAME_VISUALS member but gets its OWN arm (2) below (a different
      // renderer/staging), so it is excluded here even though isFrame is
      // true for it too.
      //
      // REQUIRES a real sourceStimulus_v2 (a leak found by the mapper
      // self-test, run over the real corpus): `isFrame` includes
      // a bare `raw.includes("frame")` substring check, which also matches
      // the 24 "generic" items' long descriptive visualSupport sentence
      // ("use counters, ten-frame, number path, or object groups depending
      // on age band" — contains "frame" as a substring of "ten-frame") even
      // though those items were never authored against an actual frame
      // stimulus (they carry no sourceStimulus_v2 at all — verified over
      // every generic-bucket id). Every REAL frame-authored item (ME_08_002
      // etc.) does carry one (`sourceStimulus_v2.type: "ten_frame"`), so
      // requiring its presence excludes the generic sentence without
      // narrowing the string match itself (which addition routing below
      // still relies on unchanged).
      if (isFrame && raw !== "two_ten_frames" && item.sourceStimulus_v2 != null && A <= 10) {
        return { kind: "ten_frame_sub", a: A, b: B, op, total };
      }
      // (2) two_ten_frames-authored subtraction (MA-651 §C4's ms.math.11
      // ids, plus any other launch_safe subtraction authored against the
      // two_ten_frames stimulus) — a<=20 (two stacked ten-frames = 20
      // cells): two_ten_frames_sub — first frame filled, the LAST b of the
      // filled cells crossed (spanning back across the frame boundary), so
      // the make-ten structure 1.OA.C.6 names stays visible instead of
      // collapsing to loose dots.
      if (raw === "two_ten_frames" && A <= 20) {
        return { kind: "two_ten_frames_sub", a: A, b: B, op, total };
      }
      // (3) Line-authored subtraction (MA-651 §C3, 19 ids, plus the 10
      // number_line-authored ms.math.11 ids folded into §C4) —
      // visualSupport === "number_line" (LINE_VISUALS/isLine, computed
      // above): take_away_line. MA-94 §3b RED-1 still forbids routing a
      // stimulus-sourced subtraction to the number_line RENDERER itself
      // (it would print a numeral under the landing tick — a read-off
      // reveal), so this is NOT that renderer; it is a mapper-only kind
      // that mathVisualStage.ts (stageMathVisual) projects to `count_back`
      // (numeral card a, crossed b dots — literally "start at a, jump back
      // b") at EVERY band, concrete included, and to `equation` at fluent.
      // take_away_line is registered as an alias renderer that also draws
      // count_back, so an item served before staging even runs (there is
      // none in this mapper — staging is session-screen-time only, see
      // mathVisualStage.ts) never has a missing renderer. Disclosed against
      // MA-625's band table in mathVisualStage.ts: this is the one kind
      // with no counted-all concrete stage BY DESIGN.
      //
      // MA-655 RED must-fix 2: `isLine && A<=20` alone also caught
      // ME_08_023/031/035 — authored number_line, but prompt "Look at the
      // picture. How many are left?" (not "jump back"), a<=5. MA-651 §PASS
      // judged those three PASS on exactly the prompt/picture agreement
      // count_back destroys: the child is told to look at a picture for
      // what is LEFT, and count_back draws a numeral card + a "−" + crossed
      // dots with no countable remainder and (unlike the frame kinds) no
      // MA-647 caption to fall back on. So the line arm additionally
      // requires the prompt to actually carry the count-back "jump back /
      // where do you land" framing, OR teen magnitude (A>10 — MA-651 §C4's
      // ms.math.11 "counted-all-at-16" finding, none of which use "look at
      // the picture" phrasing). This keeps all 29 intended repairs (the 19
      // §C3 "jump back" ids + the 10 §C4 teen ids) and leaves the 3 PASS
      // ids on the plain take_away arm below, unchanged from what MA-651
      // already confirmed reads correctly for them.
      if (isLine && A <= 20 && (/jump back|where do you land/i.test(promptStr) || A > 10)) {
        return { kind: "take_away_line", a: A, b: B, op, total };
      }
      return { kind: "take_away", a, b, op, total };
    }
    return undefined;
  }
  // MA-705 — two_ten_frames-authored ADDITION (MA-651 batch13 §3 C2's
  // ms.math.11 addition twins of MA-655's two_ten_frames_sub — the one
  // skill whose standard, 1.OA.C.6, names the make-ten strategy by name).
  // Routed BEFORE the generic isAdditionType/drawnAnswer tail below, which
  // used to let these fall to the uncapped "groups" arm and draw a loose
  // pile of up to 18 dots (MA-651 batch13's friction finding, 10 ids).
  //
  // Gate mirrors MA-655's two_ten_frames_sub arm exactly (raw ===
  // "two_ten_frames", the routing key — this is the conjunct that actually
  // excludes any other two_ten_frames-authored ITEM SHAPE, load-bearing:
  // RED's MUT-4, dropping it, reds 2 test files and 260 audit assertions),
  // plus real finite a/b>0 (both addends must actually be authored — the
  // synth-split fallback never reaches this branch since a/b are already
  // set from the real corpus stimulus by this point, see
  // stimulusOperandsFromDetails/model.mode "add" above) and a+b<=20 (two
  // stacked ten-frames = 20 cells; the real corpus's 10 ids sum 11-18, see
  // the Phase 0 table, but this stays a real gate rather than an
  // assumption).
  //
  // R3 (operator ruling round 1, 2026-09-21): no separate `op === "+"`
  // conjunct here — dead by construction, not just by data. The `op ===
  // "-"` arm immediately above unconditionally `return`s on every path (a
  // faithful sub kind or `undefined`), so control only reaches this branch
  // when `op` is statically `"+"` (TypeScript narrows it; `op: "+" | "-"`
  // has no third case) — subtraction is excluded by control flow, not by
  // an `A + B === total` coincidence, and re-checking it here would be a
  // no-op RED's own MUT-3 (widening a hypothetical gate to also accept
  // `op === "-"`) proved unreachable.
  if (raw === "two_ten_frames" && Number.isFinite(a) && Number.isFinite(b)) {
    const A = a as number;
    const B = b as number;
    if (A > 0 && B > 0 && A + B <= 20 && A + B === total) {
      return { kind: "two_ten_frames_add", a: A, b: B, op, total };
    }
  }
  // MA-697 — line-authored, stimulus-sourced ADDITION ("Start at 5. Jump
  // forward 3. Where do you land?", ms.math.07/09; ms.math.11's "What is the
  // sum?" line records): the addition twin of MA-655's `take_away_line`.
  // `lineSafe` above correctly refuses the numeral-printing `number_line`
  // renderer for these (MA-94 §3b RED-1), but nothing replaced it, so they
  // fell to two static `groups` piles. `count_on_line` draws the line with a
  // start marker, unit ticks and `b` hops, and no numeral past the start
  // (CountOnLineRenderer.tsx); mathVisualStage.ts stages it to count_on /
  // equation like its non-jump `groups` siblings. Gate: isLine AND a
  // stimulus-sourced "add" model — so no prompt-parsed item (which keeps
  // `number_line`) and no non-line item is affected — plus the faithful-
  // picture operands (b > 0, a + b === total) and the renderer's 0..20 reach.
  // Only op "+" reaches this point (the op "-" arm above returns on every path).
  if (isLine && model?.source === "stimulus" && model.mode === "add" && Number.isFinite(a) && Number.isFinite(b)) {
    const A = a as number;
    const B = b as number;
    if (A >= 0 && B > 0 && A + B === total && A + B <= COUNT_ON_LINE_MAX) {
      return { kind: "count_on_line", a: A, b: B, op, total };
    }
  }
  const drawnAnswer = (a as number) + (b as number); // renderers draw a+b, ignoring op
  if (!Number.isFinite(a) || !Number.isFinite(b) || drawnAnswer !== total) {
    return undefined;
  }
  // MA-704/MA-707 — never draw the uncapped "groups" fallback as a giant
  // pile of loose dots. ten_frame/number_line are already capped at 10
  // cells (frameFits/lineFits above), so this can only ever fire for
  // kind==="groups"; refuse the picture (no mathVisual) rather than draw
  // more than MAX_LOOSE_COUNTED_OBJECTS counters. The prompt (the bare
  // equation) stays the question, matching the surface every other
  // no-model item in these skills already serves.
  if (kind === "groups" && drawnAnswer > MAX_LOOSE_COUNTED_OBJECTS) {
    return undefined;
  }
  // MA-690 (batch-8 extension) — a join picture under "How many stars in
  // all?" draws stars in both groups (Groups/TenFrame already carry the
  // glyph to every mark). Only a noun with a glyph matches; every other
  // prompt keeps the byte-identical glyph-free payload.
  const inAllGlyph = op === "+" && (kind === "groups" || kind === "ten_frame")
    ? detectInAllGlyph(String(item.prompt))
    : undefined;
  return inAllGlyph ? { kind, a, b, op, total, glyph: inAllGlyph } : { kind, a, b, op, total };
}

// ── controlledSentences

// MA-492 (CS word-image serve wiring slice): per-item sense exclusion for the
// approved-image lookup, mirroring SceneIllustration.jsx's
// TARGET_WORD_ITEM_EXCLUSIONS discipline (MA-308 slice 11) for the image
// lane. illustrationUrlForWord resolves by WORD only (a single manifest
// image per word, e.g. "stamp" -> a postage stamp), but
// scripts/aiImages/csWordImageTargets.mjs's per-occurrence sense
// adjudication found one word ("stamp") where a specific real item uses a
// DIFFERENT sense the image would misdepict (CS_EXP2_121, "stamp his foot
// on the mat" — the verb, not the postage-stamp noun the approved image
// shows) — see that file's CS_IMAGE_WORD_ITEM_EXCLUSIONS (not imported here;
// that module is offline pipeline tooling, not a runtime dependency, so this
// mirrors its data by hand — keep in sync if that file's exclusions change).
// Without this check, CS_EXP2_121 would silently paint the wrong-sense image
// the moment "stamp" was approved, exactly the defect class MA-308 slice 11
// closed for emoji.
// Exported for the parity pin in contentToEngineItem-cs-image-wiring.test.mjs
// (Fable review finding, 2026-08-16): the test deep-compares this mirror
// against csWordImageTargets.mjs's CS_IMAGE_WORD_ITEM_EXCLUSIONS so the two
// cannot silently drift — a new offline exclusion that never reaches this
// runtime mirror would otherwise serve a wrong-sense image with no failing
// test.
//
// MA-511 (CS emoji→AI-image upgrade slice, 2026-08-16): the 6 entries below
// (ride/rode/chop/tube/glass/wrap) mirror the SAME 6 words
// csWordImageTargets.mjs's CS_IMAGE_WORD_ITEM_EXCLUSIONS just added (copied
// verbatim from SceneIllustration.jsx's own TARGET_WORD_ITEM_EXCLUSIONS,
// MA-308 slice 11) — required by the parity pin above, which fails loud
// until both sides carry the identical word/item-id sets, by design.
export const CS_IMAGE_TARGET_ITEM_EXCLUSIONS: Readonly<Record<string, ReadonlySet<string>>> = {
  stamp: new Set(["CS_EXP2_121"]),
  ride: new Set(["CS_12_063"]),
  rode: new Set(["CS_12_069", "CS_12_070"]),
  chop: new Set(["CS_11_055"]),
  tube: new Set(["CS_12_097_v2", "CS_12_100_v2"]),
  glass: new Set(["CS_14_068"]),
  wrap: new Set(["CS_15_035"]),
  // MA-511 Fable-review image-only exclusions (wrong-sense minority items;
  // see csWordImageTargets.mjs's own entry comment for the full reasoning).
  pen: new Set(["CS_SLICE_0605", "CS_10_037"]),
  right: new Set(["CS_15_050"]),
};

/** Controlled sentence → EngineItem (cloze multiple-choice). */
export function controlledSentenceToEngineItem(
  item: ControlledSentence,
  microSkillId: string,
): MappedEngineItem {
  const options = item.options ?? [item.correctAnswer];
  // MA-119 (cs-pic-empty slice): resolve the SAME depiction ScaffoldPIC's
  // targetWord branch (SceneIllustration.jsx, MA-308) independently
  // computes at render time — pure functions over the same two fields
  // (correctAnswer, sentenceTemplate) stored in visualScaffold.data below,
  // so this can never drift from what actually renders. Real launch_safe
  // items resolve neither (readingHintContentAudit.mjs's class
  // a-empty-render) — for those, withdraw PIC rather than offer a hint tab
  // that opens onto a blank render.
  // MA-308 slice 11: pass item.id so a per-item exclusion (a real occurrence
  // using a different sense than the word's mapped majority — see
  // TARGET_WORD_ITEM_EXCLUSIONS in SceneIllustration.jsx) withdraws PIC
  // instead of offering a hint that would then paint the wrong-sense emoji.
  // MA-308 full-target-coverage (architectural slice, closes slices-remaining
  // to none): NO bystander fallback here anymore. A direct target-word
  // lookup or nothing — never `pickEmojiFromTemplate`'s bystander scan, which
  // is the exact defect class this item exists to eliminate (see ADR-088's
  // slices-remaining + the §3d acceptance: "depicts its TARGET or is
  // honestly absent, 0 bystander-only depictions"). An unmapped target now
  // withdraws PIC (supports below) rather than showing a wrong bystander
  // picture — an honest empty state, not a broken one (same principle
  // MA-323 just established for the Letters hint on this same pack/day).
  const targetPicEmoji = pickTargetEmoji(item.correctAnswer, item.id)?.emoji ?? undefined;
  // MA-492 (CS word-image serve wiring slice): a reviewer-APPROVED,
  // uploaded AI image of the TARGET word (scripts/aiImages/manifest.json,
  // resolved offline into approvedImageIndex's word-keyed lookup) is now a
  // second, higher-fidelity source for the exact same depiction —
  // word-image depiction of the CS target is licensed under MA-308 mode
  // (a): decode-cloze assesses decoding the letters, not guessing the word
  // from a picture (founder ruling 2026-07-22), and each of the 48 words
  // currently resolvable here was individually sense-adjudicated per real
  // occurrence in scripts/aiImages/csWordImageTargets.mjs (not edited by
  // this slice — read it for the adjudication reasoning). At render time
  // ScaffoldPIC (src/components/scaffolds/index.tsx) gives data.imageUrl
  // render PRIORITY over the emoji/SceneIllustration path below — the
  // emoji resolved by pickTargetEmoji remains the fallback (still passed
  // through visualScaffold.data below) for words with no approved image,
  // and also the fallback the image itself degrades to if it fails to load
  // (ScaffoldPIC's onError handler). CS_IMAGE_TARGET_ITEM_EXCLUSIONS
  // withdraws the image for the one known wrong-sense item (see that
  // const's own comment) — the emoji fallback above already independently
  // resolves (or doesn't) for that same item via pickTargetEmoji's own
  // TARGET_WORD_ITEM_EXCLUSIONS check.
  const isImageSenseExcluded = CS_IMAGE_TARGET_ITEM_EXCLUSIONS[item.correctAnswer?.toLowerCase() ?? ""]?.has(
    item.id,
  );
  const targetImageUrl = isImageSenseExcluded ? undefined : illustrationUrlForWord(item.correctAnswer) ?? undefined;
  // MA-617a: when neither the emoji nor the approved image resolves, a
  // non-empty authored `gloss` (MA-617b authors these; 617a ships zero) is
  // the meaning clue offered instead — served as SEN with hintCue, at PIC's
  // exact priority slot (one hint, never both). A glossed item that later
  // gains an image drops the gloss silently (the image wins) — G5 in
  // csGlossGuard.mjs makes that condition visible rather than silently
  // stale. Neither PIC nor a gloss resolves → supports stays `[]`, the
  // honest empty state MA-323 established.
  const hasPic = Boolean(targetPicEmoji || targetImageUrl);
  // MA-617a (RED item 9): trimmed — the schema's z.string().trim() already
  // normalizes authored content, but the mapper trims independently too so
  // a hand-authored/legacy record with incidental leading/trailing
  // whitespace never reaches ScaffoldSEN's hintCue un-trimmed.
  const gloss = typeof item.gloss === "string" && item.gloss.trim().length > 0 ? item.gloss.trim() : undefined;
  const glossHintCue = !hasPic && gloss ? gloss : undefined;
  return {
    id: item.id,
    type: "fill_blank",
    prompt: item.sentenceTemplate,
    answer: item.correctAnswer,
    options: shuffle([...options]),
    microSkillId,
    ageBand: item.ageBand ?? "5.0-5.5",
    difficulty: item.difficulty ?? 1,
    // MA-323 (founder ruling 2026-07-23): SBX ("Letters") spelled the ENTIRE
    // correct answer word in letter tiles — a direct answer leak on a
    // multiple-choice cloze. Withdrawn unconditionally for reading; PIC
    // stays the only offered hint, and only when it actually resolves (an
    // item with no PIC match now offers NO hint — HintSlot.tsx renders no
    // "Show hint" button at all for an empty supports array, which is an
    // honest empty state, not a broken one).
    supports: hasPic ? ["PIC"] : glossHintCue ? ["SEN"] : [],
    explanation: item.whyOnlyOneAnswer ?? "",
    // D1 fix: the reading mapper previously set NO visualScaffold, so
    // the Letters hint got `undefined` and rendered empty.
    // SBX (sound boxes) reads data.word — the target word's letters to
    // decode.
    // PIC (ScaffoldPIC) reads data.imageUrl + data.text + data.word +
    // data.depictionMode.
    // MA-308: controlledSentences is decoding-cloze (mode (a), founder
    // ruling 2026-07-22) — the assessed skill is decoding the letters, not
    // guessing the word from the picture, so depicting the TARGET's own
    // meaning-in-sentence is now the CORRECT scaffold (was previously
    // forbidden everywhere per kernel principle 11 — this is the one named
    // exception). depictionMode:"targetWord" tells ScaffoldPIC to resolve
    // data.word directly instead of scanning data.text for a bystander noun.
    // MA-492: data.imageUrl (the approved reviewed image, when resolved
    // above) takes RENDER PRIORITY in ScaffoldPIC over both the emoji and
    // the SceneIllustration bystander scan — the emoji remains wired below
    // purely as the fallback for words with no approved image and for an
    // approved image that fails to load at render time. The per-word sense
    // adjudication behind the 48 currently-resolvable words lives in
    // scripts/aiImages/csWordImageTargets.mjs, not here.
    // MA-119 (cs-sen-duplicate slice): SEN is withdrawn — `data.text` here
    // is the SAME string as `prompt` above, so a SEN hint was always a
    // verbatim echo of the on-screen prompt (zero new information). `text`
    // stays wired into visualScaffold.data because PIC's own fallback chain
    // still reads it (ScaffoldPIC falls back to SceneIllustration(text) when
    // no target emoji resolves) — only the SEN support code is removed.
    visualScaffold: {
      kind: "reading",
      // MA-308 slice 11: itemId lets ScaffoldPIC's own independent
      // pickTargetEmoji call (the one that actually paints the picture at
      // render time) apply the same per-item exclusion this mapper just
      // applied above — without it, an excluded item could still render
      // the wrong-sense emoji via ScaffoldPIC's unguarded call.
      data: {
        word: item.correctAnswer,
        text: item.sentenceTemplate,
        depictionMode: "targetWord",
        itemId: item.id,
        imageUrl: targetImageUrl,
        // MA-617a: wired unconditionally (undefined when no gloss resolves,
        // or when PIC resolved instead) — ScaffoldSEN reads
        // `data.hintCue ?? data.text`, and codeHasRenderableData's SEN
        // branch already refuses an echo/empty hintCue on its own, so no
        // extra empty-string guard is needed here.
        hintCue: glossHintCue,
      },
    },
    displayMode: "tap_choice",
    launchStatus: item.launchStatus ?? "launch_safe",
  };
}

interface FoundationItem {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  targetSkill: string;
  itemType: string;
  prompt: string;
  correctAnswer: string;
  options?: string[];
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
  /** MA-95: the authored structured stimulus. Foundation-math items carry a
   *  fully-specified visual here (counts/parts/frames) that the mapper
   *  previously DROPPED entirely — every launch_safe ms.math.* foundation
   *  item rendered bare multiple-choice, blank + unanswerable. */
  sourceStimulus_v2?: {
    representation?: string;
    type?: string;
    leftCount?: number;
    rightCount?: number;
    numberBond?: {
      whole?: number;
      knownPart?: number;
      missingPart?: number;
      /** MA-687 — which lower circle holds the known part ("left" | "right"). */
      knownPartSlot?: string;
      /** MA-687 — the authored concrete whole: a five frame with
       *  `filledSlotsForWhole` counters (ms.math.05's FI_CD5/FI_EXP2 records). */
      wholeVisual?: { kind?: string; slotsVisible?: number; filledSlotsForWhole?: number; [k: string]: unknown };
      /** MA-687 — the authored known part: `count` counters. */
      knownPartVisual?: { kind?: string; count?: number; [k: string]: unknown };
      /** MA-687 — the authored missing part: an empty circle, count hidden. */
      missingSlotVisual?: { kind?: string; countHidden?: boolean; [k: string]: unknown };
    };
    twoSetJoin?: { leftSet?: { count?: number }; rightSet?: { count?: number } };
    /** MA-690 — `objectKind` is the authored noun ("stars", "blocks"). */
    countableSet?: { count?: number; objectKind?: string };
    objectSet?: { quantity?: number };
    singleSet?: { count?: number };
    groups?: { left?: { count?: number }; right?: { count?: number } };
    leftFrame?: { filledCount?: number };
    rightFrame?: { filledCount?: number };
    /** MA-C18 — subitizing_pattern payload (dot/dice/frame/finger/… layouts). */
    pattern?: {
      quantity?: number;
      patternType?: string;
      layout?: string;
      positions?: unknown[];
      [k: string]: unknown;
    };
    /** MA-C19 — numeral_glyph / numeral_identification / numeral_to_quantity_match
     *  / numeral_with_quantity_choices payload (read-the-numeral). */
    numeral?: { value?: number; displayedNumeral?: number; [k: string]: unknown };
    /** MA-C19 — drag_numeral_to_quantity payload (rendered as MC per founder D). */
    numeralCard?: { value?: number; [k: string]: unknown };
    /** MA-C20 — two_color_ten_frame (make-ten) payload: ONE part-whole
     *  ten-frame, `shownPart` cells filled in `shownColor`, the rest open
     *  for the missing part (the answer). */
    tenFrame?: {
      frameSize?: number;
      whole?: number;
      shownPart?: number;
      missingPart?: number;
      shownColor?: string;
      missingColor?: string;
      layout?: string;
      [k: string]: unknown;
    };
    details?: { [k: string]: unknown };
    /** MA-686 slice b — the authored scaffold-behavior block; only
     *  `readAloudOnLaunch` is read (see `foundationItemToEngineItem`'s
     *  `readAloudRequired` computation) — everything else here is unused
     *  by this mapper today. */
    scaffold?: { readAloudOnLaunch?: boolean; [k: string]: unknown };
    [k: string]: unknown;
  };
  // formation (trace) extras
  letter?: string;
  case?: string;
  strokes?: string[];
  startPoint?: string;
  commonError?: string;
  // math-foundation (S5.1b) richness
  supportMode?: string[];
}

// Content supportMode → EngineItem SupportCode. Only modes with a real
// renderer affordance map; visualScaffolded / mascotGuided have no SupportCode
// and are dropped (the raw supportMode stays on the content item). Mirrors the
// comprehension/grammar mapper precedent (PIC for picture, SEN for read-aloud).
const SUPPORT_MODE_TO_CODE: Partial<Record<string, SupportCode>> = {
  pictureSupported: "PIC",
  readAloudRequired: "SEN",
};
function supportsFromMode(supportMode?: string[]): SupportCode[] {
  if (!supportMode) return [];
  const codes: SupportCode[] = [];
  for (const m of supportMode) {
    const code = SUPPORT_MODE_TO_CODE[m];
    if (code && !codes.includes(code)) codes.push(code);
  }
  return codes;
}

/** MA-C18 — map a subitizing_pattern's authored layout to a dot_pattern
 *  arrangement FAMILY. dot/dice/domino/array pip layouts → "dice"; frame
 *  glimpses → "frame"; finger/picture/unknown → the generic dot cluster
 *  (count-faithful; a hand/named-object glyph is out of scope — decision-logged).
 *  Answer-safety is family-invariant: the count is always drawn from quantity. */
function arrangementForSubitize(patternType?: unknown): "dice" | "frame" | "generic" {
  const t = typeof patternType === "string" ? patternType : "";
  if (t === "dot_pattern" || t === "dice_face" || t === "domino_pattern" || t === "array_pattern") return "dice";
  if (t === "five_frame" || t === "ten_frame_glimpse") return "frame";
  return "generic"; // finger_pattern, picture_pattern, unknown → generic dots
}

/** MA-C21 — repeat-unit length for each authored `pattern_strip` type. The
 *  "next" element is a POSITIONAL read of the shown sequence at
 *  `shown.length % period` — NOT a naive `unit.length` read, which gives the
 *  wrong period for AAB-type items (verified against all 25 real ms.math.00g
 *  items). */
const PATTERN_STRIP_PERIOD: Record<string, number> = { AB: 2, ABB: 3, AAB: 3, ABC: 3 };

/** MA-95 — foundation-math stimulus→visual dispatch. `foundationItemToEngineItem`
 *  had NO branch reading `sourceStimulus_v2` for math items (only letter/trace
 *  formation), so ALL ~470 launch_safe foundation items whose targetSkill is
 *  ms.math.* rendered bare multiple-choice with no picture — blank +
 *  unanswerable ("How many are there in all?" with nothing to count) — even
 *  though the stimulus carries a fully-specified visual and the authoring
 *  flags say pictureSupportVisible/persistentVisualScaffold. Routes each
 *  representation with an EXISTING renderer/scaffold, fail-closed (the drawn
 *  quantities must reproduce the authored answer/relation, else NO visual):
 *    • two_set_join → `groups` (left + right counters; child counts the join)
 *    • cardinality_count / object_count_set / single_set_count → `ten_frame`
 *      (≤10) or `groups` — count-what's-shown, answer = the drawn count
 *    • number_bond → the answer-safe NB missing_addend SCAFFOLD (whole +
 *      known part + "?" — never draws the missing part; the ADR-024 pattern)
 *    • two_object_groups / comparison_pair → the CMP compare scaffold (two
 *      labeled magnitudes; the child derives which side — prompt-aware:
 *      "fewer/less" inverts the expected side)
 *    • two_ten_frames_side_by_side → the CMPT scaffold (MA-663 round 3):
 *      SAME relation logic as CMP above, but drawn as two mini ten frames
 *      (matching the prompt's own "Which frame shows more?"), not bars
 *    • three_object_groups → the CMP3 compare scaffold (three labeled
 *      magnitudes; answer is the strict-max position — MA-113 slice)
 *  Slices that still need NEW renderers (shape/pattern MA-C21 covers those)
 *  return nothing here and stay honestly grandfathered in the MA-96
 *  baseline. */
/** MA-595 families-b — the spoken counting cue (one string, every counting
 *  "how many?" foundation picture). Names no number and draws nothing, so it
 *  can neither leak the count nor repaint the picture. A spoken string: it
 *  is in the narration practice denominator (narratableStrings.mjs walks
 *  `hintCue`). */
export const COUNT_ALL_HINT_CUE = "Point to each thing as you count it. The last number you say tells how many.";

/** MA-595 families-b — the counting cue's prompt rule: a "how many" question
 *  that does not already tell the child to count (those records carry the
 *  instruction in the prompt itself, so the cue would only repeat it). */
export function isCountCuePrompt(prompt: unknown): boolean {
  const p = String(prompt ?? "");
  return /\bhow many\b/i.test(p) && !/\bcount\b/i.test(p);
}

function foundationMathVisual(item: FoundationItem): {
  mathVisual?: MappedEngineItem["mathVisual"];
  visualScaffold?: MappedEngineItem["visualScaffold"];
  supports: SupportCode[];
  visualRequired: boolean;
} {
  const none = { supports: [] as SupportCode[], visualRequired: false };
  if (!/^ms\.math\./.test(item.targetSkill ?? "")) return none;
  // MA-807 (RED SHOULD-5) — the hint rung a mathExemplars item of the SAME
  // model carries (supportsForModel/scaffoldDataForModel, the ME mapper's own
  // ladder): F5 for a count up to 5, F5/NL/NB for a join up to 5, etc. Used
  // by the three representations MA-807 routed (object_count_set,
  // subitizing_pattern, two_set_join — no other launch_safe record authors
  // them); HintSlot's inline-visual filter and MA-769's stem rule still
  // decide what is offered next to the picture on screen.
  const ladderFor = (model: ScaffoldModel) => ({
    supports: supportsForModel(model),
    visualScaffold: { kind: model.mode, data: scaffoldDataForModel(model) as unknown as Record<string, unknown> },
  });
  const ss2 = item.sourceStimulus_v2;
  if (!ss2) return none;
  const rep = ss2.representation ?? ss2.type ?? "";
  const ans = Number(item.correctAnswer);
  const num = (v: unknown): number | null =>
    Number.isFinite(Number(v)) ? Number(v) : null;

  // two_set_join → groups (a + b counters; the join IS the question).
  if (rep === "two_set_join" && ss2.twoSetJoin) {
    const l = num(ss2.twoSetJoin.leftSet?.count);
    const r = num(ss2.twoSetJoin.rightSet?.count);
    if (l != null && r != null && l >= 0 && r >= 0 && l + r > 0 && l + r === ans) {
      return {
        mathVisual: { kind: "groups", a: l, b: r, op: "+", total: l + r },
        ...ladderFor({ mode: "add", a: l, b: r, whole: l + r, impliedAnswer: l + r, source: "stimulus" }),
        visualRequired: true, // the prompt has no operands — the picture IS the question
      };
    }
    return none;
  }

  // MA-807 — object_count_set (ms.math.00a's FI_CNT_00a counting records):
  // the same ten frame as the counting sets below, plus the count model's
  // mathExemplars ladder (RED round 1 SHOULD-5). Kept a separate branch so
  // the cardinality_count / single_set_count branch below (ms.math.02's
  // coach-ineligible shape, pinned by coachShapePin) is untouched.
  if (rep === "object_count_set") {
    const q = num(ss2.objectSet?.quantity);
    if (q != null && q > 0 && q === ans) {
      return {
        mathVisual: { kind: q <= 10 ? "ten_frame" : "groups", a: q, b: 0, op: "+", total: q },
        ...ladderFor({ mode: "count", a: q, b: 0, whole: q, impliedAnswer: q, source: "stimulus" }),
        visualRequired: true,
      };
    }
    return none;
  }

  // counting sets → ten_frame (≤10) / groups; the drawn count IS the answer
  // (the sanctioned count-what's-shown pattern, ADR-012).
  const countN =
    rep === "cardinality_count" ? num(ss2.countableSet?.count)
    : rep === "single_set_count" ? num(ss2.singleSet?.count)
    : null;
  if (countN != null) {
    // MA-818 — a cardinality_count record keyed 0 with count 0 draws an EMPTY
    // ten frame (K.CC.A.3: "0 representing a count of no objects"; ms.math.04
    // "Which number shows how many dots?" / "Tap the number zero."). No count
    // cue: "Point to each thing as you count it" has nothing to point at, so
    // the record keeps `supports: []`. single_set_count keeps count > 0 (no
    // record authors 0 there). audit:served-answerability admits this empty
    // frame only for a K.CC.A.3 item keyed 0 (isZeroCountFrame).
    if (
      rep === "cardinality_count" && String(ss2.countableSet?.count ?? "").trim() === "0" &&
      String(item.correctAnswer ?? "").trim() === "0"
    ) {
      return {
        mathVisual: { kind: "ten_frame", a: 0, b: 0, op: "+", total: 0 },
        supports: [],
        visualRequired: true,
      };
    }
    if (countN > 0 && countN === ans) {
      // MA-690 — draw the noun the prompt names ("How many stars in all?")
      // from the authored `countableSet.objectKind`, via the same noun table
      // as detectCountGlyph. A noun with no glyph keeps the byte-identical
      // glyph-free payload (the generic circle).
      // RED SHOULD-3: and only when the prompt itself names that noun.
      const glyph = rep === "cardinality_count"
        ? glyphNamedByPrompt(glyphForNoun(ss2.countableSet?.objectKind), item.prompt)
        : undefined;
      // MA-595 families-b — the counting cue. The picture IS the count, so
      // every structural rung either repaints it (T10/F5 of the same count:
      // MA-769 drops those) or prints the answer (NL/NLMA/NB label it). The
      // one help that names no number is HOW to count: a spoken process cue
      // (one-to-one + the last number tells how many), served as SEN with
      // `hintCue` (ScaffoldSEN speaks it). Only for a "how many" question
      // whose prompt does not already give a counting instruction; every
      // other record here keeps `supports: []`. No a/b/op in the data, so the
      // apple coach's shape test (coachShapePin) is unchanged.
      const countCue = isCountCuePrompt(item.prompt);
      return {
        mathVisual: {
          kind: countN <= 10 ? "ten_frame" : "groups",
          a: countN, b: 0, op: "+", total: countN,
          ...(glyph ? { glyph } : {}),
        },
        supports: countCue ? ["SEN"] : [],
        ...(countCue ? { visualScaffold: { kind: "count_cue", data: { hintCue: COUNT_ALL_HINT_CUE } } } : {}),
        visualRequired: true,
      };
    }
    return none;
  }

  // MA-C18 — subitizing patterns → the dot_pattern renderer (dice / five-frame
  // / generic dot cluster). The drawn dot count IS the answer (count-what's-
  // shown, ADR-012). Fail-closed: quantity a positive integer === the answer,
  // and (when authored) positions.length === quantity. visualRequired — the
  // prompt has nothing to count without the picture.
  if (rep === "subitizing_pattern" && ss2.pattern) {
    const q = num(ss2.pattern.quantity);
    const positions = Array.isArray(ss2.pattern.positions) ? ss2.pattern.positions : null;
    if (q != null && q > 0 && q === ans && (positions === null || positions.length === q)) {
      const arrangement = arrangementForSubitize(ss2.pattern.patternType);
      return {
        mathVisual: { kind: "dot_pattern", a: q, b: 0, total: q, arrangement },
        ...ladderFor({ mode: "count", a: q, b: 0, whole: q, impliedAnswer: q, source: "stimulus" }),
        visualRequired: true,
      };
    }
    return none;
  }

  // number_bond → the answer-safe `number_bond` mathVisual (whole + known
  // part + "?"), shown by default, plus the NB scaffold as the hint rung.
  // Same fail-closed + degenerate rules as partWholeFromStimulus: known ≤ 0
  // or known ≥ whole would make the shown whole reveal the answer — no visual.
  //
  // MA-687 — these 80 ms.math.05 records (FI_CD5_*, FI_EXP2_*) were served
  // with the bond ONLY behind a hint tap (`visualRequired: false`), so a
  // fresh learner saw "What part is missing?" over nothing. The prompt names
  // no number: the picture IS the question, so it is `visualRequired`. The
  // authored model is concrete — a five frame holding the whole, counters for
  // the known part, an empty circle for the missing part — and is drawn as
  // authored (`arrangement: "concrete"` / `"concrete_known_right"`, which
  // side holds the known part) when it is self-consistent: a 5-slot five
  // frame whose filled slots equal the whole (≤ 5), known-part counters equal
  // to the known part, and a missing slot whose count is hidden. Otherwise the
  // numeral bond (no arrangement) is drawn. Either way the renderer reads
  // only `a` (known) and `total` (whole), never `b`: the missing part is
  // never drawn. On MathSession the NB hint (the numeral bond) is dropped
  // while this bond is on screen (HintSlot's number_bond arm, MA-769).
  if (rep === "number_bond" && ss2.numberBond) {
    const nb = ss2.numberBond;
    const known = num(nb.knownPart);
    const whole = num(nb.whole);
    const missing = num(nb.missingPart);
    if (
      known != null && whole != null && missing != null &&
      known > 0 && known < whole && known + missing === whole && missing === ans
    ) {
      const wv = nb.wholeVisual;
      const kv = nb.knownPartVisual;
      const concrete =
        wv?.kind === "five_frame" && num(wv.slotsVisible) === 5 &&
        num(wv.filledSlotsForWhole) === whole && whole <= 5 &&
        num(kv?.count) === known &&
        nb.missingSlotVisual?.countHidden === true;
      const arrangement = concrete
        ? (nb.knownPartSlot === "right" ? "concrete_known_right" : "concrete")
        : undefined;
      return {
        mathVisual: {
          kind: "number_bond", a: known, b: missing, op: "+", total: whole,
          ...(arrangement ? { arrangement } : {}),
        },
        visualScaffold: {
          kind: "missing_addend",
          // MA-687 RED round 2 SHOULD-1 — the NB hint draws "?" on the same
          // side as the concrete stem: a known part on the RIGHT puts the
          // missing part on the LEFT (MA-769's blank-left bond).
          data: {
            mode: "missing_addend", a: known, b: missing, op: "+", whole, missing,
            ...(arrangement === "concrete_known_right" ? { blankSide: "left" as const } : {}),
          },
        },
        supports: ["NB"],
        visualRequired: true,
      };
    }
    return none;
  }

  // comparisons → CMP scaffold (or, for two_ten_frames_side_by_side, the
  // CMPT ten-frames-side-by-side scaffold — MA-663 round 3, see below).
  // Fail-closed on the RELATION: the drawn pair must make the authored
  // answer derivable under the prompt's direction ("fewer/less" asks for
  // the SMALLER side).
  let left: number | null = null;
  let right: number | null = null;
  // MA-663 round 3 (RED round 2 must-fix 4) — two_ten_frames_side_by_side
  // prompts "Which frame shows more?" over an authored PAIR OF TEN FRAMES,
  // not two unlabeled magnitudes. It shares this branch's relationHolds
  // check (the relation math is identical) but returns a DIFFERENT visual
  // kind below, so the rendered picture matches what the prompt names —
  // round 2 routed it into the same bar-chart `compare` kind as the other
  // two reps, which answered correctly but drew bars for a prompt that
  // named frames (RED round 2, live-confirmed).
  const isTenFrameCompare = rep === "two_ten_frames_side_by_side";
  if (rep === "two_object_groups" && ss2.groups) {
    left = num(ss2.groups.left?.count);
    right = num(ss2.groups.right?.count);
  } else if (rep === "comparison_pair" || ss2.type === "comparison_pair") {
    left = num(ss2.leftCount);
    right = num(ss2.rightCount);
  } else if (isTenFrameCompare) {
    left = num(ss2.leftFrame?.filledCount);
    right = num(ss2.rightFrame?.filledCount);
  }
  if (left != null && right != null) {
    const answer = String(item.correctAnswer ?? "").toLowerCase();
    const fewer = /\b(fewer|less|least|small(?:er|est)?)\b/i.test(String(item.prompt ?? ""));
    // Three authored answer families: yes/no ("Are the two groups equal?"),
    // same/equal, and left/right (incl. "left group"). Fail-closed: any
    // answer shape this can't verify gets no visual.
    // RED-3 (§3b panel): the yes/no arm is only meaningful for an EQUALITY
    // question — a future non-equality yes/no prompt must fail-close, not
    // pass on "the groups happen to be unequal".
    const isEqualityPrompt = /\b(equal|same)\b/i.test(String(item.prompt ?? ""));
    const relationHolds = /^(yes|no)$/.test(answer)
      ? isEqualityPrompt && (left === right) === (answer === "yes")
      : left === right
        ? /^(same|equal)/.test(answer)
        : (fewer ? left < right : left > right)
          ? /^left/.test(answer)
          : /^right/.test(answer);
    if (relationHolds) {
      return {
        visualScaffold: isTenFrameCompare
          ? {
              // MA-663 round 3 (RED round 2 must-fix 4) — two mini ten
              // frames side by side (ScaffoldCMPT), matching the prompt's
              // own "Which frame shows more?" naming, instead of bars.
              kind: "compare_ten_frames",
              data: { a: left, b: right, aLabel: "Left", bLabel: "Right" },
            }
          : {
              kind: "compare",
              // RED-1 (§3b panel): the answers are SIDE words ("left group") but
              // ScaffoldCMP stacks its bars top/bottom — label them so the child
              // can map bar → side (the label is the side name, never the answer
              // by itself: the child still compares the magnitudes).
              data: {
                mode: "compare", a: left, b: right, op: "+",
                whole: Math.max(left, right),
                aLabel: "Left", bLabel: "Right",
              },
            },
        supports: isTenFrameCompare ? ["CMPT"] : ["CMP"],
        // MA-663: visualRequired, not scaffold-only-hidden-by-default. Every
        // authored ms.math.03 comparison prompt ("Which group has more?")
        // carries ZERO operands in the prompt text itself — unlike a
        // mathExemplars text comparison ("Which group has more: 4 shells or
        // 3 shells?"), the picture IS the question, the same class as
        // two_set_join/counting/subitizing above.
        //
        // MA-663 round 2 (⭐M1 correction): round 1's comment here claimed
        // "Foundation always shows its scaffold regardless of this flag
        // (persistentVisualScaffold), so this is a no-op there" — FALSE and
        // UNVERIFIED at the time it was written. `persistentVisualScaffold`
        // was read by zero lines of `src/`; FoundationSession mounted no
        // inline renderer for `visualScaffold.kind==="compare"`, only a
        // closed-by-default HintSlot behind "Show hint" — these 60 items
        // served as unanswerable blank slots on Foundation too (RED
        // round 2, caught before merge). Fixed in the SAME round:
        // FoundationSession.tsx now renders ScaffoldCMP/ScaffoldCMP3 INLINE,
        // above the prompt, always visible (the actual persistent-scaffold
        // behavior this comment always claimed) — see FoundationSession.tsx's
        // own comment at that render site. The claim is verified now, not
        // asserted: servedFormDescribe reports pictureShownByDefault:true
        // for all 60 (scripts/audit/servedFormDescribe.mjs).
        // This flag still matters for any OTHER surface that might pull this
        // skill's union: MixedSession's isMixedServableMathItem correctly
        // excludes these (unreachable outside Foundation before this item,
        // so that exposure was latent, never live).
        //
        // MA-663 round 3 (RED round 2 ⭐must-fix 1 correction): this flag is
        // now load-bearing for a SECOND reason beyond the mixed-servable
        // gate above — FoundationSession.tsx's inline render gates on
        // `item.visualRequired` specifically (not on visualScaffold.kind
        // alone), because ms.math.03's OWN mathExemplars TEXT items (the 55
        // siblings, mapped by mathExemplarToEngineItem, not this function)
        // ALSO emit `visualScaffold.kind: "compare"` for their own hint-only
        // CMP scaffold, with visualRequired staying false there. Round 2
        // gated FoundationSession's render on kind alone and silently made
        // those 55 always-on too (undisclosed regression, caught by RED
        // round 2) — this flag is the ONE signal that correctly tells
        // FoundationSession which of the two "compare"-kind populations it
        // is looking at.
        // two_object_groups/comparison_pair/two_ten_frames_side_by_side are
        // authored ONLY for ms.math.03 today (verified over the real
        // corpus) — this is not a routing change for any other skill.
        visualRequired: true,
      };
    }
    return none;
  }

  // MA-113 slice — three_object_groups → CMP3 scaffold ("Which group has
  // the most?", ms.math.03). The 3-way sibling of the two_object_groups CMP
  // block above; kept as its own branch rather than generalizing that one,
  // since the answer/relation shape (a single strict-max POSITION out of
  // three, not a two-way side/equal/yes-no relation) doesn't compose with
  // the existing relationHolds logic. Fail-closed: every count a positive
  // integer, the max strictly unique (a future authored tie must render
  // nothing rather than guess), and the authored answer names exactly that
  // position.
  if (rep === "three_object_groups" && ss2.groups) {
    const g = ss2.groups as { left?: { count?: unknown }; middle?: { count?: unknown }; right?: { count?: unknown } };
    const l = num(g.left?.count);
    const m = num(g.middle?.count);
    const r = num(g.right?.count);
    if (l != null && m != null && r != null && l > 0 && m > 0 && r > 0) {
      const counts: Record<string, number> = { left: l, middle: m, right: r };
      const maxVal = Math.max(l, m, r);
      const maxPositions = Object.keys(counts).filter((k) => counts[k] === maxVal);
      const answer = String(item.correctAnswer ?? "").toLowerCase();
      if (maxPositions.length === 1 && maxPositions[0] === answer) {
        return {
          visualScaffold: {
            kind: "compare3",
            data: {
              mode: "compare3", left: l, middle: m, right: r, whole: maxVal,
              leftLabel: "Left", middleLabel: "Middle", rightLabel: "Right",
            },
          },
          supports: ["CMP3"],
          // MA-663: same visualRequired correction as the two-way `compare`
          // branch above — "Which group has the most?" carries no operands
          // in the prompt text; the picture IS the question.
          visualRequired: true,
        };
      }
    }
    return none;
  }

  // MA-C19 — numeral card/glyph items ("Which number is this?" / "What
  // number do you see?" / "Tap the picture that matches the number" / "Match
  // the card to the right group of dots" / "Drag the number to the matching
  // group"). Five representations share ONE shape: the authored numeral is
  // the question (read-the-numeral, the same "the visual IS the question"
  // class as read-the-clock/read-the-blocks) — showing it is faithful, not a
  // leak. The item's options/correctAnswer are already plain numeral-text
  // choices served fine by the existing multiple-choice tiles regardless of
  // prompt wording; the authored quantityChoices/answerOptions/targetQuantities
  // dot-picture metadata these three carry has no reader anywhere in the app
  // today (repo-wide search confirms it), so serving numeral-to-numeral match
  // is not an answer-safety regression, just a prompt-fidelity simplification
  // (a real dot-quantity option renderer is a separate future item).
  // drag_numeral_to_quantity is itemType drag_match, rendered as MC per the
  // founder-D precedent above (foundationItemToEngineItem has no drag UI).
  // Fail-closed: the displayed numeral must be a non-negative integer that
  // equals Number(correctAnswer) exactly.
  //
  // MA-654 slice b (2026-09-14): displayedNumeral/value === 0 is now
  // ADMITTED (n >= 0, not n > 0) for numeral_identification and
  // numeral_to_quantity_match — the two representations the real corpus
  // actually authors at n=0 (FI_NUM_00e_001/002/003/019/020). "0" is a
  // legitimate numeral card (NumeralCardRenderer already draws it fine —
  // `safeNumeral` accepts n>=0). The item's authored `answerOptions`/
  // `optionLayouts` dot-group metadata (e.g. FI_NUM_00e_019/020's
  // `optionLayouts["0"] === "empty_circle"`) is NOT part of this fix's
  // justification — RED round 1 confirmed nothing in the repo (no renderer,
  // no consumer) reads `answerOptions`/`optionLayouts` today; it is
  // authored-but-unrendered content, a pre-existing gap in the whole
  // `numeral_to_quantity_match` family (filed as a follow-up, not fixed
  // here — see the nit below). The prior n>0 gate treated 0 as
  // falsy/absent rather than as a valid drawn numeral, so these 5 items
  // fell through to `none` — visualRequired stayed false and, verified
  // against master with `servedFormDescribe`, they rendered NO visual
  // anywhere: not just on /play/mixed (MA-149's unanswerable-prompt
  // class), but on MathSession too (ms.math.00e is strand `math`, so
  // `journeyTap` routes it to `/play/session`, not `/play/foundation` —
  // `FoundationSession.tsx` never even sees these items). See
  // scripts/audit/mixedServableOperandSource.mjs's baseline, now empty for
  // these 5 ids.
  //
  // Nit (RED round 1, filed not fixed): the served form these 5 now share
  // with siblings `FI_NUM_00e_004..021` is digit-tile matching ("find the
  // tile whose glyph matches the card"), not numeral↔quantity mapping —
  // the prompt says "group of dots" but the options are digits `0 1 2 3`,
  // and for the zero case specifically "0 means no dots" is never shown
  // (the `empty_circle` layout above being unrendered). Pre-existing
  // across the whole `numeral_to_quantity_match` family, not introduced or
  // worsened here; this slice achieves parity with the 15 already-serving
  // siblings, not a pedagogy fix.
  //
  // This DOES emit a mathVisual with a===0 (drawn=0), which is why
  // sweepFoundation's "foundation visual has drawable objects" (drawn>0)
  // invariant (scripts/audit/mathContentSweepTest.mjs) now carries an
  // explicit numeral_card exemption alongside its existing CATEGORICAL_KINDS
  // carve-out — a "0" card draws a glyph, not a count of counters, so the
  // drawn-count>0 assumption never applied to this kind in the first place.
  // numeral_glyph / numeral_with_quantity_choices / drag_numeral_to_quantity
  // have NO real n=0 items in the corpus today (repo-wide check); left at
  // n>0 rather than widened speculatively — narrow the gate to exactly the
  // representations with proven n=0 content, per this repo's fail-closed
  // convention.
  if ((rep === "numeral_glyph" || rep === "numeral_with_quantity_choices") && ss2.numeral) {
    const n = num(ss2.numeral.value);
    if (n != null && n > 0 && n === ans) {
      return { mathVisual: { kind: "numeral_card", a: n, b: 0, total: n }, supports: [], visualRequired: true };
    }
    return none;
  }
  if ((rep === "numeral_identification" || rep === "numeral_to_quantity_match") && ss2.numeral) {
    const n = num(ss2.numeral.displayedNumeral);
    if (n != null && n >= 0 && n === ans) {
      return { mathVisual: { kind: "numeral_card", a: n, b: 0, total: n }, supports: [], visualRequired: true };
    }
    return none;
  }
  if (rep === "drag_numeral_to_quantity" && ss2.numeralCard) {
    const n = num(ss2.numeralCard.value);
    if (n != null && n > 0 && n === ans) {
      return { mathVisual: { kind: "numeral_card", a: n, b: 0, total: n }, supports: [], visualRequired: true };
    }
    return none;
  }

  // MA-C20 — two_color_ten_frame make-ten items ("What part is missing?" /
  // "Drag the missing part to the frame."). ONE part-whole ten-frame — draw
  // exactly `shownPart` cells filled in the authored color, leave
  // `frameSize - shownPart` open for the missing part (the answer). Distinct
  // from MA-C10's `two_ten_frames_missing_addend` (TWO separate frames).
  // Fail-closed: shownPart/missingPart positive, sum to whole===frameSize,
  // missingPart===ans. The always-on visual additionally requires
  // shownPart!==ans — that clause excludes a SYMMETRIC split
  // (shownPart===missingPart, e.g. 5/5 of 10): there the FILLED-cell count
  // coincidentally equals the answer, which would leak it in an UNBIDDEN,
  // default-shown picture even though the renderer faithfully draws
  // shownPart, not "the answer" as a concept.
  //
  // REWRITE: MA-643 (2026-09-13) — the corpus's 4 real symmetric 5/5 items
  // (FI_CD10_005/016/023/029) were RE-SPLIT to asymmetric splits (6/4, 7/3,
  // 4/6, 3/7) so the frame renders by default on first paint instead of
  // being gated behind a hint — the child outcome the arbiter chose. The
  // corpus therefore has ZERO shownPart===missingPart two_color_ten_frame
  // items today. This symmetric arm and MA-595(a)'s hint below are KEPT as
  // a defensive branch (fail-safe for any future authored symmetric split,
  // not dead code to delete) but have no live real-corpus subscriber; the
  // symmetric→T10 contract is exercised by synthetic fixtures in
  // newRenderers.test.mjs and scaffoldContractTest.mjs instead of real ids.
  //
  // MA-595(a) — a symmetric split previously fell through to NOTHING: no
  // always-on visual AND no hint (supportMode SEN/PIC codes carry no
  // scaffold data, so the MA-483 render gate suppressed the button — the
  // Lane C "gate-suppressed" class). MA-C20's always-on carve-out is
  // UNCHANGED; a symmetric split instead emits a revealable COMPLETE-THE-
  // FRAME hint: a T10 ten-frame pre-filled with the KNOWN part only
  // (pre-fill = shownPart, sourced from the known part, never from the
  // missing part), the missing part's cells left OPEN for the child to
  // complete — that completion IS the make-ten pedagogy. The scaffold
  // renders no numeral anywhere, so the answer is never displayed; the
  // filled-cell count equals the GIVEN known part (for a symmetric split
  // that count coincides with the answer — the same given-operand
  // coincidence class scaffoldContractTest's NL guard exempts via
  // isGivenCoincidence: restating a given is not a fresh reveal). Gated to
  // whole === frameSize === 10 so ScaffoldT10's missing_addend branch draws
  // exactly `whole` cells and the open-cell count is exactly the missing
  // part (never padded by beyond-the-whole cells a child could miscount).
  if (rep === "two_color_ten_frame" && ss2.tenFrame) {
    const tf = ss2.tenFrame;
    const shownPart = num(tf.shownPart);
    const missingPart = num(tf.missingPart);
    const whole = num(tf.whole);
    const frameSize = num(tf.frameSize);
    if (shownPart == null || missingPart == null || whole == null || frameSize == null) return none;
    const frameFaithful =
      shownPart > 0 && missingPart > 0 &&
      shownPart + missingPart === whole && whole === frameSize && missingPart === ans;
    if (frameFaithful && shownPart !== ans) {
      return {
        mathVisual: {
          kind: "two_color_ten_frame", a: shownPart, b: 0, total: frameSize,
          color: typeof tf.shownColor === "string" ? tf.shownColor : undefined,
        },
        supports: [],
        visualRequired: true, // the prompt has no operands — the picture IS the question
      };
    }
    if (frameFaithful && shownPart === ans && frameSize === 10) {
      // MA-595(a): symmetric split — T10 complete-the-frame HINT (see block
      // comment above). shownPart === ans together with missingPart === ans
      // implies shownPart === missingPart, so this arm is exactly the
      // MA-C20 symmetric carve-out and nothing else.
      return {
        visualScaffold: {
          kind: "missing_addend",
          data: { mode: "missing_addend", a: shownPart, b: missingPart, op: "+", whole, missing: missingPart },
        },
        supports: ["T10"],
        visualRequired: false, // scaffold-only; default-visibility is MA-116
      };
    }
    return none;
  }

  // MA-C21 — shape_array (ms.math.00f "Which shape is on the card?") + pattern_strip
  // (ms.math.00g "What comes next?") pre-chapter identification items. Both are
  // CATEGORICAL "the visual IS the question" visuals with a non-numeric answer —
  // `total` is a fixed sentinel (shape_array: 1) or len(sequence) (pattern_strip),
  // never a drawn count (renderabilityAudit's shape_array/pattern_strip contracts,
  // MA-216). Fail-closed against the authored details.
  if (rep === "shape_array" && ss2.details) {
    const shape = typeof ss2.details.targetShape === "string" ? ss2.details.targetShape : null;
    const options = Array.isArray(ss2.details.options) ? ss2.details.options : [];
    if (shape != null && shape === item.correctAnswer && options.includes(shape)) {
      return {
        mathVisual: {
          kind: "shape_array", total: 1, shape,
          color: typeof ss2.details.targetColor === "string" ? ss2.details.targetColor : undefined,
          displayStyle: typeof ss2.details.displayStyle === "string" ? ss2.details.displayStyle : undefined,
        },
        supports: [],
        visualRequired: true, // the prompt has no shape name — the picture IS the question
      };
    }
    return none;
  }
  if (rep === "pattern_strip" && ss2.details) {
    const shown = Array.isArray(ss2.details.shown)
      ? ss2.details.shown.filter((s): s is string => typeof s === "string" && s.length > 0)
      : [];
    const patternType = typeof ss2.details.patternType === "string" ? ss2.details.patternType : "";
    const period = PATTERN_STRIP_PERIOD[patternType];
    if (
      shown.length > 0 &&
      Array.isArray(ss2.details.shown) && shown.length === ss2.details.shown.length &&
      period != null && shown[shown.length % period] === item.correctAnswer
    ) {
      // MA-691 (A3) — the PAT hint rung (ScaffoldPAT: the strip redrawn in
      // boxed repeat units with the open "?" slot). Fail-closed: emitted only
      // when the shown strip is EXACTLY periodic with the authored period
      // (every piece equals the piece one unit earlier), so the boxes are the
      // true repeat units; otherwise no rung, as before. The picture itself
      // is unchanged either way.
      const periodic = shown.every((s, i) => s === shown[i % period]);
      return {
        mathVisual: { kind: "pattern_strip", total: shown.length, sequence: shown },
        ...(periodic
          ? { visualScaffold: { kind: "pattern_unit", data: { sequence: [...shown], period } } }
          : {}),
        supports: periodic ? ["PAT"] : [],
        visualRequired: true, // the prompt has no sequence text — the picture IS the question
      };
    }
    return none;
  }

  return none; // (MA-C18 subitizing, MA-C19 numeral, MA-C20 ten-frame, MA-C21 shape/pattern, MA-113-slice three_object_groups now emit above)
}

/** Foundation item → EngineItem.
 *  - letter MC (identifyNamedLetter / caseMatch / identifyLetterForSound) +
 *    math-foundation (multiple_choice / drag_match / tap_count) → multiple_choice.
 *    drag_match has no drag UI in FoundationSession (founder D: render as MC).
 *  - traceLowercase / traceUppercase → trace (HandwritingPad-as-primary;
 *    visualScaffold carries the formation strokes for the guide).
 *  - traceNumeral (MA-208(b)) → trace, visualScaffold.kind "traceTarget"
 *    (numeral-tracing surface — FoundationSession renders <TraceItem>, not
 *    the formation pad). No authored content uses this itemType yet. */
/** MA-702 — read the authored per-option dot-group spec off a foundation
 *  numeral-to-quantity record. Two authored shapes, one reader:
 *    • `answerOptions: {kind: "dot_groups", optionLayouts: {"<n>": layout}}`
 *      (ms.math.00e, FI_NUM_00e_019-030 — one layout per option value);
 *    • `quantityChoices: {elementKind: "dots", arrangement: layout}` (dark
 *      ms.math.00c/00d records — one arrangement for every option).
 *  Fail-closed, all-or-nothing: returns undefined unless EVERY option is a
 *  plain non-negative integer string whose layout draws exactly that many
 *  dots (dotGroupLayoutAdmits), the counts are pairwise distinct, and the
 *  item's numeral card (`numeralCard`, the mapper's own emitted
 *  numeral_card visual) shows the correct answer — so the correct tile's
 *  dots always equal the card's numeral. A partial set (some tiles dots,
 *  some digits) is never emitted. Pure. */
function optionDotGroupsFor(
  item: FoundationItem,
  numeralCard: MappedEngineItem["mathVisual"] | undefined,
): Record<string, OptionDotGroup> | undefined {
  const ss2 = item.sourceStimulus_v2;
  if (!ss2) return undefined;
  const answerOptions = ss2.answerOptions as
    | { kind?: unknown; optionLayouts?: Record<string, unknown> }
    | undefined;
  const quantityChoices = ss2.quantityChoices as
    | { elementKind?: unknown; arrangement?: unknown }
    | undefined;
  let layoutFor: ((value: string) => unknown) | null = null;
  if (answerOptions?.kind === "dot_groups" && answerOptions.optionLayouts && typeof answerOptions.optionLayouts === "object") {
    const layouts = answerOptions.optionLayouts;
    layoutFor = (value) => (Object.prototype.hasOwnProperty.call(layouts, value) ? layouts[value] : undefined);
  } else if (quantityChoices?.elementKind === "dots") {
    layoutFor = () => quantityChoices.arrangement;
  }
  if (!layoutFor) return undefined;
  if (numeralCard?.kind !== "numeral_card") return undefined;
  if (String(numeralCard.total) !== item.correctAnswer) return undefined;
  const options = item.options ?? [];
  if (options.length === 0 || !options.includes(item.correctAnswer)) return undefined;
  const out: Record<string, OptionDotGroup> = {};
  const seenCounts = new Set<number>();
  for (const value of options) {
    if (!/^\d+$/.test(value)) return undefined;
    const count = Number(value);
    const layout = layoutFor(value);
    if (!dotGroupLayoutAdmits(layout, count)) return undefined;
    if (seenCounts.has(count)) return undefined;
    seenCounts.add(count);
    out[value] = { count, layout };
  }
  return out;
}

/** MA-692 — a foundation-MATH record may author the same wrong-answer
 *  diagnostics a mathExemplar does (`misconceptionCategory` +
 *  `diagnosticMeaningByWrongAnswer`); MathSession forwards them to
 *  buildAttempt/classifyMathError exactly as for mathExemplars. Threaded only
 *  for `ms.math.*` records and only when well-typed, so every record that does
 *  not author them maps byte-identically (the key is ABSENT, not undefined). */
function foundationMathDiagnostics(item: FoundationItem): {
  misconceptionCategory?: string;
  diagnosticMeaningByWrongAnswer?: Record<string, string>;
} {
  const rec = item as unknown as Record<string, unknown>;
  if (!String(rec.targetSkill ?? "").startsWith("ms.math.")) return {};
  const category = typeof rec.misconceptionCategory === "string" && rec.misconceptionCategory !== ""
    ? rec.misconceptionCategory
    : undefined;
  const raw = rec.diagnosticMeaningByWrongAnswer;
  const meanings =
    raw && typeof raw === "object" && !Array.isArray(raw) &&
    Object.values(raw as Record<string, unknown>).every((v) => typeof v === "string")
      ? (raw as Record<string, string>)
      : undefined;
  return {
    ...(category ? { misconceptionCategory: category } : {}),
    ...(meanings ? { diagnosticMeaningByWrongAnswer: meanings } : {}),
  };
}

/** MA-840 slice 2a — every caller (the dispatcher, the audits, the
 *  no-hint ledger guard) gets the family spoken cue (mathHintCue.ts). */
export function foundationItemToEngineItem(item: FoundationItem, microSkillId: string): MappedEngineItem {
  return withMathHintCue(foundationItemToEngineItemUncued(item, microSkillId), microSkillId);
}

/** MA-840 slice 2a — exported for the cue tests only (the pre-cue mapping). */
export function foundationItemToEngineItemUncued(
  item: FoundationItem,
  microSkillId: string,
): MappedEngineItem {
  const isTrace = item.itemType.startsWith("trace");
  const isTraceTarget = item.itemType === "traceNumeral"; // MA-208(b)
  const base = {
    id: item.id,
    prompt: item.prompt,
    answer: item.correctAnswer,
    microSkillId,
    ageBand: item.ageBand ?? (isTrace ? "5.0-5.5" : "4.5-5.0"),
    difficulty: item.difficulty ?? 1,
    explanation: "",
    launchStatus: item.launchStatus ?? "expert_review_needed",
    // ⭐MA-631 §3a — same rule as mathExemplarToEngineItem, for symmetry and
    // future authoring. INTENTIONALLY DEAD today: foundationItems carries
    // sourceStimulus_v2 on 585 items, never type "narrative" (asserted over
    // the real corpus in contentToEngineItem.test.mjs) — this arm can never
    // fire against the current pack.
    evidenceItemType:
      item.sourceStimulus_v2?.type === "narrative" ? ("wordProblem" as const) : undefined,
    // MA-652 RED round 1 — foundationItemToEngineItem has no arithmetic-
    // model concept (never sets `operandModelSource`), so `model` is always
    // null here: the served-prompt rule reduces to "does the prompt contain
    // a digit". Same helper as mathExemplarToEngineItem — one definition,
    // not a parallel reimplementation for this mapper.
    promptOperandSource: computePromptOperandSource(item.prompt, null),
    // MA-686 slice b — threads the authored read-aloud requirement through
    // (previously dropped entirely — see the MA-686 BACKLOG block). Either
    // authored signal is sufficient: `supportMode` carries the coarse flag
    // on every currently-authored occurrence (real-corpus verified), while
    // `sourceStimulus_v2.scaffold.readAloudOnLaunch` is the more granular
    // per-stimulus signal — an item could in principle author one without
    // the other, so both are checked. Additive-only: no other field here
    // changes because of this line.
    readAloudRequired:
      (item.supportMode?.includes("readAloudRequired") ?? false) ||
      item.sourceStimulus_v2?.scaffold?.readAloudOnLaunch === true,
    // MA-671 slice 2b, RED round 1 (must-fix 5 / nit 1) — additive
    // discriminator, see MappedEngineItem's own doc comment. A conditional
    // SPREAD (not `key: cond ? value : undefined`, the pattern most other
    // optional fields on this object use) so the key itself is genuinely
    // ABSENT from the mapped record for every non-phonemeCount item, not
    // merely `undefined`-valued — RED measured the difference: 799
    // foundationItems records used to carry `"foundationItemType" in item
    // === true` with `item.foundationItemType === undefined`, which a
    // `JSON.stringify`-based comparison can't see (the key drops on
    // serialize either way) but a direct `Object.keys`/`in` consumer would.
    // No such consumer exists today (grepped), but the doc comment's own
    // claim ("absent... for EVERY other record") is now literally true of
    // the key, not just the value.
    ...(item.itemType === "phonemeCount" ? { foundationItemType: "phonemeCount" as const } : {}),
  } as const;

  if (isTrace) {
    return {
      ...base,
      type: "trace",
      options: undefined,
      supports: ["TRC"],
      visualScaffold: isTraceTarget
        ? { kind: "traceTarget", data: { guide: item.correctAnswer } }
        : {
            kind: "formation",
            data: {
              letter: item.correctAnswer,
              case: item.case ?? "lowercase",
              strokes: item.strokes ?? [],
              startPoint: item.startPoint ?? "",
            },
          },
      displayMode: "handwrite_then_confirm",
    };
  }
  // MA-95: foundation-math stimulus dispatch (no-op for reading/letter items).
  const math = withNumeralFrameArrangement(item, foundationMathVisual(item));
  // MA-702 — per-option dot groups (see optionDotGroupsFor); absent for
  // every record that does not author a drawable dot-group option spec.
  const optionDotGroups = optionDotGroupsFor(item, math.mathVisual);
  return {
    ...base,
    type: "multiple_choice",
    options: shuffle([...(item.options ?? [item.correctAnswer])]),
    ...(optionDotGroups ? { optionDotGroups } : {}),
    // MA-492/Slice 1 (D15 rev-1): when math.visualRequired is true, the item
    // already renders an inline, default-shown math visual — content-authored
    // SEN/PIC from supportMode would only ever back a SECOND, redundant hint
    // button for data that's already unhidden on screen (291/333 of
    // foundationItems' gate-fail census). math.supports (the visual's own
    // scaffold codes, if any) is untouched — only the supportMode-derived
    // SEN/PIC withdrawal is new here.
    supports: [
      ...supportsFromMode(item.supportMode).filter(
        (c) => !(math.visualRequired && (c === "SEN" || c === "PIC")),
      ),
      ...math.supports,
    ],
    ...(math.visualScaffold ? { visualScaffold: math.visualScaffold } : {}),
    ...(math.mathVisual ? { mathVisual: math.mathVisual } : {}),
    // MA-1001 (RED r1 SHOULD 1) — a spoken numeral-target record by its own
    // data (see isSpokenNumeralTargetShape); key absent otherwise.
    ...(isSpokenNumeralTargetShape({ prompt: item.prompt, answer: item.correctAnswer, options: item.options ?? [item.correctAnswer], mathVisual: math.mathVisual })
      ? { spokenNumeralTarget: true as const }
      : {}),
    ...(math.visualRequired ? { visualRequired: true } : {}),
    ...foundationMathDiagnostics(item),
    displayMode: "tap_choice",
  };
}

// ── S5.3: comprehensionQuestions / semanticRelations / grammarItems ──
// Three MC mappers (T2-H.1.X). Each builds options = shuffle([answer,
// ...distractors]), threads support affordances, stashes pack-specific
// metadata under a NEW visualScaffold.kind, and asserts renderability before
// returning. The new visualScaffold.kind values (passage_context /
// semantic_relation_context / grammar_context) are NOT yet consumed by any
// session screen — these packs are shelf stock (no skill routes to them; see
// S5.3/S5.4 PR bodies), so the kid never reaches them until a Stage-6 routing
// dispatch wires a strand. The mappers ship ready so that wiring is the only
// remaining step.

interface ComprehensionQuestion {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  question: string;
  correctAnswer: string;
  distractors?: string[];
  acceptableAnswers?: string[];
  passageId?: string;
  evidenceSentenceIds?: string[];
  questionType?: string;
  readAloudAllowed?: boolean;
  /** MA-127 — authored on all 282 records; `false` on 136 ("a kid can't
   *  read this alone"). Mapped to `passageReadAloud` below. */
  independentReadingEligible?: boolean;
  validationNotes?: string;
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
}

interface SemanticRelation {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  prompt: string;
  correctAnswer: string;
  nearDistractors?: string[];
  farDistractors?: string[];
  sentenceFrame?: string;
  relationType?: string;
  targetWordOrConcept?: string;
  illustrationSupportRequired?: boolean;
  readAloudRequired?: boolean;
  supportMode?: string[];
  validationRule?: string;
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
}

interface GrammarItem {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  prompt: string;
  correctAnswer: string;
  distractors?: string[];
  distractorCategories?: string[];
  skill?: string;
  modality?: string;
  illustrationRequired?: boolean;
  audioSupport?: boolean;
  validationRule?: string;
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
}

export function comprehensionQuestionToEngineItem(
  item: ComprehensionQuestion,
  microSkillId: string,
): MappedEngineItem {
  const answer = item.correctAnswer;
  // Phase-1 comprehension reader: join the question to its passage so the
  // screen shows the WHOLE passage above the question. Degrade gracefully if a
  // passage doesn't resolve (render a bare MC question rather than crash the
  // session); the comprehensionJoinIntegrity test enforces that all 250
  // launch_safe CQ items DO resolve, so a drift fails CI, not a live session.
  const passage = passageById(item.passageId);
  // ⭐MA-490 — the MA-127 evidence-highlighting hint the MA-119 comment below
  // deferred to was never built, and the text it would have shown was already
  // sitting in the content: `evidenceSentenceIds` joins to REAL sentence text
  // on the SAME passage this mapper already resolves (`passage`, just above).
  // Founder directive: "282 comprehension questions have no hint, but the
  // hint text is already written and unused -- solve this." Measured against
  // the real corpus (all 282 launch_safe items): every one has a non-empty
  // `evidenceSentenceIds`, every passageId resolves, every sentence id
  // resolves, and NONE of the 282 joined evidence strings echo the question
  // (word-normalized containment check) — so this is 100% real coverage, not
  // a partial fix. Multi-sentence evidence (105/282 items) joins in authored
  // order; sequencing/character/inference items commonly cite 2+ sentences.
  // ⚠️This is NOT a repeat of the on-screen passage: the passage's FULL body
  // is already shown above the question (passageBodySentences below), so a
  // hint that names WHICH sentence answers the question is real
  // evidence-pointing, not a restated prompt — the exact case HintSlot's own
  // echo guard (hasRenderableScaffold, compared against the PROMPT) does not
  // reject, and should not: it's new information (which sentence), not the
  // same information twice.
  const evidenceText = (item.evidenceSentenceIds ?? [])
    .map((sid) => passage?.sentences.find((sn) => sn.sentence_id === sid)?.text)
    .filter((t): t is string => !!t)
    .join(" ");
  // MA-492/Slice 8 (D27, MA-127) — the evidence sentence(s) as INDEXES into
  // `passage.sentences` (== `passageBodySentences` below, index-aligned: both
  // derive from the same `passage.sentences` array in authored order). This is
  // the field the MA-127 in-place highlight needs and `evidenceSentenceIds`
  // alone doesn't give: an id ("DP_R06_013_s1") only resolves to a POSITION
  // by re-searching the passage, which is exactly what the highlight render
  // site would otherwise have to redo per item — computed once here, where
  // the passage is already resolved, instead of string/id-matching at render
  // time. `findIndex` (not a lookup map) because passages are short (a handful
  // of sentences); `-1` (unresolved id) is filtered out defensively, though
  // `comprehensionJoinIntegrity` already asserts every authored id resolves
  // on all 282 launch_safe items, so this filter is a belt on an already
  // buckled belt, not a silent-drop path in practice.
  const evidenceSentenceIndexes = (item.evidenceSentenceIds ?? [])
    .map((sid) => passage?.sentences.findIndex((sn) => sn.sentence_id === sid) ?? -1)
    .filter((idx) => idx !== -1);
  const mapped: MappedEngineItem = {
    id: item.id,
    type: "multiple_choice",
    prompt: item.question,
    answer,
    options: shuffle([answer, ...(item.distractors ?? [])]),
    microSkillId,
    ageBand: item.ageBand ?? "5.5-6.0",
    difficulty: item.difficulty ?? 1,
    // MA-490 — SEN now carries the resolved evidence-sentence text (see
    // evidenceText above) instead of being permanently withdrawn. Falls back
    // to no hint (not an empty SEN panel) when a passage/sentence somehow
    // fails to resolve — an honest empty state, not a broken one, matching
    // the MA-323 precedent elsewhere in this file. readAloudAllowed stays on
    // the content data as an authoring fact only; it does not gate supports.
    supports: evidenceText ? ["SEN"] : [],
    explanation: item.validationNotes ?? "",
    visualScaffold: {
      kind: "passage_context",
      data: {
        text: evidenceText,
        passageId: item.passageId,
        evidenceSentenceIds: item.evidenceSentenceIds,
        questionType: item.questionType,
        acceptableAnswers: item.acceptableAnswers,
      },
    },
    displayMode: "tap_choice",
    launchStatus: item.launchStatus ?? "expert_review_needed",
    // Phase-1: full passage body (ALL sentences) + title, reusing the RC-3b
    // PassageReader fields ReadingSession already renders. The passage_context
    // visualScaffold.kind is the marker the screen branches on. Conditional so
    // an unresolved passage degrades to a bare MC question.
    ...(passage
      ? {
          passageBodySentences: passage.sentences.map((sn) => sn.text),
          passageTitle: passage.title,
        }
      : {}),
    // MA-492/Slice 8 (D27, MA-127) — omitted (not `[]`) when there is nothing
    // to highlight, so a render-site `item.evidenceSentenceIndexes?.length`
    // check degrades identically whether the field is absent or empty.
    ...(evidenceSentenceIndexes.length > 0 ? { evidenceSentenceIndexes } : {}),
    // MA-127 — the author's "can't read this alone" flag, carried through
    // as the read-aloud affordance it drives. Strict `=== false` (an absent
    // flag is NOT a request for read-aloud) and only when the passage
    // resolved (no body, nothing to read). Authored per QUESTION; MA-787
    // reconciled the four passages that disagreed (CQ_0375-0382 moved to true)
    // and audit:passage-eligibility keeps every passage at one value, but this
    // still reads the question's own flag and is never derived from the passage.
    ...(passage && item.independentReadingEligible === false ? { passageReadAloud: true as const } : {}),
  };
  assertMapperInvariants(mapped, "comprehensionQuestions", { skillId: microSkillId, itemId: item.id });
  return mapped;
}

export function semanticRelationToEngineItem(
  item: SemanticRelation,
  microSkillId: string,
  imageIndex?: Parameters<typeof illustrationUrlForItem>[1],
): MappedEngineItem {
  const answer = item.correctAnswer;
  // MA-119 (pic-empty-classes slice, joint completion of MA-66's
  // content-wiring-mapper-render slice): semanticRelations' visualScaffold
  // carries no `text` field, so an offered "PIC" always fell through to
  // ScaffoldPIC's static "no picture" placeholder — a dead button on every
  // one of 577 launch_safe items regardless of illustrationSupportRequired.
  // PIC is now resolved AT MAP TIME via MA-66's approved-image index: offered
  // only when a reviewer-approved + uploaded illustration URL actually
  // exists for this item id (today: never — the index ships empty until a
  // real image batch is generated/reviewed). illustrationSupportRequired
  // stays on the content data as an authoring/eligibility fact only; it no
  // longer gates supports directly.
  const illustrationUrl = illustrationUrlForItem(item.id, imageIndex);
  // MA-492/Slice 4 (D7-D11 rev-1) — a category-level TEXT hint, not an
  // image (ADR-096 closes MA-457 with "no" for this pack: category/function
  // relationTypes have targetWordOrConcept===correctAnswer verbatim, a
  // direct leak; part_whole/attribute leak via world knowledge — the same
  // class MA-121 already rejects). resolveSemanticRelationHintCue is
  // FAIL-CLOSED: an unrecognized relationType resolves to undefined, so an
  // unauthored category never gets a generic filler hint offered in its
  // place. PIC behavior (illustrationUrl) is unchanged.
  const hintCue = resolveSemanticRelationHintCue(item.relationType as string | undefined);
  const mapped: MappedEngineItem = {
    id: item.id,
    type: "multiple_choice",
    prompt: item.prompt,
    answer,
    options: shuffle([answer, ...(item.nearDistractors ?? []), ...(item.farDistractors ?? [])]),
    microSkillId,
    ageBand: item.ageBand ?? "5.5-6.0",
    difficulty: item.difficulty ?? 1,
    supports: [...(hintCue ? (["SEN"] as const) : []), ...(illustrationUrl ? (["PIC"] as const) : [])],
    explanation: item.validationRule ?? "",
    visualScaffold: {
      kind: "semantic_relation_context",
      data: {
        sentenceFrame: item.sentenceFrame ?? item.prompt,
        relationType: item.relationType,
        targetWordOrConcept: item.targetWordOrConcept,
        supportMode: item.supportMode ?? [],
        readAloudRequired: item.readAloudRequired ?? false,
        ...(hintCue ? { text: hintCue } : {}),
        ...(illustrationUrl ? { imageUrl: illustrationUrl } : {}),
      },
    },
    displayMode: "tap_choice",
    launchStatus: item.launchStatus ?? "expert_review_needed",
  };
  assertMapperInvariants(mapped, "semanticRelations", { skillId: microSkillId, itemId: item.id });
  return mapped;
}

export function grammarItemToEngineItem(
  item: GrammarItem,
  microSkillId: string,
  imageIndex?: Parameters<typeof illustrationUrlForItem>[1],
): MappedEngineItem {
  const answer = item.correctAnswer;
  // MA-119 (gi-sen-empty slice): grammarItems' visualScaffold (grammar_context,
  // below) carries no `text` field, so an offered "SEN" hint always rendered
  // an empty panel regardless of audioSupport — SEN withdrawn unconditionally
  // (there is no answer-safe text distinct from the on-screen prompt to back
  // it — backfilling `prompt` itself would just trade the empty-render defect
  // for a duplicate-prompt one). audioSupport stays on the content data as an
  // authoring fact only; it no longer gates supports.
  // MA-119 (pic-empty-classes slice, joint completion of MA-66's
  // content-wiring-mapper-render slice): PIC (illustrationRequired) is no
  // longer offered unconditionally either — it's now resolved AT MAP TIME
  // via MA-66's approved-image index, same as semanticRelations above.
  // illustrationRequired stays an authoring/eligibility fact only.
  const illustrationUrl = illustrationUrlForItem(item.id, imageIndex);
  // MA-492/Slice 4 (D7-D11 rev-1) — a category-level TEXT hint, not an
  // image (ADR-096 extends MA-457's "no" verdict to this pack: illustrating
  // any candidate word either depicts the answer, if it's the answer, or
  // points at the wrong tile, if it's a distractor). resolveGrammarHintCue
  // is FAIL-CLOSED: an unrecognized skill resolves to undefined, so an
  // unauthored skill never gets a generic filler hint offered in its place.
  // PIC behavior (illustrationUrl) is unchanged.
  const hintCue = resolveGrammarHintCue(item.skill as string | undefined);
  const supports: SupportCode[] = [
    ...(hintCue ? (["SEN"] as const) : []),
    ...(illustrationUrl ? (["PIC"] as const) : []),
  ];
  const mapped: MappedEngineItem = {
    id: item.id,
    type: "multiple_choice",
    prompt: item.prompt,
    answer,
    options: shuffle([answer, ...(item.distractors ?? [])]),
    microSkillId,
    ageBand: item.ageBand ?? "5.5-6.0",
    difficulty: item.difficulty ?? 1,
    supports,
    explanation: item.validationRule ?? "",
    visualScaffold: {
      kind: "grammar_context",
      data: {
        skill: item.skill,
        modality: item.modality,
        distractorCategories: item.distractorCategories,
        ...(hintCue ? { text: hintCue } : {}),
        ...(illustrationUrl ? { imageUrl: illustrationUrl } : {}),
      },
    },
    displayMode: "tap_choice",
    launchStatus: item.launchStatus ?? "expert_review_needed",
  };
  assertMapperInvariants(mapped, "grammarItems", { skillId: microSkillId, itemId: item.id });
  return mapped;
}

// ── S5.5 (T2-H.2): encodingWords — core sound-box + HFW subset ────────
// One pack, five modalities (see stage5/transforms.mjs transformEncodingWord
// for the launch-status dispositions):
//   sound_box_encode → type "sound_box_encode" / displayMode
//     "tap_sound_boxes" (founder-locked NEW ItemType; T2-D shipped
//     SoundBoxEncode.tsx, wired into FoundationSession — the renderer is
//     live. Any items still gated exclude_independent_practice are a
//     content-review hold, not a missing renderer).
//   HFW multiple_choice + sentence_completion → renderable tap_choice MC
//     (these SERVE — ms.read.08 routes here via PACK_OVERRIDE).
//   HFW trace → type "trace" (gated — word-trace guide unverified).
//   HFW listen_choose → type "listen_choose" (gated — no item-audio renderer).

interface EncodingWord {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  modality?: string;
  // core sound-box fields
  word?: string;
  prompt?: string;
  boxes?: unknown;
  letterTilePool?: string[];
  targetPhonemes?: string[];
  targetGraphemes?: string[];
  blendPattern?: string;
  blendType?: string;
  pictureRef?: string;
  pictureRequired?: boolean;
  audioScript?: string;
  audioPending?: boolean;
  // HFW subset fields (wordTier present ⟺ hfwSubset)
  wordTier?: string;
  hfwSubset?: boolean;
  targetWord?: string;
  correctAnswer?: string;
  distractors?: string[]; // MC / listen_choose: answer NOT included
  options?: string[]; // sentence_completion: answer INCLUDED
  sentenceTemplate?: string;
  whyOnlyOneAnswer?: string;
  strokeOrder?: unknown;
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
}

// ⭐Live-QA finding (measured, 2026-08-13): `pickTargetEmoji` is a WORD-LEVEL
// lookup with no sense disambiguation — it maps "can" to 🥫 (the tin-can
// NOUN), which is the correct, licensed depiction for controlledSentences'
// own genuine noun usage (CS_06_036 "A ___ has a lid." → can, verified:
// exactly 1 correctAnswer==="can" in that pack). But encodingWords'
// sentence_completion pack authors "can" ONLY as the MODAL VERB (verified:
// the pack's one correctAnswer==="can" item is HFW_sc_can_01, "I ___
// jump.") — a tin-can picture next to "I ___ jump." actively misleads a
// child rather than helping her. This is MA-121's word-sense discipline
// (a match must depict the SENSE actually used, not merely the spelling)
// applied to a homograph the shared map has no way to disambiguate on its
// own. Scoped NARROWLY: excludes the ANSWER STRING from this pack/case's
// PIC resolution only — never touches `EMOJI_BY_TEMPLATE_WORD`/
// `TARGET_ONLY_EMOJI_BY_WORD` (SceneIllustration.jsx, shared with
// controlledSentences, whose own "can" usage stays correct and untouched)
// and never touches `pickTargetEmoji` itself. Extend this set only when a
// FUTURE encodingWords sentence_completion answer is independently found to
// have the same cross-sense collision — do not pre-emptively add words with
// no measured defect. Flagged as the residual in MA-492 Slice 6's 2026-08-08
// decision log; fixed here.
export const HOMOGRAPH_EXCLUDED_ANSWERS: ReadonlySet<string> = new Set(["can"]);

/** MA-794 — what the screen PRINTS for an ms.read.08 HFW_mc_* item (the
 *  spoken prompt, which names the target, is `spokenPrompt`). Wording chosen
 *  so NO token of it equals, or is a >=2-char prefix of / prefixed by, ANY
 *  tile (answers + distractors) of the 60 items, so the print offers no
 *  shortcut ("Tap the word you hear." matched "words"/"there"/"he"; "Listen,
 *  then tap." matched the distractor "then"); pinned by
 *  ma794HfwAudioPrompt.test.mjs, which re-derives the pick from the pack. */
export const HFW_FIND_WORD_DISPLAY_PROMPT = "Listen, tap.";

export function encodingWordToEngineItem(
  item: EncodingWord,
  microSkillId: string,
): MappedEngineItem {
  const base = {
    id: item.id,
    microSkillId,
    ageBand: item.ageBand ?? "5.5-6.0",
    difficulty: item.difficulty ?? 1,
    launchStatus: item.launchStatus ?? "expert_review_needed",
  } as const;
  let mapped: MappedEngineItem;

  switch (item.modality) {
    case "multiple_choice": {
      // HFW word recognition: "Find the word 'a'." + distractors (answer NOT in array).
      const answer = item.correctAnswer ?? "";
      // MA-492/Slice 6 (D12) — `supports: []` stays unconditional (no hint
      // mechanism for this class); recorded machine-visibly in
      // `scripts/audit/noHintExemptions.json` (60 items, D32).
      // ⚠MA-794 SUPERSEDES D12's rationale: D12 argued "the target word is
      // already spelled out verbatim in the on-screen prompt, so no hint is
      // needed" - but that disclosure is exactly the defect (a child can
      // string-match letters without recognising the word). The screen now
      // prints "Listen, tap." and the speaker names the word
      // (`spokenPrompt`, auto-speaks), so the exemption now stands because the
      // item is audio-led: the speaker IS the instruction, and any
      // visual hint would re-disclose the key.
      mapped = {
        ...base,
        type: "multiple_choice",
        // MA-794 — the child must RECOGNISE the word, not string-match it: the
        // screen shows an answer-free prompt and the speaker names the word
        // (the authored string, unchanged, so its narration take already
        // exists). Derived by rule from the record, no content change.
        prompt: HFW_FIND_WORD_DISPLAY_PROMPT,
        spokenPrompt: item.prompt ?? `Find the word '${item.targetWord ?? answer}'.`,
        answer,
        options: shuffle([answer, ...(item.distractors ?? [])]),
        supports: [],
        explanation: "",
        visualScaffold: { kind: "hfw_context", data: { targetWord: item.targetWord, wordTier: item.wordTier } },
        displayMode: "tap_choice",
      };
      break;
    }
    case "sentence_completion": {
      // HFW cloze: sentenceTemplate + options (answer INCLUDED — controlledSentences shape).
      const answer = item.correctAnswer ?? "";
      // MA-119 (ew-sen-empty slice): this modality's visualScaffold (hfw_context,
      // below) carries no `text` field, so an offered "SEN" hint always rendered
      // an empty panel. There is no answer-safe text distinct from the on-screen
      // prompt to back it — sentenceTemplate IS the prompt already, and
      // whyOnlyOneAnswer names the correct choice (an answer leak, not a safe
      // backfill) — so SEN is withdrawn unconditionally, same reasoning as the
      // cq-sen-empty/gi-sen-empty precedents.
      //
      // MA-492/Slice 6 (D13): this modality IS eligible for the SAME
      // controlledSentences' `depictionMode: "targetWord"` PIC treatment
      // (contentToEngineItem.ts:1274-1339) — unlike decodablePassages (D4
      // rev-1, RETRACTED, see the design spec §1b), this pack's ADR-088
      // licensing precondition is independently verified, not inherited by
      // shape: `whyOnlyOneAnswer` above is an AUTHORED field with genuine
      // per-item implausibility reasoning (e.g. `HFW_EXP3_sc_029`: "'sit'
      // fits 'on the rug'; 'sing' would not be 'on the rug'; 'read' would
      // not fit; 'upon' is a preposition") — red-team verified (real sample,
      // 20 items) as pack-wide, not assumed. Measured against the real
      // mapper + `pickTargetEmoji`: 38/200 launch_safe items raw-resolve a
      // target emoji; 37/200 actually OFFER PIC once the one measured
      // HOMOGRAPH_EXCLUDED_ANSWERS case ("can") is withdrawn below, and 0/37
      // collide with any distractor's own
      // `pickTargetEmoji` glyph (swept by
      // `scripts/audit/picScaffoldAnswerSafetyTest.mjs`'s encodingWords
      // sweep, added in the same diff as this comment) — no
      // unique-identifier leak, unlike decodablePassages' 92/93 measured
      // collision rate. The remaining 163/200 are genuinely undepictable
      // high-frequency function words (`are/is/am/were/has/will/of/to/if/
      // when`, D14) plus the one measured HOMOGRAPH_EXCLUDED_ANSWERS case
      // ("can" — see that set's own comment above) — no emoji, no AI image,
      // no text hint helps (K11 + MA-323); that bucket is recorded as a
      // named exemption in `scripts/audit/noHintExemptions.json`, not
      // silently absorbed.
      const targetPicEmoji = HOMOGRAPH_EXCLUDED_ANSWERS.has(answer.toLowerCase())
        ? undefined
        : pickTargetEmoji(answer, item.id)?.emoji ?? undefined;
      mapped = {
        ...base,
        type: "multiple_choice",
        prompt: item.sentenceTemplate ?? "",
        answer,
        options: shuffle([...(item.options ?? [answer])]),
        supports: targetPicEmoji ? ["PIC"] : [],
        explanation: item.whyOnlyOneAnswer ?? "",
        visualScaffold: {
          kind: "hfw_context",
          data: {
            targetWord: item.targetWord,
            wordTier: item.wordTier,
            word: answer,
            text: item.sentenceTemplate,
            depictionMode: "targetWord",
            itemId: item.id,
          },
        },
        displayMode: "tap_choice",
      };
      break;
    }
    case "trace": {
      // HFW word formation (gated — see transform): HandwritingPad guide.
      const word = item.targetWord ?? "";
      mapped = {
        ...base,
        type: "trace",
        prompt: `Trace the word: ${word}`,
        answer: word,
        options: undefined,
        supports: ["TRC"],
        explanation: "",
        visualScaffold: {
          kind: "formation",
          data: { letter: word, case: "lowercase", strokes: item.strokeOrder ?? [], startPoint: "" },
        },
        displayMode: "handwrite_then_confirm",
      };
      break;
    }
    case "trace_target": {
      // MA-208(b) — sight-word TRACING (ms.read.dolch_trace), distinct from
      // "trace" above (the OLD word-FORMATION guide, stroke-order-driven,
      // still REGATE_MODALITIES-gated for an unrelated reason). This case
      // routes to the NEW traceTarget surface (FoundationSession renders
      // <TraceItem> + judgeTrace — completion-graded, no stroke-order data
      // needed). No authored content uses this modality yet (slice (c)).
      const word = item.targetWord ?? "";
      mapped = {
        ...base,
        type: "trace",
        prompt: `Trace the word: ${word}`,
        answer: word,
        options: undefined,
        supports: ["TRC"],
        explanation: "",
        visualScaffold: { kind: "traceTarget", data: { guide: word } },
        displayMode: "handwrite_then_confirm",
      };
      break;
    }
    case "listen_choose": {
      // HFW listen-and-pick (gated — no item-audio renderer; audioPending).
      const answer = item.correctAnswer ?? "";
      // MA-119 (ew-sen-empty slice): same hfw_context visualScaffold shape as
      // sentence_completion above — no `text` field, so SEN always rendered
      // empty. audioScript is already consumed as this item's own `prompt`,
      // so there is no distinct answer-safe text to backfill; withdrawn
      // unconditionally. Currently unreachable (never launch_safe today —
      // see the comment above), fixed at the source so a future launch-status
      // flip can't silently reintroduce a live empty-panel hint.
      mapped = {
        ...base,
        type: "listen_choose",
        prompt: item.audioScript ?? `Find the word '${answer}'.`,
        answer,
        options: shuffle([answer, ...(item.distractors ?? [])]),
        supports: [],
        explanation: "",
        visualScaffold: { kind: "hfw_context", data: { targetWord: item.targetWord, wordTier: item.wordTier } },
        displayMode: "tap_choice",
      };
      break;
    }
    default: {
      // sound_box_encode (the entire core pack) — T2-D contract.
      const word = item.word ?? item.prompt ?? "";
      // MA-681 (ADR-055 addendum) — the child BUILDS the word, so for the two
      // skills below the screen prints a neutral line (never the word) and the
      // speaker says `spokenPrompt`: ms.read.04 -> the framed SEGMENTED cue
      // (manifest-gated by FoundationSession until its clip exists);
      // ms.read.07 -> the BARE WORD as dictation (already a recorded clip).
      // `answer` is shown after the check. Every other skill keeps the legacy
      // shape. PIC: the "Picture" hint is withheld unless the ref resolves to
      // a real asset (none exist — ADR-055's MA-596 addendum).
      const cueKind: SoundBoxCueKind | undefined = SOUND_BOX_CUE_KIND_BY_SKILL[microSkillId];
      const cueGraphemes = Array.isArray(item.targetGraphemes) ? item.targetGraphemes : [];
      const cueSpoken =
        cueKind === "dictation" && word !== "" ? word
        : cueKind === "segmented" && cueGraphemes.length > 0 ? soundBoxCueFor(cueGraphemes)
        : null;
      mapped = {
        ...base,
        type: "sound_box_encode",
        prompt: cueSpoken !== null ? SOUND_BOX_CUE_DISPLAY_PROMPT : word,
        ...(cueSpoken !== null ? { spokenPrompt: cueSpoken, soundBoxCueKind: cueKind } : {}),
        answer: word,
        options: undefined,
        supports: item.pictureRequired && soundBoxPictureResolves(item.pictureRef) ? ["PIC"] : [],
        explanation: "",
        visualScaffold: {
          kind: "sound_box",
          data: {
            word,
            boxes: item.boxes,
            letterTilePool: item.letterTilePool,
            targetPhonemes: item.targetPhonemes,
            targetGraphemes: item.targetGraphemes,
            blendPattern: item.blendPattern,
            blendType: item.blendType,
            pictureRef: item.pictureRef,
          },
        },
        displayMode: "tap_sound_boxes",
      };
      break;
    }
  }
  assertMapperInvariants(mapped, "encodingWords", { skillId: microSkillId, itemId: item.id });
  return mapped;
}

// ── RC-2 (reading-connection dispatch §3): rhyming + syllable_counting ──
// Phono packs are audio-FIRST multiple-choice: the judgment is about SOUND
// (rhyme / syllable count), so the prompt is also spoken (audioScript) and
// the item cannot be done honestly without hearing it. Mapper contract:
//   - options = shuffle([answer, ...distractors]) — the pack schema +
//     phonoPackGuards pin that authored distractors EXCLUDE the answer.
//   - audioScript + audioPending ride under visualScaffold.kind
//     "phono_audio" (data.audioScript / data.audioPending) — EngineItem has
//     no top-level audio field, and the S5.3/S5.5 precedent stashes
//     pack-specific metadata under a pack-owned visualScaffold.kind. RC-4's
//     serving/renderer work reads the script from there.
//   - The AUTO-PLAY requirement itself is NOT carried on the item: it is
//     derived from microSkillId via isAudioRequired (RC-3.5 added the
//     microSkillId path; ms.read.0a / ms.read.0b are in
//     AUDIO_REQUIRED_MICROSKILLS, src/engine/audioRequired.ts).
// Routing: RC-4 shipped the PACK_OVERRIDE (ms.read.0a/0b → these packs) —
// both skills are ch1 supportSkills in chapters.json and serve today.

interface RhymingItem {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  targetWord?: string;
  targetRime?: string;
  prompt: string;
  correctAnswer: string;
  distractors?: string[]; // rhyme-candidate words; answer NOT included
  audioScript?: string;
  audioPending?: boolean;
  validationRule?: string;
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
  // MA-492/Slice 2 (D22-D23) — authored SEPARATELY from audioScript; data.text
  // stays bound to audioScript unchanged. hintCue is the onset/rime
  // decomposition ScaffoldSEN renders in preference to data.text.
  hintCue?: string;
}

interface SyllableCountingItem {
  difficulty?: number; // I.2.a backfill; ?? 1 in mapper for stray un-backfilled items
  id: string;
  targetWord?: string;
  syllableCount?: number;
  prompt: string;
  correctAnswer: string; // digit STRING ("1".."5") — pack guard pins === String(syllableCount)
  distractors?: string[]; // digit strings; answer NOT included
  audioScript?: string;
  audioPending?: boolean;
  validationRule?: string;
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
  // MA-492/Slice 2 (D22/D25) — see RhymingItem's hintCue comment above.
  hintCue?: string;
}

/** Rhyming item → EngineItem (audio-first multiple-choice). */
export function rhymingItemToEngineItem(
  item: RhymingItem,
  microSkillId: string,
): MappedEngineItem {
  const answer = item.correctAnswer;
  const mapped: MappedEngineItem = {
    id: item.id,
    type: "multiple_choice",
    prompt: item.prompt,
    answer,
    options: shuffle([answer, ...(item.distractors ?? [])]),
    microSkillId,
    ageBand: item.ageBand ?? "4.0-4.5",
    difficulty: item.difficulty ?? 1,
    // SEN = read-aloud affordance (replay button); the first-render
    // auto-play is the isAudioRequired gate, not a SupportCode.
    supports: ["SEN"],
    explanation: item.validationRule ?? "",
    visualScaffold: {
      kind: "phono_audio",
      data: {
        task: "rhyming",
        targetWord: item.targetWord,
        targetRime: item.targetRime,
        audioScript: item.audioScript,
        // MA-119: ScaffoldSEN renders data.text (not audioScript) — without
        // this the offered SEN hint rendered empty (e-sen-empty-render,
        // MA-118-phono). audioScript is already leak-vetted (restates the
        // prompt, never the answer).
        text: item.audioScript ?? "",
        audioPending: item.audioPending ?? false,
        // MA-492/Slice 2 (D22-D23) — the real hint-panel text. Authored
        // separately from audioScript (which stays wired to `text` above,
        // unchanged — audioScript is the future TTS-question channel, not
        // hint content). ScaffoldSEN reads `data.hintCue ?? data.text`, so
        // this is what actually reaches the render gate/panel now.
        hintCue: item.hintCue,
      },
    },
    displayMode: "tap_choice",
    launchStatus: item.launchStatus ?? "expert_review_needed",
  };
  assertMapperInvariants(mapped, "rhyming", { skillId: microSkillId, itemId: item.id });
  return mapped;
}

/** Syllable-counting item → EngineItem (audio-first multiple-choice;
 *  options are digit STRINGS — per-pack distractor pattern, CLAUDE.md). */
export function syllableCountingItemToEngineItem(
  item: SyllableCountingItem,
  microSkillId: string,
): MappedEngineItem {
  const answer = item.correctAnswer;
  const mapped: MappedEngineItem = {
    id: item.id,
    type: "multiple_choice",
    prompt: item.prompt,
    answer,
    options: shuffle([answer, ...(item.distractors ?? [])]),
    microSkillId,
    ageBand: item.ageBand ?? "4.5-5.0",
    difficulty: item.difficulty ?? 1,
    supports: ["SEN"],
    explanation: item.validationRule ?? "",
    visualScaffold: {
      kind: "phono_audio",
      data: {
        task: "syllable_counting",
        targetWord: item.targetWord,
        syllableCount: item.syllableCount,
        audioScript: item.audioScript,
        // MA-119: see rhymingItemToEngineItem — ScaffoldSEN renders data.text.
        text: item.audioScript ?? "",
        audioPending: item.audioPending ?? false,
        // MA-492/Slice 2 (D22/D25) — see rhymingItemToEngineItem's hintCue
        // comment above. The clap-cue text names the STRATEGY, never the
        // syllable count itself (D26 rejects a countable chunk display).
        hintCue: item.hintCue,
      },
    },
    displayMode: "tap_choice",
    launchStatus: item.launchStatus ?? "expert_review_needed",
  };
  assertMapperInvariants(mapped, "syllable_counting", { skillId: microSkillId, itemId: item.id });
  return mapped;
}

// ── RC-3 (reading-connection dispatch §3): decodablePassages ────────────────
// 172 launch_safe passages (wholesale-replaced from staging) covering
// ms.read.06/09/11/12/13/14. MINIMAL first-pass (D-RC-1 LOCKED by founder
// 2026-06-12): reuse the existing fill_blank chip-grid render path — no new
// renderer needed. The multi-sentence passage reader is RC-3b (Code-sized,
// separate task). Difficulty:1 stamped for all items — a per-passage
// difficulty backfill is a documented follow-up (RC-3b scope).

interface DecodablePassageSentence {
  sentence_id: string;
  text: string;
}

interface DecodablePassageItem {
  id: string;
  targetSkill: string;
  title?: string;
  sentences: DecodablePassageSentence[];
  ageBand?: AgeBand;
  launchStatus?: LaunchStatus;
  /** Authored near-miss cloze distractors (same slot/POS/register as the
   *  answer, decodable at the passage level). When present, these are
   *  PREFERRED over the passageClozeDistractors generator — they are
   *  plausible decoys, not just any in-passage word. Filtered to exclude the
   *  answer and deduped at map time; the generator backfills if fewer than
   *  two distinct options would remain. (Cloze-distractor quality pass.) */
  clozeDistractors?: string[];
  /** MA-826 — the passage's authored character names ("Mia", "Brad"). */
  characterNames?: string[];
  /** MA-616a — authored story-constrained cloze target (see
   *  docs/build/specs/MA-616-dp-cloze-slot.md). When present, the mapper
   *  locates and blanks THIS word in the last sentence instead of
   *  unconditionally blanking the last word. Absent → today's behaviour,
   *  byte-identical. */
  clozeSlot?: ClozeSlot;
}

/** A whitespace-split token's "core": the token with leading/trailing
 *  punctuation stripped (letters/digits — any Unicode letter or digit —
 *  kept; everything outside that run discarded). Internal punctuation
 *  (an apostrophe in "don't") survives untouched — only the token's ends
 *  are trimmed. Used by the clozeSlot path to find/replace an authored
 *  slot word while preserving the sentence's real punctuation. */
export function tokenCore(token: string): string {
  return token.replace(/^[^\p{L}\p{N}]+/u, "").replace(/[^\p{L}\p{N}]+$/u, "");
}

/** Split a whitespace token into its stripped leading punctuation, core,
 *  and stripped trailing punctuation, so a replacement can reuse the same
 *  punctuation around a new core (e.g. "nap." -> "" / "nap" / "."). */
export function splitTokenPunct(token: string): { leading: string; trailing: string } {
  const leading = token.match(/^[^\p{L}\p{N}]+/u)?.[0] ?? "";
  const trailing = token.match(/[^\p{L}\p{N}]+$/u)?.[0] ?? "";
  return { leading, trailing };
}

/** Decodable passage → EngineItem (fill_blank cloze from last sentence).
 *
 *  MINIMAL first-pass (D-RC-1 LOCKED): derives a single cloze item from the
 *  passage's last sentence so the existing fill_blank chip-grid renderer
 *  works without modification. The last sentence is used because it typically
 *  contains the passage's most decodable target word.
 *
 *  Cloze derivation:
 *    - Strip trailing period from the last sentence text.
 *    - answer  = last word (post-split on whitespace).
 *    - prompt  = preceding words + " ___."
 *    - options = not applicable for fill_blank (chip-grid derives from the
 *      answer directly; options field omitted).
 *
 *  The item id traces back to the authored passage. The passage title is
 *  embedded in explanation for RC-3b UX surfacing.
 */
// Decodable-cloze distractor builder. The decodablePassages bundle carries no
// authored distractors, but the reading renderer needs TAPPABLE options
// (choices = item.options): with none, the cloze renders unanswerable (the
// "Tap the missing word" bug — every decodable passage was affected once the
// RC-F refactor moved choice-building from the renderer to item.options).
// Draw plausible word chips from the passage's OWN content words (decodable,
// in-theme, same level), excluding the answer + high-frequency function words;
// top up from a small decodable pool so there are always >=3 choices.
const CLOZE_STOPWORDS = new Set([
  "the","and","are","was","for","you","his","her","had","has","him","can","not",
  "but","out","this","that","they","with","she","her","off","its","our","all",
  "any","who","why","how","let","yes","saw","see","get","got","did","does","done",
]);
const CLOZE_FALLBACK_WORDS = ["red","big","hot","wet","sad","fun","run","sun","top","cup"];

function passageClozeDistractors(
  sentences: DecodablePassageSentence[],
  answer: string,
  max = 3,
): string[] {
  const ans = answer.toLowerCase();
  const seen = new Set<string>([ans]);
  const content: string[] = [];
  for (const sentence of sentences) {
    for (const raw of sentence.text.split(/\s+/)) {
      const w = raw.toLowerCase().replace(/[^a-z']/g, "");
      if (w.length < 3 || seen.has(w) || CLOZE_STOPWORDS.has(w)) continue;
      seen.add(w);
      content.push(w);
    }
  }
  // Prefer chips closest in length to the answer (more plausible decoys).
  content.sort((a, b) => Math.abs(a.length - ans.length) - Math.abs(b.length - ans.length));
  const picked = content.slice(0, max);
  for (const f of CLOZE_FALLBACK_WORDS) {
    if (picked.length >= max) break;
    if (!seen.has(f)) { picked.push(f); seen.add(f); }
  }
  return picked;
}

/** Resolve cloze decoys for a decodable passage.
 *
 *  PREFERS authored `item.clozeDistractors` (plausible near-misses fitting the
 *  blank's slot) over the passage-word generator. Authored decoys are:
 *    - filtered to exclude the answer (case-insensitive),
 *    - deduped (case-insensitive, first spelling kept),
 *  then capped at 3. If fewer than 3 distinct authored decoys remain, the
 *  passageClozeDistractors generator backfills (skipping the answer + any
 *  decoy already chosen) so the cloze always keeps >=2 distinct options incl.
 *  the answer (renderability + rc3-decodable hard constraint). With no
 *  authored field, this is exactly the generator path (unchanged behaviour).
 */
function clozeDistractorsFor(
  item: DecodablePassageItem,
  answer: string,
  max = 3,
): string[] {
  const ans = answer.toLowerCase();
  const picked: string[] = [];
  const seen = new Set<string>([ans]);
  for (const raw of item.clozeDistractors ?? []) {
    if (picked.length >= max) break;
    const w = (raw ?? "").trim();
    const key = w.toLowerCase();
    if (!w || seen.has(key)) continue;
    seen.add(key);
    picked.push(w);
  }
  if (picked.length < max) {
    for (const g of passageClozeDistractors(item.sentences, answer, max)) {
      if (picked.length >= max) break;
      const key = g.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      picked.push(g);
    }
  }
  return picked;
}

/** MA-616a — resolve the cloze (answer + prompt) for a decodablePassages
 *  item's last sentence.
 *
 *  With NO authored `clozeSlot`: today's unconditional behaviour, unchanged
 *  byte-for-byte — strip the last sentence's trailing period, split on
 *  whitespace, blank the LAST word (MapperShapeMismatchError if the sentence
 *  has < 2 words).
 *
 *  With an authored `clozeSlot`: tokenize the last sentence on whitespace
 *  WITHOUT pre-stripping punctuation (so each token keeps its own leading/
 *  trailing marks); a token's "core" is that token with leading/trailing
 *  punctuation stripped (tokenCore). The slot is the LAST token whose core
 *  equals `clozeSlot.word` exactly (case-sensitive, as printed — a
 *  sentence-initial slot is authored capitalized, e.g. "Cat"). No match
 *  throws MapperShapeMismatchError (fail loud; dpClozeSlotGuard.mjs makes
 *  this unreachable on committed content). `answer` is the matched core
 *  LOWERCASED (a capital must never hand the child the answer through
 *  sentence-initial capitalization). `prompt` keeps every other token
 *  untouched and replaces the slot token with its own leading/trailing
 *  punctuation around "___" (so "nap." -> "___.", a mid-sentence "cat"
 *  with no punctuation -> "___"). */
function resolveDpCloze(
  item: DecodablePassageItem,
  microSkillId: string,
): { answer: string; prompt: string } {
  const lastSentence = item.sentences[item.sentences.length - 1];
  if (item.clozeSlot) {
    const tokens = lastSentence.text.trim().split(/\s+/);
    const targetWord = item.clozeSlot.word;
    let slotIndex = -1;
    for (let i = tokens.length - 1; i >= 0; i--) {
      if (tokenCore(tokens[i]) === targetWord) {
        slotIndex = i;
        break;
      }
    }
    if (slotIndex === -1) {
      throw new MapperShapeMismatchError(
        "decodablePassages",
        INVARIANTS.CLOZE_SLOT_WORD_MATCHED,
        { skillId: microSkillId, itemId: item.id },
      );
    }
    const core = tokenCore(tokens[slotIndex]);
    const { leading, trailing } = splitTokenPunct(tokens[slotIndex]);
    const promptTokens = tokens.slice();
    promptTokens[slotIndex] = `${leading}___${trailing}`;
    return { answer: core.toLowerCase(), prompt: promptTokens.join(" ") };
  }
  const rawText = lastSentence.text.trim().replace(/\.\s*$/, "");
  const words = rawText.split(/\s+/);
  // Invariant: every passage has ≥2 words in its last sentence (validated
  // against the 172-item bundle; MapperShapeMismatchError if violated).
  if (words.length < 2) {
    throw new MapperShapeMismatchError(
      "decodablePassages",
      INVARIANTS.PROMPT_NON_EMPTY,
      { skillId: microSkillId, itemId: item.id },
    );
  }
  const answer = words[words.length - 1];
  const prompt = words.slice(0, words.length - 1).join(" ") + " ___.";
  return { answer, prompt };
}

function decodablePassageItemToEngineItem(
  item: DecodablePassageItem,
  microSkillId: string,
): MappedEngineItem {
  const { answer, prompt } = resolveDpCloze(item, microSkillId);
  // RC-3b: passage body = every sentence BEFORE the cloze (last) sentence.
  const passageBodySentences = item.sentences
    .slice(0, item.sentences.length - 1)
    .map((sentence) => sentence.text);

  // Cloze decoys: PREFER authored near-misses (clozeDistractors) — they fit
  // the blank's slot (same POS/register, decodable) instead of being any
  // in-passage word. Filter out the answer + dedupe (case-insensitive); if
  // fewer than 3 distinct decoys survive, backfill from the passage-derived
  // generator so every cloze keeps >=2 distinct options incl. the answer
  // (renderability + rc3-decodable hard constraint). Generator-only when the
  // passage carries no authored distractors.
  const distractors = clozeDistractorsFor(item, answer);

  const mapped: MappedEngineItem = {
    id: item.id,
    type: "fill_blank",
    prompt,
    answer,
    // Tappable choices: answer + slot-fitting word distractors, shuffled once
    // at map time (the renderer reads item.options — RC-F). Without this the
    // "Tap the missing word" cloze had nothing to tap.
    options: shuffle([answer, ...distractors]),
    microSkillId,
    ageBand: item.ageBand ?? "5.0-5.5",
    // Difficulty backfill for decodablePassages is a documented follow-up:
    // all items emit 1 here. The required-difficulty schema field was added
    // to the OTHER 9 mapped packs (I.2.a); this pack was not in that set.
    difficulty: 1,
    // MA-323 (founder ruling 2026-07-23): SBX (sound-box decode, spells the
    // entire answer in letter tiles) is withdrawn unconditionally — a direct
    // answer leak on a multiple-choice cloze. MA-352: SEN is ALSO withdrawn
    // unconditionally now — `data.text` below is the SAME string as `prompt`
    // above, so a SEN hint was always a verbatim echo of the on-screen
    // prompt (zero new information; there is no distinct answer-safe text
    // in this pack's shape to backfill from — same reasoning as the
    // comprehensionQuestions/grammarItems/encodingWords SEN-withdrawal
    // slices, MA-118/MA-119). `text` stays wired into visualScaffold.data
    // because it is harmless authoring context, not because anything reads
    // it — HintSlot.tsx renders no "Show hint" button at all for an empty
    // supports array, which is an honest empty state, not a broken one.
    supports: [],
    explanation: item.title ? `Passage: ${item.title}` : "",
    visualScaffold: {
      kind: "reading",
      data: { word: answer, text: prompt },
    },
    displayMode: "tap_choice",
    launchStatus: item.launchStatus ?? "launch_safe",
    // RC-3b multi-sentence reader (ms.read.13 routes here):
    passageBodySentences,
    passageTitle: item.title,
    // MA-826 — conditional spread: the key is absent when nothing is authored.
    ...(Array.isArray(item.characterNames) && item.characterNames.length > 0
      ? { characterNames: [...item.characterNames] }
      : {}),
  };
  assertMapperInvariants(mapped, "decodablePassages", {
    skillId: microSkillId,
    itemId: item.id,
  });
  return mapped;
}

/** Dispatch a pack item to the right mapper by pack name.
 *
 *  T2-H.1 — FAIL LOUD. `pack` is typed `ContentPackName` (was `string`),
 *  and the switch is EXHAUSTIVE: every pack either has a mapper or throws a
 *  CLASSIFIED error (see contentToEngineItemErrors.ts). The old `default`
 *  branch silently emitted an unrenderable degenerate item (options:
 *  undefined, empty prompt/answer) for any unmapped pack — that hole is now
 *  closed. A mis-routed or not-yet-mapped pack stops the session with a
 *  traceable error instead of serving a broken item.
 *
 *  The `never` assignment in the final `default` makes a future
 *  ContentPackName addition a COMPILE error here (you can't add a pack
 *  without deciding its mapper-or-throw outcome). */
export function contentItemToEngineItem(
  pack: ContentPackName,
  item: Record<string, unknown>,
  microSkillId: string,
): MappedEngineItem {
  // Structured context for the fail-loud errors (skill + item id when present).
  const ctx = { skillId: microSkillId, itemId: (item as { id?: string }).id };
  switch (pack) {
    // ── Mapped, kid-facing today ─────────────────────────────────────
    case "mathExemplars":
      // MA-663 round 2 nit (RED): the OLD cross-pack guard
      // (contentReader.ts's getMergedContentForMicroSkill) was a tautology
      // — it re-derived "is this a member of PACK_DATA[pack]" from the very
      // array it was just read out of, so it could never actually fire. This
      // is the real check: mathExemplars records always carry `expectedAnswer`
      // and never `correctAnswer`; foundationItems is the exact reverse
      // (verified against the real corpus — see below). If a PACK_MERGE
      // record's own shape doesn't match the pack it's tagged with — e.g. a
      // future refactor mixes up which pack a record came from — this throws
      // instead of silently mis-mapping the item (wrong renderer, wrong
      // answer-shape) to a kid.
      if ("correctAnswer" in item && !("expectedAnswer" in item)) {
        throw new Error(
          `contentItemToEngineItem: item ${String((item as { id?: string }).id)} dispatched to mathExemplars mapper but has foundationItems shape (correctAnswer, no expectedAnswer) — cross-pack mapper confusion.`,
        );
      }
      return mathExemplarToEngineItem(item as unknown as MathExemplar, microSkillId);
    case "controlledSentences":
      return controlledSentenceToEngineItem(
        item as unknown as ControlledSentence,
        microSkillId,
      );
    case "foundationItems":
      // MA-663 round 2 nit (RED) — see the mathExemplars case above.
      if ("expectedAnswer" in item && !("correctAnswer" in item)) {
        throw new Error(
          `contentItemToEngineItem: item ${String((item as { id?: string }).id)} dispatched to foundationItems mapper but has mathExemplars shape (expectedAnswer, no correctAnswer) — cross-pack mapper confusion.`,
        );
      }
      return foundationItemToEngineItem(item as unknown as FoundationItem, microSkillId);

    // ── Kid-facing in scope, mapper not yet shipped ──────────────────
    // Each carries the planned sub-PR so the operator knows where the fix
    // lives. None of these is routed AND launch_safe today, so under the
    // independent_practice fail-closed filter the pool is empty and this
    // throw never fires for current paths — it's the guardrail for when a
    // skill IS routed here (or a scaffolded audience surfaces ERN items).
    case "decodablePassages":
      // RC-3 (reading-connection dispatch §3): 172 launch_safe passages mapped
      // via MINIMAL first-pass (D-RC-1 LOCKED by founder 2026-06-12).
      // Reuses fill_blank chip-grid render path; multi-sentence reader is RC-3b.
      return decodablePassageItemToEngineItem(
        item as unknown as DecodablePassageItem,
        microSkillId,
      );
    // ── S5.5 (T2-H.2): mapped. HFW MC/cloze SERVE (ms.read.08 routes here);
    //    sound_box_encode's renderer (SoundBoxEncode.tsx) shipped in T2-D and
    //    is wired into FoundationSession — remaining exclude_independent_
    //    practice items are a content-review hold, not a missing renderer.
    //    trace / listen_choose are still integration-gated (no renderer). ──
    case "encodingWords":
      return encodingWordToEngineItem(item as unknown as EncodingWord, microSkillId);
    case "wordProblemTemplates":
      throw new NoMapperImplementedError(pack, "T2-H.4", ctx);
    // ── RC-2 (reading-connection dispatch §3): phono packs mapped (45
    //    rhyming ms.read.0a + 30 syllable_counting ms.read.0b, all
    //    launch_safe; audio-first MC). The audio-required behavior derives
    //    from microSkillId via isAudioRequired (RC-3.5). RC-4 shipped the
    //    PACK_OVERRIDE routing both ms.read.0a/0b here — both serve today. ──
    case "rhyming":
      return rhymingItemToEngineItem(item as unknown as RhymingItem, microSkillId);
    case "syllable_counting":
      return syllableCountingItemToEngineItem(item as unknown as SyllableCountingItem, microSkillId);
    // ── S5.3 (T2-H.1.X): mapped, but shelf stock — no skill routes here
    //    yet, so the new visualScaffold.kind values aren't consumed until a
    //    Stage-6 routing dispatch wires a strand. ──
    case "comprehensionQuestions":
      return comprehensionQuestionToEngineItem(item as unknown as ComprehensionQuestion, microSkillId);
    case "semanticRelations":
      return semanticRelationToEngineItem(item as unknown as SemanticRelation, microSkillId);
    case "grammarItems":
      return grammarItemToEngineItem(item as unknown as GrammarItem, microSkillId);

    // ── Not kid-facing — consumed via other paths, never as items ────
    case "feedbackStrings":
    case "scaffoldPrompts":
    case "parentSummaryTemplates":
      throw new NotKidFacingPackError(pack, ctx);

    // ── Placeholder packs — zero authored items (verified empty) ─────
    case "categories":
    case "attributes":
    case "multipleMeaningWords":
    case "tier2Vocabulary":
      throw new NoAuthoredContentError(pack, ctx);

    default: {
      // Exhaustiveness: a new ContentPackName that reaches here without a
      // case above is a compile error. At runtime (untyped .mjs callers /
      // raw strings) this is the unknown-pack guard.
      const _exhaustive: never = pack;
      throw new UnknownPackError(String(_exhaustive), ctx);
    }
  }
}
